// Code generated by 'guts'. DO NOT EDIT.

// From codersdk/templates.go
/**
 * ACLAvailable is a list of users and groups that can be added to a resource
 * ACL.
 */
export interface ACLAvailable {
	readonly users: readonly ReducedUser[];
	readonly groups: readonly Group[];
}

// From codersdk/aibridge.go
/**
 * AIBridgeAgenticAction represents data from one interception, including
 * tool calls, thinking blocks, and token usage. Tool-less child interceptions
 * are represented as actions with an empty ToolCalls slice.
 */
export interface AIBridgeAgenticAction {
	readonly interception_id: string;
	readonly model: string;
	/**
	 * Attribution contains attribution data from this interception.
	 * Unknown attribution is serialized as an empty object.
	 */
	readonly attribution: AIBridgeAttribution;
	readonly token_usage: AIBridgeSessionThreadsTokenUsage;
	/**
	 * PricedModel is the model and prices used for this interception. Nil
	 * when it was not priced.
	 */
	readonly priced_model?: AIBridgePricedModel;
	readonly thinking: readonly AIBridgeModelThought[];
	readonly tool_calls: readonly AIBridgeToolCall[];
}

// From codersdk/aibridge.go
/**
 * AIBridgeAttribution contains the attribution fields recorded for one
 * interception.
 */
export type AIBridgeAttribution = Record<string, string>;

// From codersdk/deployment.go
export interface AIBridgeConfig {
	readonly enabled: boolean;
	/**
	 * @deprecated Injected MCP in AI Bridge is deprecated and will be removed in a future release.
	 */
	readonly inject_coder_mcp_tools: boolean;
	readonly retention: number;
	readonly max_concurrency: number;
	readonly rate_limit: number;
	readonly structured_logging: boolean;
	/**
	 * StructuredLoggingSource selects which process emits the records that
	 * StructuredLogging enables. See AIStructuredLoggingSource.
	 */
	readonly structured_logging_source?: string;
	readonly send_actor_headers: boolean;
	readonly actor_header_id: string;
	readonly actor_header_username: string;
	readonly actor_header_email: string;
	readonly allow_byok: boolean;
	/**
	 * Budget settings for AI Governance cost controls.
	 */
	readonly budget_policy?: string;
	readonly budget_period?: string;
	/**
	 * Circuit breaker protects against cascading failures from upstream AI
	 * provider overload (503, 529).
	 */
	readonly circuit_breaker_enabled: boolean;
	readonly circuit_breaker_failure_threshold: number;
	readonly circuit_breaker_interval: number;
	readonly circuit_breaker_timeout: number;
	readonly circuit_breaker_max_requests: number;
	/**
	 * APIDumpDir is the base directory under which each provider's
	 * request/response dumps are written, in a subdirectory named after
	 * the provider. Empty disables dumping.
	 */
	readonly api_dump_dir: string;
	/**
	 * DisableContentRecording stops user prompts, tool calls and model
	 * reasoning from being recorded, including tool names and their arguments.
	 * Interceptions and token usage are still recorded, so cost controls,
	 * budget enforcement and spend reporting are unaffected.
	 */
	readonly disable_content_recording: boolean;
}

// From codersdk/aibridge.go
export interface AIBridgeListSessionsResponse {
	readonly count: number;
	readonly sessions: readonly AIBridgeSession[];
}

// From codersdk/aibridge.go
/**
 * AIBridgeModelThought represents a single thinking block from
 * the model.
 */
export interface AIBridgeModelThought {
	readonly text: string;
}

// From codersdk/aibridge.go
/**
 * AIBridgePricedModel is the model and per-token prices used to compute the
 * cost of an interception. Prices are integer micro-units per million
 * tokens, so 1000000 is $1.00 per million tokens. A nil price means the
 * price is not known and was treated as zero.
 */
export interface AIBridgePricedModel {
	/**
	 * Model is the priced model, either the requested model or the model
	 * reported by the provider.
	 */
	readonly model: string;
	readonly input_price: number | null;
	readonly output_price: number | null;
	readonly cache_read_price: number | null;
	readonly cache_write_price: number | null;
}

// From codersdk/aibridge.go
/**
 * AIBridgeProvider is the display metadata for a configured AI provider,
 * used to filter AI Gateway sessions by provider_name. It carries no
 * configuration so it can be served to anyone who can read sessions.
 */
export interface AIBridgeProvider {
	readonly name: string;
	readonly type: AIProviderType;
	readonly display_name: string;
	readonly icon: string;
}

// From codersdk/deployment.go
export interface AIBridgeProxyConfig {
	readonly enabled: boolean;
	readonly listen_addr: string;
	readonly target: string;
	readonly tls_cert_file: string;
	readonly tls_key_file: string;
	readonly cert_file: string;
	readonly key_file: string;
	readonly domain_allowlist: string;
	readonly upstream_proxy: string;
	readonly upstream_proxy_ca: string;
	readonly allowed_private_cidrs: string;
	readonly api_dump_dir: string;
}

// From codersdk/aibridge.go
export interface AIBridgeSession {
	readonly id: string;
	readonly initiator: MinimalUser;
	readonly providers: readonly string[];
	readonly models: readonly string[];
	readonly client: string | null;
	// empty interface{} type, falling back to unknown
	readonly metadata: Record<string, unknown>;
	readonly started_at: string;
	readonly ended_at?: string;
	readonly threads: number;
	readonly token_usage_summary: AIBridgeSessionTokenUsageSummary;
	/**
	 * NetworkCalls summarizes the Agent Firewall network requests made during the
	 * session. A nil value means the session did not pass through Agent
	 * Firewall, so network call monitoring was not active, which the UI
	 * surfaces as "Disabled".
	 */
	readonly network_calls?: AIBridgeSessionNetworkCallSummary;
	readonly last_prompt?: string;
	readonly last_active_at: string;
}

// From codersdk/aibridge.go
/**
 * AIBridgeSessionNetworkCallSummary aggregates the Agent Firewall network
 * calls made during a session. Blocked counts calls denied by the firewall
 * allow-list.
 */
export interface AIBridgeSessionNetworkCallSummary {
	readonly total: number;
	readonly blocked: number;
}

// From codersdk/aibridge.go
/**
 * AIBridgeSessionNetworkDomain is one destination host contacted during a
 * session, with the number of network calls made to it.
 */
export interface AIBridgeSessionNetworkDomain {
	readonly domain: string;
	readonly count: number;
}

// From codersdk/aibridge.go
/**
 * AIBridgeSessionThreadsResponse is the response for GET
 * /api/v2/ai-gateway/sessions/{session_id} which returns a single
 * session with fully expanded threads.
 */
export interface AIBridgeSessionThreadsResponse {
	readonly id: string;
	readonly initiator: MinimalUser;
	readonly providers: readonly string[];
	readonly models: readonly string[];
	readonly client?: string;
	// empty interface{} type, falling back to unknown
	readonly metadata: Record<string, unknown>;
	readonly page_started_at?: string;
	readonly page_ended_at?: string;
	readonly started_at: string;
	readonly ended_at?: string;
	readonly token_usage_summary: AIBridgeSessionThreadsTokenUsage;
	/**
	 * NetworkCalls summarizes the Agent Firewall network calls made during the
	 * session. A nil value means the session did not pass through Agent
	 * Firewall, so network call monitoring was not active, which the UI
	 * surfaces as "Disabled".
	 */
	readonly network_calls?: AIBridgeSessionNetworkCallSummary;
	/**
	 * NetworkTopDomains lists the most contacted destination hosts, ordered by
	 * call count descending. NetworkDomainCount is the total number of distinct
	 * domains, used to render a "+N more" overflow beyond the listed domains.
	 */
	readonly network_top_domains?: readonly AIBridgeSessionNetworkDomain[];
	readonly network_domain_count?: number;
	/**
	 * NetworkCallLogs is the chronological list of individual network calls made
	 * during the session, holding the earliest calls up to a server-side cap.
	 * NetworkCalls remains authoritative for whole-session totals, so a shorter
	 * list than NetworkCalls.Total means the list was truncated. Empty when the
	 * session did not pass through Agent Firewall.
	 */
	readonly network_call_logs?: readonly AgentFirewallLog[];
	readonly threads: readonly AIBridgeThread[];
}

// From codersdk/aibridge.go
/**
 * AIBridgeSessionThreadsTokenUsage represents aggregated token usage
 * with metadata containing provider-specific fields.
 */
export interface AIBridgeSessionThreadsTokenUsage {
	readonly input_tokens: number;
	readonly output_tokens: number;
	readonly cache_read_input_tokens: number;
	readonly cache_write_input_tokens: number;
	// empty interface{} type, falling back to unknown
	readonly metadata: Record<string, unknown>;
	/**
	 * CostMicros is the summed cost of the priced usage, in micro-units
	 * (1000000 is $1.00).
	 */
	readonly cost_micros: number;
	/**
	 * HasUnpricedUsage reports that some usage has no cost, so CostMicros
	 * is a lower bound.
	 */
	readonly has_unpriced_usage: boolean;
}

// From codersdk/aibridge.go
export interface AIBridgeSessionTokenUsageSummary {
	readonly input_tokens: number;
	readonly output_tokens: number;
	readonly cache_read_input_tokens: number;
	readonly cache_write_input_tokens: number;
}

// From codersdk/aibridge.go
/**
 * AIBridgeThread represents a single thread within a session.
 * A thread groups interceptions by their thread_root_id.
 */
export interface AIBridgeThread {
	readonly id: string;
	readonly prompt?: string;
	readonly model: string;
	readonly provider: string;
	readonly credential_kind: string;
	readonly credential_hint: string;
	readonly started_at: string;
	readonly ended_at?: string;
	readonly token_usage: AIBridgeSessionThreadsTokenUsage;
	/**
	 * PricedModel is the model and prices used for the root interception
	 * (the prompt). Other requests in the thread may use different models
	 * and prices, see agentic_actions[].priced_model. Nil when the root
	 * interception was not priced.
	 */
	readonly priced_model?: AIBridgePricedModel;
	/**
	 * Attribution contains attribution data from the root interception.
	 * Unknown attribution is serialized as an empty object.
	 */
	readonly attribution: AIBridgeAttribution;
	readonly agentic_actions: readonly AIBridgeAgenticAction[];
	/**
	 * ErrorType is the categorized terminal upstream error from the root
	 * interception, or nil when the interception succeeded. See the
	 * aibridge_interception_error_type enum for possible values.
	 */
	readonly error_type?: string;
	/**
	 * ErrorMessage is the raw terminal upstream error message from the root
	 * interception. Nil when the interception succeeded.
	 */
	readonly error_message?: string;
	/**
	 * AgentFirewallSessionID links this thread to an agent firewall
	 * confinement session. Nil when the request did not pass through
	 * the agent firewall.
	 */
	readonly agent_firewall_session_id?: string;
	/**
	 * AgentFirewallSequenceNumber is the firewall sequence number from
	 * the root interception. Used to determine the position of this
	 * LLM request in the firewall event stream. Nil when the request
	 * did not pass through the agent firewall.
	 */
	readonly agent_firewall_sequence_number?: number;
}

// From codersdk/aibridge.go
/**
 * AIBridgeToolCall represents a tool call recorded during an
 * interception.
 */
export interface AIBridgeToolCall {
	readonly id: string;
	readonly interception_id: string;
	readonly provider_response_id: string;
	readonly server_url: string;
	readonly tool: string;
	readonly injected: boolean;
	readonly input: string;
	// empty interface{} type, falling back to unknown
	readonly metadata: Record<string, unknown>;
	readonly created_at: string;
}

// From codersdk/aibridge.go
/**
 * AIBudgetLimit is an AI spend limit and the tier that produced it. Both
 * fields are always populated together.
 */
export interface AIBudgetLimit {
	readonly spend_limit_micros: number;
	readonly limit_source: AIBudgetLimitSource;
}

// From codersdk/aibridge.go
export type AIBudgetLimitSource = "group" | "user_override";

export const AIBudgetLimitSources: AIBudgetLimitSource[] = [
	"group",
	"user_override",
];

// From codersdk/deployment.go
export type AIBudgetPeriod = "month";

export const AIBudgetPeriods: AIBudgetPeriod[] = ["month"];

export const AIBudgetPolicies: AIBudgetPolicy[] = ["highest"];

// From codersdk/deployment.go
export type AIBudgetPolicy = "highest";

// From codersdk/deployment.go
export interface AIConfig {
	readonly bridge?: AIBridgeConfig;
	readonly aibridge_proxy?: AIBridgeProxyConfig;
	readonly chat?: ChatConfig;
}

// From codersdk/aigatewaykeys.go
/**
 * AIGatewayKey is a shared secret used by a standalone AI Gateway
 * to authenticate into coderd.
 */
export interface AIGatewayKey {
	readonly id: string;
	readonly name: string;
	readonly key_prefix: string;
	readonly created_at: string;
	readonly last_heartbeat_at?: string;
}

// From codersdk/client.go
/**
 * AIGatewayKeyHeader contains the authentication key for a standalone AI Gateway replica.
 */
export const AIGatewayKeyHeader = "X-Coder-AI-Governance-Gateway-Key";

// From codersdk/aimodelprices.go
/**
 * AIModelPrice is a per-model token price used by AI Gateway to compute the
 * cost of an interception.
 *
 * Prices are integer micro-units per million tokens, so 1000000 is $1.00 per
 * million tokens. A nil price means the price is not known, which the cost
 * calculation treats the same as zero. Distinguish that from an explicit 0,
 * which declares the model free of charge.
 */
export interface AIModelPrice {
	readonly provider: string;
	readonly model: string;
	readonly input_price: number | null;
	readonly output_price: number | null;
	readonly cache_read_price: number | null;
	readonly cache_write_price: number | null;
	readonly source: AIModelPriceSource;
	readonly created_at: string;
	readonly updated_at: string;
}

// From codersdk/aimodelprices.go
export type AIModelPriceSource = "custom" | "default";

export const AIModelPriceSources: AIModelPriceSource[] = ["custom", "default"];

// From codersdk/aimodelprices.go
/**
 * AIModelPriceUpsert is one model's prices in an upsert request. It carries
 * only the writable fields of AIModelPrice.
 */
export interface AIModelPriceUpsert {
	readonly provider: string;
	readonly model: string;
	readonly input_price: number | null;
	readonly output_price: number | null;
	readonly cache_read_price: number | null;
	readonly cache_write_price: number | null;
}

// From codersdk/aiproviders.go
/**
 * AIProvider represents an AI provider configuration row as returned
 * by the API. Each APIKey entry carries the row's ID so callers can
 * reference it in an UpdateAIProviderRequest; the plaintext value is
 * never echoed back (see AIProviderKey.Masked). Secret fields on
 * Settings are never included in responses.
 */
export interface AIProvider {
	readonly id: string;
	readonly type: AIProviderType;
	readonly name: string;
	readonly display_name: string;
	readonly icon: string;
	readonly enabled: boolean;
	readonly base_url: string;
	readonly api_keys: readonly AIProviderKey[];
	readonly settings: AIProviderSettings;
	readonly created_at: string;
	readonly updated_at: string;
	/**
	 * Status carries runtime routing status; nil when empty.
	 */
	readonly status?: AIProviderStatus;
}

// From codersdk/aiproviders_bedrock.go
export type AIProviderBedrockProtocol = "invoke-model" | "mantle";

export const AIProviderBedrockProtocols: AIProviderBedrockProtocol[] = [
	"invoke-model",
	"mantle",
];

// From codersdk/aiproviders_bedrock.go
/**
 * AIProviderBedrockSettings configures providers that authenticate
 * against AWS Bedrock. AccessKey and AccessKeySecret are write-only:
 * servers strip them from GET and list responses. Both secret fields
 * use a pointer so a PATCH can distinguish "leave untouched" (omitted)
 * from "explicitly clear" (empty string), e.g. when migrating to
 * IAM role-based authentication.
 */
export interface AIProviderBedrockSettings {
	/**
	 * Region is the AWS region used to construct the Bedrock endpoint
	 * URL when BaseURL is not set on the parent provider.
	 */
	readonly region?: string;
	/**
	 * Model is the AWS Bedrock model identifier used for primary
	 * requests.
	 */
	readonly model?: string;
	/**
	 * SmallFastModel is the AWS Bedrock model identifier used for
	 * background tasks (e.g. Claude Code's haiku-class model).
	 */
	readonly small_fast_model?: string;
	/**
	 * AccessKey is the AWS access key ID used to authenticate against
	 * Bedrock. Write-only.
	 */
	readonly access_key?: string;
	/**
	 * AccessKeySecret is the AWS secret access key paired with
	 * AccessKey. Write-only.
	 */
	readonly access_key_secret?: string;
	/**
	 * RoleARN, when set, is the IAM role assumed via STS before calling
	 * Bedrock. The base identity (static keys or the AWS environment, e.g.
	 * IRSA / EKS Pod Identity / EC2 Instance Profile) signs the AssumeRole
	 * call, and the resulting temporary credentials sign Bedrock requests.
	 */
	readonly role_arn?: string;
	/**
	 * ExternalID is the STS external ID sent on the AssumeRole call when
	 * RoleARN is set. The server generates and owns it: create and update
	 * reject any client-supplied value that differs from the stored one (an
	 * update may echo the stored value back).
	 */
	readonly external_id?: string;
	/**
	 * Protocol selects the Bedrock wire protocol. An empty value resolves to
	 * AIProviderBedrockProtocolInvokeModel, so existing rows keep the legacy
	 * behavior.
	 */
	readonly protocol?: AIProviderBedrockProtocol;
	/**
	 * ResolvedModel and ResolvedSmallFastModel are the model IDs behind the
	 * configured identifiers, which differ from them only for application
	 * inference profile ARNs. The server resolves those through AWS when the
	 * provider is written and owns the values; a client cannot set them.
	 */
	readonly resolved_model?: string;
	readonly resolved_small_fast_model?: string;
}

// From codersdk/aiproviders_bedrock.go
/**
 * AIProviderBedrockSettingsVersion is the current schema version of
 * AIProviderBedrockSettings.
 */
export const AIProviderBedrockSettingsVersion = 1;

// From codersdk/aiproviders_claude_platform_aws.go
/**
 * AIProviderClaudePlatformAWSSettings configures providers that authenticate
 * against Claude Platform for AWS: Anthropic's native Messages API hosted on
 * AWS. It speaks the standard Messages wire format with standard Anthropic
 * model IDs, so it is an authentication and routing variant of
 * AIProviderTypeAnthropic rather than a provider type of its own.
 * Requests use a client or provider API key when available, otherwise the
 * gateway signs with its ambient AWS credentials.
 */
export interface AIProviderClaudePlatformAWSSettings {
	/**
	 * Region selects the default regional endpoint and SigV4 signing scope.
	 * Required even when BaseURL points at a proxy.
	 */
	readonly region: string;
	/**
	 * WorkspaceID is sent as the anthropic-workspace-id header on every
	 * request. Required regardless of the credential used.
	 */
	readonly workspace_id: string;
}

// From codersdk/aiproviders_claude_platform_aws.go
/**
 * AIProviderClaudePlatformAWSSettingsVersion is the current schema version of
 * AIProviderClaudePlatformAWSSettings.
 */
export const AIProviderClaudePlatformAWSSettingsVersion = 1;

// From codersdk/aiproviders.go
/**
 * AIProviderKey is a single API key registered on a provider. The
 * plaintext is never returned; Masked is a one-way rendering safe for
 * display (see aibridge utils MaskSecret). ID lets clients reference
 * the row in an UpdateAIProviderRequest without re-sending plaintext.
 */
export interface AIProviderKey {
	readonly id: string;
	readonly masked: string;
	readonly created_at: string;
}

// From codersdk/aiproviders.go
/**
 * AIProviderKeyMutation describes the intended state of a single key
 * in an UpdateAIProviderRequest. Exactly one of ID or APIKey must be
 * set:
 *
 *   - ID set, APIKey nil: keep this existing key (matched by ID).
 *   - ID nil, APIKey set: insert this new plaintext as a new key.
 *
 * Any existing key whose ID is absent from the request is deleted.
 */
export interface AIProviderKeyMutation {
	readonly id?: string;
	readonly api_key?: string;
}

// From codersdk/aiproviders.go
/**
 * AIProviderSettings is the discriminated container for type-specific
 * provider settings stored in ai_providers.settings. Providers that
 * need no type-specific configuration (current OpenAI and standard
 * Anthropic flows) leave every field nil; the wire form for those
 * providers is JSON null.
 *
 * On the wire, settings serialize as a JSON object that always carries
 * _type and _version discriminator keys alongside the type-specific
 * fields. The custom (Un)MarshalJSON implementations on this type
 * handle the routing automatically; callers should never marshal the
 * concrete settings struct directly.
 *
 * AIProviderTypeBedrock is a distinct provider type for historical reasons.
 * New Anthropic *authentication methods* do not get provider types: they are
 * settings variants on AIProviderTypeAnthropic, which is why
 * ClaudePlatformAWS has no matching AIProviderType.
 */
export interface AIProviderSettings {}

// From codersdk/aiproviders_bedrock.go
/**
 * AIProviderSettingsTypeBedrock is the _type discriminator value for
 * AIProviderBedrockSettings.
 */
export const AIProviderSettingsTypeBedrock = "bedrock";

// From codersdk/aiproviders_claude_platform_aws.go
/**
 * AIProviderSettingsTypeClaudePlatformAWS is the _type discriminator value for
 * AIProviderClaudePlatformAWSSettings.
 */
export const AIProviderSettingsTypeClaudePlatformAWS = "claude_platform_aws";

// From codersdk/aiproviders.go
/**
 * AIProviderStatus carries non-fatal routing warnings. Direct
 * routing remains available for the provider.
 */
export interface AIProviderStatus {
	readonly warnings?: readonly string[];
}

// From codersdk/chats.go
/**
 * AIProviderSummary is provider metadata embedded in other API responses.
 */
export interface AIProviderSummary {
	readonly id: string;
	readonly type: AIProviderType;
	readonly name: string;
	readonly display_name: string;
	readonly icon: string;
	readonly enabled: boolean;
	readonly deleted: boolean;
}

// From codersdk/aiproviders.go
export type AIProviderType =
	| "anthropic"
	| "azure"
	| "bedrock"
	| "copilot"
	| "google"
	| "openai"
	| "openai-compat"
	| "openrouter"
	| "vercel";

export const AIProviderTypes: AIProviderType[] = [
	"anthropic",
	"azure",
	"bedrock",
	"copilot",
	"google",
	"openai",
	"openai-compat",
	"openrouter",
	"vercel",
];

// From codersdk/aibridge.go
/**
 * AISpendPeriodWindow is the [Start, End) window over which AI spend is
 * aggregated.
 */
export interface AISpendPeriodWindow {
	/**
	 * PeriodStart is the inclusive lower bound of the current budget
	 * period.
	 */
	readonly period_start: string;
	/**
	 * PeriodEnd is the exclusive upper bound of the current budget
	 * period.
	 */
	readonly period_end: string;
}

// From codersdk/deployment.go
export type AIStructuredLoggingSource = "both" | "coderd" | "gateway";

export const AIStructuredLoggingSources: AIStructuredLoggingSource[] = [
	"both",
	"coderd",
	"gateway",
];

// From codersdk/allowlist.go
/**
 * APIAllowListTarget represents a single allow-list entry using the canonical
 * string form "<resource_type>:<id>". The wildcard symbol "*" is treated as a
 * permissive match for either side.
 */
export interface APIAllowListTarget {
	readonly type: RBACResource;
	readonly id: string;
}

// From codersdk/apikey.go
/**
 * APIKey: do not ever return the HashedSecret
 */
export interface APIKey {
	readonly id: string;
	readonly user_id: string;
	readonly last_used: string;
	readonly expires_at: string;
	readonly created_at: string;
	readonly updated_at: string;
	readonly login_type: LoginType;
	readonly scope: APIKeyScope; // Deprecated: use Scopes instead.
	readonly scopes: readonly APIKeyScope[];
	readonly token_name: string;
	readonly lifetime_seconds: number;
	readonly allow_list: readonly APIAllowListTarget[];
}

// From codersdk/apikey.go
export type APIKeyScope =
	| "ai_gateway_key:*"
	| "ai_gateway_key:create"
	| "ai_gateway_key:delete"
	| "ai_gateway_key:read"
	| "ai_gateway_key:update"
	| "ai_model_price:*"
	| "ai_model_price:read"
	| "ai_model_price:update"
	| "ai_provider:*"
	| "ai_provider:create"
	| "ai_provider:delete"
	| "ai_provider:read"
	| "ai_provider:update"
	| "ai_seat:*"
	| "ai_seat:create"
	| "ai_seat:read"
	| "aibridge_interception:*"
	| "aibridge_interception:create"
	| "aibridge_interception:read"
	| "aibridge_interception:update"
	| "all"
	| "api_key:*"
	| "api_key:create"
	| "api_key:delete"
	| "api_key:read"
	| "api_key:update"
	| "application_connect"
	| "assign_org_role:*"
	| "assign_org_role:assign"
	| "assign_org_role:create"
	| "assign_org_role:delete"
	| "assign_org_role:read"
	| "assign_org_role:unassign"
	| "assign_org_role:update"
	| "assign_role:*"
	| "assign_role:assign"
	| "assign_role:read"
	| "assign_role:unassign"
	| "audit_log:*"
	| "audit_log:create"
	| "audit_log:read"
	| "boundary_log:*"
	| "boundary_log:create"
	| "boundary_log:delete"
	| "boundary_log:read"
	| "boundary_usage:*"
	| "boundary_usage:delete"
	| "boundary_usage:read"
	| "boundary_usage:update"
	| "chat:*"
	| "chat:create"
	| "chat:delete"
	| "chat_model_config:*"
	| "chat_model_config:create"
	| "chat_model_config:delete"
	| "chat_model_config:read"
	| "chat_model_config:share"
	| "chat_model_config:update"
	| "chat_project:*"
	| "chat_project:create"
	| "chat_project:delete"
	| "chat_project_memory:*"
	| "chat_project_memory:create"
	| "chat_project_memory:delete"
	| "chat_project_memory:read"
	| "chat_project:read"
	| "chat_project:share"
	| "chat_project:update"
	| "chat:read"
	| "chat:share"
	| "chat:update"
	| "coder:all"
	| "coder:apikeys.manage_self"
	| "coder:application_connect"
	| "coder:templates.author"
	| "coder:templates.build"
	| "coder:workspaces.access"
	| "coder:workspaces.create"
	| "coder:workspaces.delete"
	| "coder:workspaces.operate"
	| "connection_log:*"
	| "connection_log:read"
	| "connection_log:update"
	| "crypto_key:*"
	| "crypto_key:create"
	| "crypto_key:delete"
	| "crypto_key:read"
	| "crypto_key:update"
	| "debug_info:*"
	| "debug_info:read"
	| "deployment_config:*"
	| "deployment_config:read"
	| "deployment_config:update"
	| "deployment_stats:*"
	| "deployment_stats:read"
	| "file:*"
	| "file:create"
	| "file:read"
	| "group:*"
	| "group:create"
	| "group:delete"
	| "group_member:*"
	| "group_member:read"
	| "group:read"
	| "group:update"
	| "idpsync_settings:*"
	| "idpsync_settings:read"
	| "idpsync_settings:update"
	| "inbox_notification:*"
	| "inbox_notification:create"
	| "inbox_notification:read"
	| "inbox_notification:update"
	| "license:*"
	| "license:create"
	| "license:delete"
	| "license:read"
	| "mcp_server_config:*"
	| "mcp_server_config:create"
	| "mcp_server_config:delete"
	| "mcp_server_config:read"
	| "mcp_server_config:share"
	| "mcp_server_config:update"
	| "notification_message:*"
	| "notification_message:create"
	| "notification_message:delete"
	| "notification_message:read"
	| "notification_message:update"
	| "notification_preference:*"
	| "notification_preference:read"
	| "notification_preference:update"
	| "notification_template:*"
	| "notification_template:read"
	| "notification_template:update"
	| "oauth2_app:*"
	| "oauth2_app_code_token:*"
	| "oauth2_app_code_token:create"
	| "oauth2_app_code_token:delete"
	| "oauth2_app_code_token:read"
	| "oauth2_app:create"
	| "oauth2_app:delete"
	| "oauth2_app:read"
	| "oauth2_app_secret:*"
	| "oauth2_app_secret:create"
	| "oauth2_app_secret:delete"
	| "oauth2_app_secret:read"
	| "oauth2_app_secret:update"
	| "oauth2_app:update"
	| "organization:*"
	| "organization:create"
	| "organization:delete"
	| "organization_member:*"
	| "organization_member:create"
	| "organization_member:delete"
	| "organization_member:read"
	| "organization_member:update"
	| "organization:read"
	| "organization_skill:*"
	| "organization_skill:create"
	| "organization_skill:delete"
	| "organization_skill:read"
	| "organization_skill:share"
	| "organization_skill:update"
	| "organization:update"
	| "prebuilt_workspace:*"
	| "prebuilt_workspace:delete"
	| "prebuilt_workspace:update"
	| "provisioner_daemon:*"
	| "provisioner_daemon:create"
	| "provisioner_daemon:delete"
	| "provisioner_daemon:read"
	| "provisioner_daemon:update"
	| "provisioner_jobs:*"
	| "provisioner_jobs:create"
	| "provisioner_jobs:read"
	| "provisioner_jobs:update"
	| "replicas:*"
	| "replicas:read"
	| "system:*"
	| "system:create"
	| "system:delete"
	| "system:read"
	| "system:update"
	| "tailnet_coordinator:*"
	| "tailnet_coordinator:create"
	| "tailnet_coordinator:delete"
	| "tailnet_coordinator:read"
	| "tailnet_coordinator:update"
	| "template:*"
	| "template:create"
	| "template:delete"
	| "template:read"
	| "template:update"
	| "template:use"
	| "template:view_insights"
	| "usage_event:*"
	| "usage_event:create"
	| "usage_event:read"
	| "usage_event:update"
	| "user:*"
	| "user:create"
	| "user:delete"
	| "user:read"
	| "user:read_personal"
	| "user_secret:*"
	| "user_secret:create"
	| "user_secret:delete"
	| "user_secret:read"
	| "user_secret:update"
	| "user_skill:*"
	| "user_skill:create"
	| "user_skill:delete"
	| "user_skill:read"
	| "user_skill:update"
	| "user:update"
	| "user:update_personal"
	| "webpush_subscription:*"
	| "webpush_subscription:create"
	| "webpush_subscription:delete"
	| "webpush_subscription:read"
	| "workspace_agent_devcontainers:*"
	| "workspace_agent_devcontainers:create"
	| "workspace_agent_resource_monitor:*"
	| "workspace_agent_resource_monitor:create"
	| "workspace_agent_resource_monitor:read"
	| "workspace_agent_resource_monitor:update"
	| "workspace:*"
	| "workspace:application_connect"
	| "workspace_build_orchestration:*"
	| "workspace_build_orchestration:create"
	| "workspace_build_orchestration:delete"
	| "workspace_build_orchestration:read"
	| "workspace_build_orchestration:update"
	| "workspace:create"
	| "workspace:create_agent"
	| "workspace:delete"
	| "workspace:delete_agent"
	| "workspace_dormant:*"
	| "workspace_dormant:application_connect"
	| "workspace_dormant:create"
	| "workspace_dormant:create_agent"
	| "workspace_dormant:delete"
	| "workspace_dormant:delete_agent"
	| "workspace_dormant:read"
	| "workspace_dormant:share"
	| "workspace_dormant:ssh"
	| "workspace_dormant:start"
	| "workspace_dormant:stop"
	| "workspace_dormant:update"
	| "workspace_dormant:update_agent"
	| "workspace_proxy:*"
	| "workspace_proxy:create"
	| "workspace_proxy:delete"
	| "workspace_proxy:read"
	| "workspace_proxy:update"
	| "workspace:read"
	| "workspace_secret:*"
	| "workspace_secret:create"
	| "workspace_secret:read"
	| "workspace_secret:read_secret"
	| "workspace_secret:update"
	| "workspace:share"
	| "workspace:ssh"
	| "workspace:start"
	| "workspace:stop"
	| "workspace:update"
	| "workspace:update_agent";

export const APIKeyScopes: APIKeyScope[] = [
	"ai_gateway_key:*",
	"ai_gateway_key:create",
	"ai_gateway_key:delete",
	"ai_gateway_key:read",
	"ai_gateway_key:update",
	"ai_model_price:*",
	"ai_model_price:read",
	"ai_model_price:update",
	"ai_provider:*",
	"ai_provider:create",
	"ai_provider:delete",
	"ai_provider:read",
	"ai_provider:update",
	"ai_seat:*",
	"ai_seat:create",
	"ai_seat:read",
	"aibridge_interception:*",
	"aibridge_interception:create",
	"aibridge_interception:read",
	"aibridge_interception:update",
	"all",
	"api_key:*",
	"api_key:create",
	"api_key:delete",
	"api_key:read",
	"api_key:update",
	"application_connect",
	"assign_org_role:*",
	"assign_org_role:assign",
	"assign_org_role:create",
	"assign_org_role:delete",
	"assign_org_role:read",
	"assign_org_role:unassign",
	"assign_org_role:update",
	"assign_role:*",
	"assign_role:assign",
	"assign_role:read",
	"assign_role:unassign",
	"audit_log:*",
	"audit_log:create",
	"audit_log:read",
	"boundary_log:*",
	"boundary_log:create",
	"boundary_log:delete",
	"boundary_log:read",
	"boundary_usage:*",
	"boundary_usage:delete",
	"boundary_usage:read",
	"boundary_usage:update",
	"chat:*",
	"chat:create",
	"chat:delete",
	"chat_model_config:*",
	"chat_model_config:create",
	"chat_model_config:delete",
	"chat_model_config:read",
	"chat_model_config:share",
	"chat_model_config:update",
	"chat_project:*",
	"chat_project:create",
	"chat_project:delete",
	"chat_project_memory:*",
	"chat_project_memory:create",
	"chat_project_memory:delete",
	"chat_project_memory:read",
	"chat_project:read",
	"chat_project:share",
	"chat_project:update",
	"chat:read",
	"chat:share",
	"chat:update",
	"coder:all",
	"coder:apikeys.manage_self",
	"coder:application_connect",
	"coder:templates.author",
	"coder:templates.build",
	"coder:workspaces.access",
	"coder:workspaces.create",
	"coder:workspaces.delete",
	"coder:workspaces.operate",
	"connection_log:*",
	"connection_log:read",
	"connection_log:update",
	"crypto_key:*",
	"crypto_key:create",
	"crypto_key:delete",
	"crypto_key:read",
	"crypto_key:update",
	"debug_info:*",
	"debug_info:read",
	"deployment_config:*",
	"deployment_config:read",
	"deployment_config:update",
	"deployment_stats:*",
	"deployment_stats:read",
	"file:*",
	"file:create",
	"file:read",
	"group:*",
	"group:create",
	"group:delete",
	"group_member:*",
	"group_member:read",
	"group:read",
	"group:update",
	"idpsync_settings:*",
	"idpsync_settings:read",
	"idpsync_settings:update",
	"inbox_notification:*",
	"inbox_notification:create",
	"inbox_notification:read",
	"inbox_notification:update",
	"license:*",
	"license:create",
	"license:delete",
	"license:read",
	"mcp_server_config:*",
	"mcp_server_config:create",
	"mcp_server_config:delete",
	"mcp_server_config:read",
	"mcp_server_config:share",
	"mcp_server_config:update",
	"notification_message:*",
	"notification_message:create",
	"notification_message:delete",
	"notification_message:read",
	"notification_message:update",
	"notification_preference:*",
	"notification_preference:read",
	"notification_preference:update",
	"notification_template:*",
	"notification_template:read",
	"notification_template:update",
	"oauth2_app:*",
	"oauth2_app_code_token:*",
	"oauth2_app_code_token:create",
	"oauth2_app_code_token:delete",
	"oauth2_app_code_token:read",
	"oauth2_app:create",
	"oauth2_app:delete",
	"oauth2_app:read",
	"oauth2_app_secret:*",
	"oauth2_app_secret:create",
	"oauth2_app_secret:delete",
	"oauth2_app_secret:read",
	"oauth2_app_secret:update",
	"oauth2_app:update",
	"organization:*",
	"organization:create",
	"organization:delete",
	"organization_member:*",
	"organization_member:create",
	"organization_member:delete",
	"organization_member:read",
	"organization_member:update",
	"organization:read",
	"organization_skill:*",
	"organization_skill:create",
	"organization_skill:delete",
	"organization_skill:read",
	"organization_skill:share",
	"organization_skill:update",
	"organization:update",
	"prebuilt_workspace:*",
	"prebuilt_workspace:delete",
	"prebuilt_workspace:update",
	"provisioner_daemon:*",
	"provisioner_daemon:create",
	"provisioner_daemon:delete",
	"provisioner_daemon:read",
	"provisioner_daemon:update",
	"provisioner_jobs:*",
	"provisioner_jobs:create",
	"provisioner_jobs:read",
	"provisioner_jobs:update",
	"replicas:*",
	"replicas:read",
	"system:*",
	"system:create",
	"system:delete",
	"system:read",
	"system:update",
	"tailnet_coordinator:*",
	"tailnet_coordinator:create",
	"tailnet_coordinator:delete",
	"tailnet_coordinator:read",
	"tailnet_coordinator:update",
	"template:*",
	"template:create",
	"template:delete",
	"template:read",
	"template:update",
	"template:use",
	"template:view_insights",
	"usage_event:*",
	"usage_event:create",
	"usage_event:read",
	"usage_event:update",
	"user:*",
	"user:create",
	"user:delete",
	"user:read",
	"user:read_personal",
	"user_secret:*",
	"user_secret:create",
	"user_secret:delete",
	"user_secret:read",
	"user_secret:update",
	"user_skill:*",
	"user_skill:create",
	"user_skill:delete",
	"user_skill:read",
	"user_skill:update",
	"user:update",
	"user:update_personal",
	"webpush_subscription:*",
	"webpush_subscription:create",
	"webpush_subscription:delete",
	"webpush_subscription:read",
	"workspace_agent_devcontainers:*",
	"workspace_agent_devcontainers:create",
	"workspace_agent_resource_monitor:*",
	"workspace_agent_resource_monitor:create",
	"workspace_agent_resource_monitor:read",
	"workspace_agent_resource_monitor:update",
	"workspace:*",
	"workspace:application_connect",
	"workspace_build_orchestration:*",
	"workspace_build_orchestration:create",
	"workspace_build_orchestration:delete",
	"workspace_build_orchestration:read",
	"workspace_build_orchestration:update",
	"workspace:create",
	"workspace:create_agent",
	"workspace:delete",
	"workspace:delete_agent",
	"workspace_dormant:*",
	"workspace_dormant:application_connect",
	"workspace_dormant:create",
	"workspace_dormant:create_agent",
	"workspace_dormant:delete",
	"workspace_dormant:delete_agent",
	"workspace_dormant:read",
	"workspace_dormant:share",
	"workspace_dormant:ssh",
	"workspace_dormant:start",
	"workspace_dormant:stop",
	"workspace_dormant:update",
	"workspace_dormant:update_agent",
	"workspace_proxy:*",
	"workspace_proxy:create",
	"workspace_proxy:delete",
	"workspace_proxy:read",
	"workspace_proxy:update",
	"workspace:read",
	"workspace_secret:*",
	"workspace_secret:create",
	"workspace_secret:read",
	"workspace_secret:read_secret",
	"workspace_secret:update",
	"workspace:share",
	"workspace:ssh",
	"workspace:start",
	"workspace:stop",
	"workspace:update",
	"workspace:update_agent",
];

// From codersdk/apikey.go
export interface APIKeyWithOwner extends APIKey {
	readonly username: string;
}

// From healthsdk/healthsdk.go
/**
 * AccessURLReport shows the results of performing a HTTP_GET to the /healthz endpoint through the configured access URL.
 */
export interface AccessURLReport extends BaseReport {
	/**
	 * Healthy is deprecated and left for backward compatibility purposes, use `Severity` instead.
	 */
	readonly healthy: boolean;
	readonly access_url: string;
	readonly reachable: boolean;
	readonly status_code: number;
	readonly healthz_response: string;
}

// From codersdk/licenses.go
export interface AddLicenseRequest {
	readonly license: string;
}

// From codersdk/deployment.go
export type Addon = "ai_governance";

export const Addons: Addon[] = ["ai_governance"];

// From codersdk/chats.go
/**
 * AdvisorConfig is the deployment-wide runtime configuration for the
 * chat advisor.
 */
export interface AdvisorConfig {
	/**
	 * Enabled is always true and cannot be changed.
	 */
	readonly enabled: boolean;
	/**
	 * MaxUsesPerRun caps how many times the advisor can be invoked per
	 * chat turn. 0 means unlimited.
	 */
	readonly max_uses_per_run: number;
	/**
	 * MaxOutputTokens caps the advisor model response tokens. 0 means
	 * use the runtime default.
	 */
	readonly max_output_tokens: number;
}

// From codersdk/users.go
export type AgentChatSendShortcut = "enter" | "modifier_enter";

export const AgentChatSendShortcuts: AgentChatSendShortcut[] = [
	"enter",
	"modifier_enter",
];

// From codersdk/workspacebuilds.go
export interface AgentConnectionTiming {
	readonly started_at: string;
	readonly ended_at: string;
	readonly stage: TimingStage;
	readonly workspace_agent_id: string;
	readonly workspace_agent_name: string;
}

// From codersdk/users.go
export type AgentDisplayMode = "always_collapsed" | "always_expanded" | "auto";

export const AgentDisplayModes: AgentDisplayMode[] = [
	"always_collapsed",
	"always_expanded",
	"auto",
];

// From codersdk/agentfirewall.go
/**
 * AgentFirewallLog represents a single audit event from an agent firewall proxy.
 */
export interface AgentFirewallLog {
	readonly id: string;
	readonly session_id: string;
	readonly sequence_number: number;
	readonly allowed: boolean;
	readonly created_at: string;
	readonly proto: string;
	readonly method: string;
	readonly detail: string;
	readonly matched_rule: string | null;
	readonly captured_at?: string;
}

// From codersdk/agentfirewall.go
/**
 * AgentFirewallSession represents a firewall session for a workspace agent.
 */
export interface AgentFirewallSession {
	readonly id: string;
	readonly workspace_id: string;
	readonly owner_id: string;
	readonly confined_process: string;
	readonly started_at: string;
}

// From codersdk/agentfirewall.go
/**
 * AgentFirewallSessionLogsParams are query parameters for listing
 * agent firewall session logs.
 */
export interface AgentFirewallSessionLogsParams {
	/**
	 * SeqAfter is an inclusive lower bound on sequence_number.
	 * Only logs with sequence_number >= SeqAfter are returned.
	 */
	readonly seq_after?: number;
	/**
	 * SeqBefore is an exclusive upper bound on sequence_number.
	 * Only logs with sequence_number < SeqBefore are returned.
	 */
	readonly seq_before?: number;
	/**
	 * Limit caps the number of returned rows. Defaults to 100.
	 */
	readonly limit?: number;
}

// From codersdk/agentfirewall.go
/**
 * AgentFirewallSessionLogsResponse is the response for
 * GET /api/v2/agent-firewall/sessions/{id}/logs.
 */
export interface AgentFirewallSessionLogsResponse {
	readonly results: readonly AgentFirewallLog[];
}

// From agenthooks/types.go
/**
 * ChatRef identifies the chat a lifecycle hook event refers to.
 */
export interface AgentHookChatRef {
	readonly chat_id: string;
	readonly owner_id: string;
	readonly workspace_id?: string;
	readonly turn_id?: string;
	readonly parent_chat_id?: string;
	/**
	 * RootChatID identifies the user-facing root of the chat tree.
	 */
	readonly root_chat_id?: string;
}

// From agenthooks/types.go
/**
 * Claims describes the JWT minted by coderd for a lifecycle hook dispatch.
 */
export interface AgentHookClaims {
	readonly iss: string;
	readonly sub: string;
	readonly aud: string;
	readonly iat: number;
	readonly nbf: number;
	readonly exp: number;
	readonly jti: string;
	readonly type: AgentHookEventType;
	readonly body_sha256: string;
}

// From agenthooks/types.go
export type AgentHookEventType =
	| "post_compact"
	| "post_tool_use"
	| "pre_compact"
	| "pre_tool_use"
	| "session_start"
	| "stop"
	| "user_prompt_submit";

export const AgentHookEventTypes: AgentHookEventType[] = [
	"post_compact",
	"post_tool_use",
	"pre_compact",
	"pre_tool_use",
	"session_start",
	"stop",
	"user_prompt_submit",
];

// From agenthooks/http.go
/**
 * Hooks lets a consumer implement only the lifecycle events it uses.
 */
export interface AgentHookHooks {
	// Function type detected, and unsupported. Leaving the type as unknown
	readonly SessionStart: unknown;
	// Function type detected, and unsupported. Leaving the type as unknown
	readonly UserPromptSubmit: unknown;
	// Function type detected, and unsupported. Leaving the type as unknown
	readonly PreToolUse: unknown;
	// Function type detected, and unsupported. Leaving the type as unknown
	readonly PostToolUse: unknown;
	// Function type detected, and unsupported. Leaving the type as unknown
	readonly PreCompact: unknown;
	// Function type detected, and unsupported. Leaving the type as unknown
	readonly PostCompact: unknown;
	// Function type detected, and unsupported. Leaving the type as unknown
	readonly Stop: unknown;
}

// From agenthooks/http.go
/**
 * MaxRequestBodyBytes limits memory used to verify hook requests.
 */
export const AgentHookMaxRequestBodyBytes = 10485760; // 10 MiB

// From agenthooks/types.go
/**
 * Meta identifies a hook dispatch and its chat.
 */
export interface AgentHookMeta extends AgentHookChatRef {
	readonly dispatch_id: string;
	readonly schema_version: number;
}

// From agenthooks/jwt.go
/**
 * MinSecretLen is the minimum HS256 secret length in bytes. go-jose
 * accepts shorter keys, so signing and verification enforce it to fail
 * closed on missing or weak secrets.
 */
export const AgentHookMinSecretLen = 32;

// From agenthooks/types.go
/**
 * Permission controls whether mutable hook input may proceed.
 */
export interface AgentHookPermission {
	readonly decision: AgentHookPermissionDecision;
	readonly reason?: string;
	readonly input_override?: unknown;
}

// From agenthooks/types.go
export type AgentHookPermissionDecision = "allow" | "deny";

export const AgentHookPermissionDecisions: AgentHookPermissionDecision[] = [
	"allow",
	"deny",
];

// From agenthooks/types.go
/**
 * PostCompactData is empty; Meta identifies the compacted chat.
 */
export interface AgentHookPostCompactData {}

// From agenthooks/types.go
/**
 * PostToolUseData describes a completed tool call, carrying either
 * ToolResponse or ToolError.
 */
export interface AgentHookPostToolUseData {
	readonly tool_use_id: string;
	readonly tool_name: string;
	readonly tool_response?: unknown;
	readonly tool_error?: string;
}

// From agenthooks/types.go
/**
 * PreCompactData is empty; Meta identifies the chat being compacted.
 */
export interface AgentHookPreCompactData {}

// From agenthooks/types.go
/**
 * PreToolUseData describes a tool call before execution.
 */
export interface AgentHookPreToolUseData {
	readonly tool_use_id: string;
	readonly tool_name: string;
	readonly tool_input: unknown;
}

// From agenthooks/types.go
/**
 * Request is the body coderd posts to the configured lifecycle hook URL.
 */
export interface AgentHookRequest {
	readonly type: AgentHookEventType;
	readonly meta: AgentHookMeta;
	readonly data: unknown;
}

// From agenthooks/types.go
/**
 * Response carries a consumer's decision and optional injected content.
 * Permission is honored for user_prompt_submit and pre_tool_use only.
 * user_prompt_submit folds injected content into the submitted message.
 * A denied pre_tool_use yields a synthetic tool result carrying only the
 * policy text and any Reason; ModelContext persists separately as
 * model-only transcript content that never reaches clients.
 */
export interface AgentHookResponse {
	readonly permission?: AgentHookPermission;
	readonly model_context?: string;
	readonly user_message?: string;
}

// From agenthooks/types.go
/**
 * SchemaVersion is the current lifecycle hook request schema version.
 */
export const AgentHookSchemaVersion = 1;

// From agenthooks/types.go
/**
 * SessionStartData reports why a chat session started. Source is
 * "startup", "resume", or "clear".
 */
export interface AgentHookSessionStartData {
	readonly source: string;
}

// From agenthooks/types.go
/**
 * StopData is empty; Meta identifies the chat that stopped.
 */
export interface AgentHookStopData {}

// From agenthooks/types.go
/**
 * UserPromptSubmitData includes concatenated text and persisted parts.
 * Inspect Parts when structure matters.
 */
export interface AgentHookUserPromptSubmitData {
	readonly prompt: string;
	readonly parts?: unknown;
}

// From codersdk/workspacebuilds.go
export interface AgentScriptTiming {
	readonly started_at: string;
	readonly ended_at: string;
	readonly exit_code: number;
	readonly stage: TimingStage;
	readonly status: WorkspaceAgentScriptStatus;
	readonly display_name: string;
	readonly workspace_agent_id: string;
	readonly workspace_agent_name: string;
}

// From codersdk/templates.go
/**
 * AgentStatsReportResponse is returned for each report
 * request by the agent.
 */
export interface AgentStatsReportResponse {
	readonly num_comms: number;
	/**
	 * RxBytes is the number of received bytes.
	 */
	readonly rx_bytes: number;
	/**
	 * TxBytes is the number of transmitted bytes.
	 */
	readonly tx_bytes: number;
}

// From codersdk/workspaceagents.go
export type AgentSubsystem = "envbox" | "envbuilder" | "exectrace";

export const AgentSubsystems: AgentSubsystem[] = [
	"envbox",
	"envbuilder",
	"exectrace",
];

// From codersdk/aiproviders.go
export type AgentsUnsupportedProviderType = "copilot";

export const AgentsUnsupportedProviderTypes: AgentsUnsupportedProviderType[] = [
	"copilot",
];

// From codersdk/chats.go
/**
 * AnthropicInlineImageCapBytes is Anthropic's documented per-image
 * wire limit; the same cap applies to Bedrock-hosted Claude. Other
 * providers have no documented per-image cap.
 */
export const AnthropicInlineImageCapBytes = 5242880;

// From codersdk/appname.go
export type AppFamilyName =
	| "jetbrains"
	| "port_forwarding"
	| "reconnecting_pty"
	| "sftp"
	| "ssh"
	| "unknown"
	| "vscode";

export const AppFamilyNames: AppFamilyName[] = [
	"jetbrains",
	"port_forwarding",
	"reconnecting_pty",
	"sftp",
	"ssh",
	"unknown",
	"vscode",
];

// From codersdk/deployment.go
export interface AppHostResponse {
	/**
	 * Host is the externally accessible URL for the Coder instance.
	 */
	readonly host: string;
}

// From codersdk/appname.go
/**
 * AppNameOverflow sums the unregistered app names past the limit of 64.
 */
export const AppNameOverflow = "overflow";

// From codersdk/deployment.go
export interface AppearanceConfig {
	readonly application_name: string;
	readonly logo_url: string;
	readonly docs_url: string;
	/**
	 * @deprecated ServiceBanner has been replaced by AnnouncementBanners.
	 */
	readonly service_banner: BannerConfig;
	readonly announcement_banners: readonly BannerConfig[];
	readonly support_links?: readonly LinkConfig[];
	readonly codernauts_enabled: boolean;
}

// From codersdk/templates.go
export interface ArchiveTemplateVersionsRequest {
	/**
	 * By default, only failed versions are archived. Set this to true
	 * to archive all unused versions regardless of job status.
	 */
	readonly all: boolean;
}

// From codersdk/templates.go
export interface ArchiveTemplateVersionsResponse {
	readonly template_id: string;
	readonly archived_ids: readonly string[];
}

// From codersdk/roles.go
export interface AssignableRoles extends Role {
	readonly assignable: boolean;
	/**
	 * BuiltIn roles are immutable
	 */
	readonly built_in: boolean;
}

// From codersdk/audit.go
export type AuditAction =
	| "close"
	| "connect"
	| "create"
	| "delete"
	| "disconnect"
	| "login"
	| "logout"
	| "open"
	| "register"
	| "request_password_reset"
	| "start"
	| "stop"
	| "write";

export const AuditActions: AuditAction[] = [
	"close",
	"connect",
	"create",
	"delete",
	"disconnect",
	"login",
	"logout",
	"open",
	"register",
	"request_password_reset",
	"start",
	"stop",
	"write",
];

// From codersdk/audit.go
export type AuditDiff = Record<string, AuditDiffField>;

// From codersdk/audit.go
export interface AuditDiffField {
	// empty interface{} type, falling back to unknown
	readonly old?: unknown;
	// empty interface{} type, falling back to unknown
	readonly new?: unknown;
	readonly secret: boolean;
}

// From codersdk/audit.go
export interface AuditLog {
	readonly id: string;
	readonly request_id: string;
	readonly time: string;
	readonly ip: string;
	readonly user_agent: string;
	readonly resource_type: ResourceType;
	readonly resource_id: string;
	/**
	 * ResourceTarget is the name of the resource.
	 */
	readonly resource_target: string;
	readonly resource_icon: string;
	readonly action: AuditAction;
	readonly diff: AuditDiff;
	readonly status_code: number;
	readonly additional_fields: Record<string, string>;
	readonly description: string;
	readonly resource_link: string;
	readonly is_deleted: boolean;
	/**
	 * @deprecated Use 'organization.id' instead.
	 */
	readonly organization_id: string;
	readonly organization?: MinimalOrganization;
	readonly user: User | null;
}

// From codersdk/audit.go
export interface AuditLogResponse {
	readonly audit_logs: readonly AuditLog[];
	readonly count: number;
	readonly count_cap: number;
}

// From codersdk/audit.go
export interface AuditLogsRequest extends Pagination {
	readonly q?: string;
}

// From codersdk/users.go
export interface AuthMethod {
	readonly enabled: boolean;
}

// From codersdk/users.go
/**
 * AuthMethods contains authentication method information like whether they are enabled or not or custom text, etc.
 */
export interface AuthMethods {
	readonly terms_of_service_url?: string;
	readonly password: AuthMethod;
	readonly github: GithubAuthMethod;
	readonly oidc: OIDCAuthMethod;
}

// From codersdk/authorization.go
/**
 * AuthorizationCheck is used to check if the currently authenticated user (or the specified user) can do a given action to a given set of objects.
 *
 * @Description AuthorizationCheck is used to check if the currently authenticated user (or the specified user) can do a given action to a given set of objects.
 */
export interface AuthorizationCheck {
	/**
	 * Object can represent a "set" of objects, such as: all workspaces in an organization, all workspaces owned by me, and all workspaces across the entire product.
	 * When defining an object, use the most specific language when possible to
	 * produce the smallest set. Meaning to set as many fields on 'Object' as
	 * you can. Example, if you want to check if you can update all workspaces
	 * owned by 'me', try to also add an 'OrganizationID' to the settings.
	 * Omitting the 'OrganizationID' could produce the incorrect value, as
	 * workspaces have both `user` and `organization` owners.
	 */
	readonly object: AuthorizationObject;
	readonly action: RBACAction;
}

// From codersdk/authorization.go
/**
 * AuthorizationObject can represent a "set" of objects, such as: all workspaces in an organization, all workspaces owned by me,
 * all workspaces across the entire product.
 *
 * @Description AuthorizationObject can represent a "set" of objects, such as: all workspaces in an organization, all workspaces owned by me,
 * @Description all workspaces across the entire product.
 */
export interface AuthorizationObject {
	/**
	 * ResourceType is the name of the resource.
	 * `./coderd/rbac/object.go` has the list of valid resource types.
	 */
	readonly resource_type: RBACResource;
	/**
	 * OwnerID (optional) adds the set constraint to all resources owned by a given user.
	 */
	readonly owner_id?: string;
	/**
	 * OrganizationID (optional) adds the set constraint to all resources owned by a given organization.
	 */
	readonly organization_id?: string;
	/**
	 * ResourceID (optional) reduces the set to a singular resource. This assigns
	 * a resource ID to the resource type, eg: a single workspace.
	 * The rbac library will not fetch the resource from the database, so if you
	 * are using this option, you should also set the owner ID and organization ID
	 * if possible. Be as specific as possible using all the fields relevant.
	 */
	readonly resource_id?: string;
	/**
	 * AnyOrgOwner (optional) will disregard the org_owner when checking for permissions.
	 * This cannot be set to true if the OrganizationID is set.
	 */
	readonly any_org?: boolean;
}

// From codersdk/authorization.go
/**
 * AuthorizationRequest is a structure instead of a map because
 * go-playground/validate can only validate structs. If you attempt to pass
 * a map into `httpapi.Read`, you will get an invalid type error.
 */
export interface AuthorizationRequest {
	/**
	 * Checks is a map keyed with an arbitrary string to a permission check.
	 * The key can be any string that is helpful to the caller, and allows
	 * multiple permission checks to be run in a single request.
	 * The key ensures that each permission check has the same key in the
	 * response.
	 */
	readonly checks: Record<string, AuthorizationCheck>;
}

// From codersdk/authorization.go
export type AuthorizationResponse = Record<string, boolean>;

// From codersdk/workspaces.go
export type AutomaticUpdates = "always" | "never";

export const AutomaticUpdateses: AutomaticUpdates[] = ["always", "never"];

// From codersdk/deployment.go
/**
 * AvailableExperiments is an expandable type that returns all safe experiments
 * available to be used with a deployment.
 */
export interface AvailableExperiments {
	readonly safe: readonly Experiment[];
}

// From codersdk/deployment.go
export interface BannerConfig {
	readonly enabled: boolean;
	readonly message?: string;
	readonly background_color?: string;
}

// From healthsdk/healthsdk.go
/**
 * BaseReport holds fields common to various health reports.
 */
export interface BaseReport {
	readonly error?: string;
	readonly severity: HealthSeverity;
	readonly warnings: readonly HealthMessage[];
	readonly dismissed: boolean;
}

// From codersdk/deployment.go
/**
 * BuildInfoResponse contains build information for this instance of Coder.
 */
export interface BuildInfoResponse {
	/**
	 * ExternalURL references the current Coder version.
	 * For production builds, this will link directly to a release. For development builds, this will link to a commit.
	 */
	readonly external_url: string;
	/**
	 * Version returns the semantic version of the build.
	 */
	readonly version: string;
	/**
	 * DashboardURL is the URL to hit the deployment's dashboard.
	 * For external workspace proxies, this is the coderd they are connected
	 * to.
	 */
	readonly dashboard_url: string;
	/**
	 * Telemetry is a boolean that indicates whether telemetry is enabled.
	 */
	readonly telemetry: boolean;
	/**
	 * OAuth2Provider reports whether the OAuth 2.1 authorization server is
	 * enabled. The dashboard uses it to show or hide OAuth2 navigation.
	 */
	readonly oauth2_provider: boolean;
	readonly workspace_proxy: boolean;
	/**
	 * AgentAPIVersion is the current version of the Agent API (back versions
	 * MAY still be supported).
	 */
	readonly agent_api_version: string;
	/**
	 * ProvisionerAPIVersion is the current version of the Provisioner API
	 */
	readonly provisioner_api_version: string;
	/**
	 * UpgradeMessage is the message displayed to users when an outdated client
	 * is detected.
	 */
	readonly upgrade_message: string;
	/**
	 * DeploymentID is the unique identifier for this deployment.
	 */
	readonly deployment_id: string;
	/**
	 * WebPushPublicKey is the public key for push notifications via Web Push.
	 */
	readonly webpush_public_key?: string;
}

// From codersdk/workspacebuilds.go
export type BuildReason =
	| "autostart"
	| "autostop"
	| "cli"
	| "dashboard"
	| "dormancy"
	| "initiator"
	| "jetbrains_connection"
	| "ssh_connection"
	| "vscode_connection";

export const BuildReasons: BuildReason[] = [
	"autostart",
	"autostop",
	"cli",
	"dashboard",
	"dormancy",
	"initiator",
	"jetbrains_connection",
	"ssh_connection",
	"vscode_connection",
];

// From codersdk/client.go
/**
 * BuildVersionHeader contains build information of Coder.
 */
export const BuildVersionHeader = "X-Coder-Build-Version";

// From codersdk/client.go
/**
 * BypassRatelimitHeader is the custom header to use to bypass ratelimits.
 * Only owners can bypass rate limits. This is typically used for scale testing.
 * nolint: gosec
 */
export const BypassRatelimitHeader = "X-Coder-Bypass-Ratelimit";

// From codersdk/client.go
/**
 * CLITelemetryHeader contains a base64-encoded representation of the CLI
 * command that was invoked to produce the request. It is for internal use
 * only.
 */
export const CLITelemetryHeader = "Coder-CLI-Telemetry";

// From codersdk/cors_behavior.go
export type CORSBehavior = "passthru" | "simple";

export const CORSBehaviors: CORSBehavior[] = ["passthru", "simple"];

// From codersdk/workspacebuilds.go
export interface CancelWorkspaceBuildParams {
	/**
	 * ExpectStatus ensures the build is in the expected status before canceling.
	 */
	readonly expect_status?: CancelWorkspaceBuildStatus;
}

// From codersdk/workspacebuilds.go
export type CancelWorkspaceBuildStatus = "pending" | "running";

export const CancelWorkspaceBuildStatuses: CancelWorkspaceBuildStatus[] = [
	"pending",
	"running",
];

// From codersdk/users.go
/**
 * ChangePasswordWithOneTimePasscodeRequest enables callers to change their password when they've forgotten it.
 */
export interface ChangePasswordWithOneTimePasscodeRequest {
	readonly email: string;
	readonly password: string;
	readonly one_time_passcode: string;
}

// From codersdk/chats.go
/**
 * ChangedDiffStatus is the diff status of one ref after a change.
 * When the ref has no stored status, Status has only chat_id.
 */
export interface ChangedDiffStatus {
	readonly ref: DiffStatusRef;
	readonly status: ChatDiffStatus | null;
}

// From codersdk/chats.go
/**
 * Chat represents a chat session with an AI agent.
 */
export interface Chat {
	readonly id: string;
	readonly organization_id: string;
	readonly owner_id: string;
	readonly owner_username?: string;
	readonly owner_name?: string;
	readonly workspace_id?: string;
	readonly project_id?: string;
	readonly build_id?: string;
	readonly agent_id?: string;
	readonly parent_chat_id?: string;
	readonly root_chat_id?: string;
	readonly last_model_config_id: string;
	readonly last_reasoning_effort?: string;
	readonly title: string;
	/**
	 * TitleSource is where Title came from. A title write applies only when
	 * the current source ranks the same as or lower than the incoming one,
	 * in the order fallback, generated, user.
	 */
	readonly title_source: ChatTitleSource;
	/**
	 * TitleUpdatedAt orders title changes. Title writes do not change
	 * UpdatedAt.
	 */
	readonly title_updated_at: string;
	readonly status: ChatStatus;
	readonly plan_mode?: ChatPlanMode;
	readonly last_error?: ChatError;
	readonly last_turn_summary: string | null;
	/**
	 * Summary is the persisted whole-chat summary, generated in the background.
	 * It is nil until the first summary has been produced.
	 */
	readonly summary: string | null;
	/**
	 * DiffStatus is the primary pull request. It is the ref with the
	 * most recent git report.
	 */
	readonly diff_status?: ChatDiffStatus;
	/**
	 * DiffStatuses lists every ref the chat tracks. The order is
	 * stable and follows the first report of each ref. DiffStatus
	 * marks the primary.
	 */
	readonly diff_statuses?: readonly ChatDiffStatus[];
	readonly created_at: string;
	readonly updated_at: string;
	readonly archived: boolean;
	/**
	 * Shared is true when this chat's root chat has explicit user or group ACL entries.
	 */
	readonly shared: boolean;
	readonly pin_order: number;
	readonly mcp_server_ids: readonly string[];
	readonly labels: Record<string, string>;
	readonly files?: readonly ChatFileMetadata[];
	/**
	 * HasUnread is true when assistant messages exist beyond
	 * the owner's read cursor, which updates on stream
	 * connect and disconnect and via UpdateChatRequest.Read.
	 */
	readonly has_unread: boolean;
	/**
	 * Context reports the chat's pinned workspace-context state and
	 * whether it has drifted from the agent's latest pushed snapshot.
	 * Nil when the chat has no pinned context yet.
	 */
	readonly context?: ChatContext;
	/**
	 * QueuedForCapacity reports that the chat is waiting for a concurrent
	 * agent slot. Single-chat reads derive it; list responses leave it false.
	 */
	readonly queued_for_capacity?: boolean;
	readonly warnings?: readonly string[];
	readonly client_type: ChatClientType;
	/**
	 * InlineMCPServers lists the inline MCP servers declared on the chat,
	 * without headers. Only the single-chat GET sets it.
	 * Experimental.
	 */
	readonly inline_mcp_servers?: readonly InlineMCPServer[];
	/**
	 * Children holds child (subagent) chats nested under this root
	 * chat. Always initialized to an empty slice so the JSON field
	 * is present as []. Child chats cannot create their own
	 * subagents, so nesting depth is capped at 1 and this slice is
	 * always empty for child chats.
	 */
	readonly children: readonly Chat[];
}

// From codersdk/chats.go
export interface ChatACL {
	readonly users: readonly ChatUser[];
	readonly groups: readonly ChatGroup[];
}

// From codersdk/chats.go
/**
 * ChatACPTranscriptMessage is a workspace-local ACP message returned by
 * acp_wait_agent without database message metadata.
 */
export interface ChatACPTranscriptMessage {
	readonly role: ChatMessageRole;
	readonly content: readonly ChatMessagePart[];
}

// From codersdk/chats.go
/**
 * ChatAttachment is a file part of a chat message, returned by
 * GET /api/v2/chats/{chat}/attachments.
 */
export interface ChatAttachment {
	readonly message_id: number;
	/**
	 * PartIndex is the file part's position in the message content the
	 * messages endpoint returns.
	 */
	readonly part_index: number;
	readonly role: ChatAttachmentRole;
	readonly file: ChatAttachmentFile;
}

// From codersdk/chats.go
/**
 * ChatAttachmentFile holds the fields of a file part, the only part type
 * the attachments endpoint returns.
 */
export interface ChatAttachmentFile {
	readonly type: ChatAttachmentFileType;
	readonly media_type: string;
	readonly name?: string;
	/**
	 * Data holds an inline file's bytes, which JSON carries as a base64 string.
	 */
	readonly data?: string;
	/**
	 * Omitted, not null, for an inline file.
	 */
	readonly file_id?: string;
}

// From codersdk/chats.go
export type ChatAttachmentFileType = "file";

export const ChatAttachmentFileTypes: ChatAttachmentFileType[] = ["file"];

// From codersdk/chats.go
export type ChatAttachmentMediaType =
	| "application/json"
	| "application/pdf"
	| "image/gif"
	| "image/jpeg"
	| "image/png"
	| "image/svg+xml"
	| "image/webp"
	| "text/csv"
	| "text/markdown"
	| "text/plain";

export const ChatAttachmentMediaTypes: ChatAttachmentMediaType[] = [
	"application/json",
	"application/pdf",
	"image/gif",
	"image/jpeg",
	"image/png",
	"image/svg+xml",
	"image/webp",
	"text/csv",
	"text/markdown",
	"text/plain",
];

// From codersdk/chats.go
export type ChatAttachmentRole = "assistant" | "user";

export const ChatAttachmentRoles: ChatAttachmentRole[] = ["assistant", "user"];

// From codersdk/chats.go
/**
 * ChatAttachmentsResponse is the payload of
 * GET /api/v2/chats/{chat}/attachments. Attachments are returned newest
 * first.
 */
export interface ChatAttachmentsResponse {
	readonly attachments: readonly ChatAttachment[];
}

// From codersdk/chats.go
/**
 * ChatAutoArchiveDaysResponse contains the current chat auto-archive setting.
 */
export interface ChatAutoArchiveDaysResponse {
	readonly auto_archive_days: number;
}

// From codersdk/chats.go
export type ChatBusyBehavior = "interrupt" | "queue";

export const ChatBusyBehaviors: ChatBusyBehavior[] = ["interrupt", "queue"];

// From codersdk/chats.go
export type ChatClientType = "api" | "ui";

export const ChatClientTypes: ChatClientType[] = ["api", "ui"];

// From codersdk/chats.go
/**
 * ChatCompactionThresholdKeyPrefix scopes per-model chat compaction
 * threshold settings.
 */
export const ChatCompactionThresholdKeyPrefix =
	"chat_compaction_threshold_pct:";

// From codersdk/chats.go
export type ChatComputerUseProvider = "anthropic" | "openai";

export const ChatComputerUseProviders: ChatComputerUseProvider[] = [
	"anthropic",
	"openai",
];

// From codersdk/deployment.go
/**
 * ChatConfig configures Coder Agents chats.
 */
export interface ChatConfig {
	readonly acquire_batch_size: number;
	readonly debug_logging_enabled: boolean;
	readonly hook_url: string;
	readonly hook_secret: string;
	readonly hook_timeout: number;
	readonly hook_enabled: boolean;
	readonly hook_allow_insecure: boolean;
	readonly stream_silence_timeout: number;
	/**
	 * MaxStepsPerTurn is the maximum number of steps in a chat turn.
	 */
	readonly max_steps_per_turn: number;
	/**
	 * MaxGenerationRetries is the maximum number of consecutive retries
	 * after a model generation fails with a transient error.
	 */
	readonly max_generation_retries: number;
	/**
	 * MaxQueuedMessagesPerChat is the maximum number of messages that can
	 * be queued in a chat.
	 */
	readonly max_queued_messages_per_chat: number;
	/**
	 * MaxAttachmentsPerChat is the maximum number of files linked to a
	 * chat.
	 */
	readonly max_attachments_per_chat: number;
	/**
	 * MaxPromptBytes is the maximum size in bytes of the deployment system
	 * prompt, the plan mode instructions, and each user's custom prompt.
	 */
	readonly max_prompt_bytes: number;
	/**
	 * MaxConcurrentRecordingUploads is the maximum number of virtual
	 * desktop recordings that each Coder server stores at the same time.
	 */
	readonly max_concurrent_recording_uploads: number;
	/**
	 * @deprecated AI Gateway routing is now the only routing path. Setting this
	 * value has no effect. This option will be removed in a future release.
	 */
	readonly ai_gateway_routing_enabled: boolean;
}

// From codersdk/chats.go
/**
 * ChatContext reports a chat's pinned workspace context and whether it has
 * drifted from the agent's latest pushed snapshot. The chat stays usable
 * when dirty; refreshing re-pins it to the latest snapshot.
 */
export interface ChatContext {
	/**
	 * Dirty is true when the agent's latest snapshot hash differs from the
	 * chat's pinned hash.
	 */
	readonly dirty: boolean;
	/**
	 * DirtySince is when drift was first detected; nil when not dirty.
	 */
	readonly dirty_since?: string;
	/**
	 * Error is the snapshot-level error copied from the pinned snapshot
	 * (empty when healthy).
	 */
	readonly error?: string;
	/**
	 * Resources is the chat's pinned workspace-context inventory,
	 * metadata only (no bodies). It is populated only on the single-chat
	 * GET response; list and watch
	 * payloads leave it nil to stay lightweight.
	 */
	readonly resources?: readonly ChatContextResource[];
}

// From codersdk/chats.go
export interface ChatContextFilePart {
	readonly type: "context-file";
	/**
	 * ContextFilePath is the absolute path of a file loaded into
	 * the LLM context (e.g. an AGENTS.md instruction file).
	 */
	readonly context_file_path: string;
	/**
	 * ContextFileTruncated indicates the file exceeded the 64KiB
	 * instruction file limit and was truncated.
	 */
	readonly context_file_truncated?: boolean;
	/**
	 * ContextFileAgentID is the workspace agent that provided
	 * this context file. Used to detect when the agent changes
	 * (e.g. workspace rebuilt) so instruction files can be
	 * re-persisted with fresh content.
	 */
	readonly context_file_agent_id?: string;
}

// From codersdk/chats.go
/**
 * ChatContextResource is one resource in the chat's pinned workspace-context
 * inventory. It is metadata only; bodies are omitted. Reported
 * only on the single-chat GET response.
 */
export interface ChatContextResource {
	/**
	 * Source is the resource locator: the canonical file path for an
	 * instruction file, the skill directory for a skill, the file path for
	 * an MCP config, the server name for an MCP server, or an ACP harness slug.
	 */
	readonly source: string;
	readonly kind: ChatContextResourceKind;
	/**
	 * SourcePath is the configuration directory for an ACP harness.
	 */
	readonly source_path?: string;
	/**
	 * SizeBytes is the original payload size in bytes.
	 */
	readonly size_bytes: number;
	/**
	 * SkillName and SkillDescription are populated only for skill kinds.
	 */
	readonly skill_name?: string;
	readonly skill_description?: string;
	/**
	 * Tools lists the tools exposed by an MCP server. Populated only for the
	 * mcp_server kind; nil otherwise.
	 */
	readonly tools?: readonly ChatContextTool[];
	/**
	 * Status is the resource's health. Non-ok resources (invalid, unreadable,
	 * oversize, excluded) are still reported so the UI can surface why a
	 * resource was dropped from the prompt instead of silently omitting it;
	 * their body-specific fields (skill name, tools) are empty.
	 */
	readonly status: ChatContextResourceStatus;
	/**
	 * Error explains a non-ok Status; empty when healthy. May also carry a
	 * non-fatal warning when Status is ok.
	 */
	readonly error?: string;
}

// From codersdk/chats.go
export type ChatContextResourceKind =
	| "acp_harness"
	| "instruction_file"
	| "mcp_config"
	| "mcp_server"
	| "skill";

export const ChatContextResourceKinds: ChatContextResourceKind[] = [
	"acp_harness",
	"instruction_file",
	"mcp_config",
	"mcp_server",
	"skill",
];

// From codersdk/chats.go
export type ChatContextResourceStatus =
	| "excluded"
	| "invalid"
	| "ok"
	| "oversize"
	| "unreadable";

export const ChatContextResourceStatuses: ChatContextResourceStatus[] = [
	"excluded",
	"invalid",
	"ok",
	"oversize",
	"unreadable",
];

// From codersdk/chats.go
/**
 * ChatContextTool is one tool exposed by a pinned MCP server, reported on the
 * single-chat GET response. Metadata only; the input schema is omitted.
 */
export interface ChatContextTool {
	/**
	 * Name is the tool name with the `<server>__` prefix the agent adds
	 * stripped, so it reads as the server exposes it.
	 */
	readonly name: string;
	/**
	 * Description is the tool's human-readable summary; may be empty.
	 */
	readonly description?: string;
}

// From codersdk/chats.go
/**
 * ChatCost is the AI Gateway cost for the requested chat's whole tree.
 * Root and subagent chats report the same total.
 * RequestCount counts every finished request in the tree, including ones that
 * recorded no billable usage at all, such as a request that failed upstream.
 * UnpricedRequestCount counts requests with at least one usage record whose
 * model had no recorded price; RequestCount includes them and
 * TotalCostMicros omits only their unpriced usage.
 */
export interface ChatCost {
	readonly chat_id: string;
	readonly total_cost_micros: number;
	readonly request_count: number;
	readonly unpriced_request_count: number;
}

// From codersdk/chats.go
/**
 * ChatDebugLoggingAdminSettings describes the runtime admin setting
 * that allows users to opt into chat debug logging.
 */
export interface ChatDebugLoggingAdminSettings {
	readonly allow_users: boolean;
	readonly forced_by_deployment: boolean;
}

// From codersdk/chats.go
/**
 * ChatDebugRetentionDaysResponse contains the current chat debug run
 * retention setting.
 */
export interface ChatDebugRetentionDaysResponse {
	readonly debug_retention_days: number;
}

// From codersdk/chats.go
/**
 * ChatDebugRun is the detailed run response returned by the run-detail
 * endpoint. It includes the same summary fields as ChatDebugRunSummary
 * along with the full step history for the run.
 */
export interface ChatDebugRun {
	readonly id: string;
	readonly chat_id: string;
	readonly root_chat_id?: string;
	readonly parent_chat_id?: string;
	readonly model_config_id?: string;
	readonly trigger_message_id?: number;
	readonly history_tip_message_id?: number;
	readonly kind: ChatDebugRunKind;
	readonly status: ChatDebugStatus;
	readonly provider?: string;
	readonly model?: string;
	// empty interface{} type, falling back to unknown
	readonly summary: Record<string, unknown>;
	readonly started_at: string;
	readonly updated_at: string;
	readonly finished_at?: string;
	readonly steps: readonly ChatDebugStep[];
}

// From codersdk/chats.go
export type ChatDebugRunKind =
	| "chat_turn"
	| "compaction"
	| "quickgen"
	| "title_generation";

export const ChatDebugRunKinds: ChatDebugRunKind[] = [
	"chat_turn",
	"compaction",
	"quickgen",
	"title_generation",
];

// From codersdk/chats.go
/**
 * ChatDebugRunSummary is a lightweight run entry for list endpoints.
 */
export interface ChatDebugRunSummary {
	readonly id: string;
	readonly chat_id: string;
	readonly kind: ChatDebugRunKind;
	readonly status: ChatDebugStatus;
	readonly provider?: string;
	readonly model?: string;
	// empty interface{} type, falling back to unknown
	readonly summary: Record<string, unknown>;
	readonly started_at: string;
	readonly updated_at: string;
	readonly finished_at?: string;
}

// From codersdk/chats.go
export type ChatDebugStatus =
	| "completed"
	| "error"
	| "in_progress"
	| "interrupted";

export const ChatDebugStatuses: ChatDebugStatus[] = [
	"completed",
	"error",
	"in_progress",
	"interrupted",
];

// From codersdk/chats.go
/**
 * ChatDebugStep is a single step within a debug run.
 */
export interface ChatDebugStep {
	readonly id: string;
	readonly run_id: string;
	readonly chat_id: string;
	readonly step_number: number;
	readonly operation: ChatDebugStepOperation;
	readonly status: ChatDebugStatus;
	readonly history_tip_message_id?: number;
	readonly assistant_message_id?: number;
	// empty interface{} type, falling back to unknown
	readonly normalized_request: Record<string, unknown>;
	// empty interface{} type, falling back to unknown
	readonly normalized_response?: Record<string, unknown>;
	// empty interface{} type, falling back to unknown
	readonly usage?: Record<string, unknown>;
	// empty interface{} type, falling back to unknown
	readonly attempts: readonly Record<string, unknown>[];
	// empty interface{} type, falling back to unknown
	readonly error?: Record<string, unknown>;
	// empty interface{} type, falling back to unknown
	readonly metadata: Record<string, unknown>;
	readonly started_at: string;
	readonly updated_at: string;
	readonly finished_at?: string;
}

// From codersdk/chats.go
export type ChatDebugStepOperation = "generate" | "stream";

export const ChatDebugStepOperations: ChatDebugStepOperation[] = [
	"generate",
	"stream",
];

// From codersdk/chats.go
/**
 * ChatDiffContents represents the resolved diff text for a chat.
 */
export interface ChatDiffContents {
	readonly chat_id: string;
	readonly provider?: string;
	readonly remote_origin?: string;
	readonly branch?: string;
	readonly pull_request_url?: string;
	readonly diff?: string;
}

// From codersdk/chats.go
/**
 * ChatDiffStatus represents cached diff status for a chat. The URL
 * may point to a pull request or a branch page depending on whether
 * a PR has been opened.
 */
export interface ChatDiffStatus {
	readonly chat_id: string;
	readonly remote_origin?: string;
	readonly git_branch?: string;
	readonly url?: string;
	readonly pull_request_state?: string;
	readonly pull_request_title: string;
	readonly pull_request_draft: boolean;
	readonly changes_requested: boolean;
	readonly additions: number;
	readonly deletions: number;
	readonly changed_files: number;
	readonly author_login?: string;
	readonly author_avatar_url?: string;
	readonly base_branch?: string;
	readonly head_branch?: string;
	readonly pr_number?: number;
	readonly commits?: number;
	readonly approved?: boolean;
	readonly reviewer_count?: number;
	readonly refreshed_at?: string;
	readonly stale_at?: string;
}

// From codersdk/chats.go
/**
 * ChatError represents a terminal chat error in persisted chat state or the
 * live stream.
 */
export interface ChatError {
	/**
	 * Message is the normalized, user-facing error message.
	 */
	readonly message: string;
	/**
	 * Detail is optional provider-specific context shown alongside the
	 * normalized error message when available.
	 */
	readonly detail?: string;
	/**
	 * Kind classifies the error for consistent client rendering.
	 */
	readonly kind?: ChatErrorKind;
	/**
	 * Provider identifies the upstream model provider when known.
	 */
	readonly provider?: string;
	/**
	 * Retryable reports whether the underlying error is transient.
	 */
	readonly retryable: boolean;
	/**
	 * StatusCode is the best-effort upstream HTTP status code.
	 */
	readonly status_code?: number;
}

// From codersdk/chats.go
export type ChatErrorKind =
	| "auth"
	| "config"
	| "content_filter"
	| "generic"
	| "hook_denied"
	| "hook_dispatch_failed"
	| "missing_key"
	| "overloaded"
	| "provider_disabled"
	| "rate_limit"
	| "stream_silence_timeout"
	| "timeout"
	| "usage_limit";

export const ChatErrorKinds: ChatErrorKind[] = [
	"auth",
	"config",
	"content_filter",
	"generic",
	"hook_denied",
	"hook_dispatch_failed",
	"missing_key",
	"overloaded",
	"provider_disabled",
	"rate_limit",
	"stream_silence_timeout",
	"timeout",
	"usage_limit",
];

// From codersdk/chats.go
/**
 * ChatFileDownloadURLResponse contains a short-lived URL for downloading a chat file.
 */
export interface ChatFileDownloadURLResponse {
	readonly url: string;
	readonly expires_at: string;
	readonly sha256: string;
	readonly size_bytes: number;
	readonly name: string;
	readonly mime_type: string;
}

// From codersdk/chats.go
/**
 * ChatFileMetadata contains lightweight metadata about a file
 * associated with a chat, excluding the file content itself.
 */
export interface ChatFileMetadata {
	readonly id: string;
	readonly owner_id: string;
	readonly organization_id: string;
	readonly name: string;
	readonly mime_type: string;
	readonly size_bytes: number;
	readonly created_at: string;
}

// From codersdk/chats.go
export interface ChatFilePart {
	readonly type: "file";
	readonly media_type: string;
	readonly name?: string;
	readonly data?: string;
	readonly file_id?: string;
}

// From codersdk/chats.go
export interface ChatFileReferencePart {
	readonly type: "file-reference";
	readonly file_name: string;
	readonly start_line: number;
	readonly end_line: number;
	/**
	 * The code content from the diff that was commented on.
	 */
	readonly content: string;
}

// From codersdk/chats.go
/**
 * Chat git watch error messages. These are the user-visible messages
 * the server returns in 400 responses from
 * /api/v2/chats/{id}/stream/git when the chat cannot be
 * observed through a workspace agent.
 * ChatGitWatchAgentStatePrefix is the common prefix of the
 * message produced by ChatGitWatchAgentStateMessage.
 */
export const ChatGitWatchAgentStatePrefix = "Agent state is ";

// From codersdk/chats.go
/**
 * Chat git watch error messages. These are the user-visible messages
 * the server returns in 400 responses from
 * /api/v2/chats/{id}/stream/git when the chat cannot be
 * observed through a workspace agent.
 */
export const ChatGitWatchNoEligibleAgentMessage =
	"No eligible agent found for chat workspace.";

// From codersdk/chats.go
/**
 * Chat git watch error messages. These are the user-visible messages
 * the server returns in 400 responses from
 * /api/v2/chats/{id}/stream/git when the chat cannot be
 * observed through a workspace agent.
 */
export const ChatGitWatchNoWorkspaceMessage = "Chat has no workspace to watch.";

// From codersdk/chats.go
/**
 * Chat git watch error messages. These are the user-visible messages
 * the server returns in 400 responses from
 * /api/v2/chats/{id}/stream/git when the chat cannot be
 * observed through a workspace agent.
 */
export const ChatGitWatchWorkspaceNotFoundMessage = "Chat workspace not found.";

// From codersdk/chats.go
export interface ChatGroup extends Group {
	readonly role: ChatRole;
}

// From codersdk/chats.go
/**
 * ChatHookDeniedResponse is the error body returned when a lifecycle hook
 * denies a synchronous chat operation. Kind lets clients classify the denial
 * without parsing message text.
 */
export interface ChatHookDeniedResponse extends Response {
	readonly kind: ChatErrorKind;
}

// From codersdk/chats.go
/**
 * ChatHookDispatchFailedResponse is the error body returned when a
 * lifecycle hook dispatch fails during a synchronous chat operation.
 * Kind lets clients classify the failure without parsing message text.
 */
export interface ChatHookDispatchFailedResponse extends Response {
	readonly kind: ChatErrorKind;
}

// From codersdk/chats.go
export interface ChatHookNoticePart {
	readonly type: "hook-notice";
	readonly text: string;
}

// From codersdk/chats.go
/**
 * ChatInputPart is a single user input part for creating a chat.
 */
export interface ChatInputPart {
	readonly type: ChatInputPartType;
	readonly text?: string;
	readonly file_id?: string;
	/**
	 * The following fields are only set when Type is
	 * ChatInputPartTypeFileReference.
	 */
	readonly file_name?: string;
	readonly start_line?: number;
	readonly end_line?: number;
	/**
	 * The code content from the diff that was commented on.
	 */
	readonly content?: string;
	/**
	 * The following fields are only set when Type is
	 * ChatInputPartTypeWorkspaceFileReference.
	 */
	readonly workspace_file_path?: string;
	readonly workspace_file_name?: string;
	readonly workspace_file_size?: number;
	readonly workspace_file_media_type?: string;
	/**
	 * WorkspaceFileWorkspaceID is the workspace the file was uploaded
	 * to, as returned by the upload endpoint. It must match the chat's
	 * currently bound workspace.
	 */
	readonly workspace_file_workspace_id?: string;
	/**
	 * The following fields are only set when Type is
	 * ChatInputPartTypeSkillInvocation. The server loads the skill
	 * as the chat owner when the message is sent.
	 */
	readonly skill_source?: ChatSkillSource;
	readonly skill_name?: string;
}

// From codersdk/chats.go
export type ChatInputPartType =
	| "file"
	| "file-reference"
	| "skill-invocation"
	| "text"
	| "workspace-file-reference";

export const ChatInputPartTypes: ChatInputPartType[] = [
	"file",
	"file-reference",
	"skill-invocation",
	"text",
	"workspace-file-reference",
];

// From codersdk/chats.go
export type ChatListSource = "created_by_me" | "shared_with_me";

export const ChatListSources: ChatListSource[] = [
	"created_by_me",
	"shared_with_me",
];

// From codersdk/chats.go
/**
 * ChatMessage represents a single message in a chat.
 */
export interface ChatMessage {
	readonly id: number;
	readonly chat_id: string;
	readonly created_by?: string;
	readonly model_config_id?: string;
	readonly created_at: string;
	readonly role: ChatMessageRole;
	readonly content?: readonly ChatMessagePart[];
	readonly usage?: ChatMessageUsage;
	/**
	 * QueuedMessageID is the ID of the queued message this message was
	 * promoted from. It matches ChatQueuedMessage.ID in the response that
	 * queued the message. It is nil when the message was not promoted from
	 * the queue (edits create a new message without it) or when a server
	 * version that did not record the link created it.
	 */
	readonly queued_message_id?: number;
}

// From codersdk/chats.go
/**
 * ChatMessagePart is a structured chunk of a chat message.
 *
 * WARNING: This type is both an API wire type and a database
 * persistence format. Its JSON layout is stored in the
 * chat_messages.content column. Field additions, renames, type
 * changes, and omitempty behavior all affect backward-compatible
 * deserialization of stored rows. Treat changes to this struct
 * with the same care as a database migration.
 *
 * The variants struct tag declares which discriminated-union
 * variants include each field in the generated TypeScript. Bare
 * name = required, ? suffix = optional. Fields without a variants
 * tag are excluded from the generated union. See
 * scripts/apitypings/main.go for the codegen that reads these.
 *
 * omitempty rules (enforced by TestChatMessagePartVariantTags):
 *   - If a field is required (no ? suffix) in ANY variant, it
 *     must NOT use omitempty. Go would silently drop zero values
 *     that TypeScript expects to always be present.
 *   - If a field is optional (? suffix) in ALL of its variants,
 *     it MUST use omitempty. Sending zero values for fields that
 *     the frontend does not expect adds noise to the wire format
 *     and wastes space in persisted chat_messages rows.
 */
export type ChatMessagePart =
	| ChatTextPart
	| ChatReasoningPart
	| ChatToolCallPart
	| ChatToolResultPart
	| ChatSourcePart
	| ChatFilePart
	| ChatFileReferencePart
	| ChatContextFilePart
	| ChatSkillPart
	| ChatWorkspaceFileReferencePart
	| ChatSkillInvocationPart
	| ChatHookNoticePart;

// From codersdk/chats.go
export type ChatMessagePartType =
	| "context-file"
	| "file"
	| "file-reference"
	| "hook-context"
	| "hook-notice"
	| "reasoning"
	| "skill"
	| "skill-invocation"
	| "source"
	| "text"
	| "tool-call"
	| "tool-result"
	| "workspace-file-reference";

export const ChatMessagePartTypes: ChatMessagePartType[] = [
	"context-file",
	"file",
	"file-reference",
	"hook-context",
	"hook-notice",
	"reasoning",
	"skill",
	"skill-invocation",
	"source",
	"text",
	"tool-call",
	"tool-result",
	"workspace-file-reference",
];

// From codersdk/chats.go
export type ChatMessageRole = "assistant" | "system" | "tool" | "user";

export const ChatMessageRoles: ChatMessageRole[] = [
	"assistant",
	"system",
	"tool",
	"user",
];

// From codersdk/chats.go
/**
 * ChatMessageUsage contains token usage information for a chat message.
 */
export interface ChatMessageUsage {
	readonly input_tokens?: number;
	readonly output_tokens?: number;
	readonly total_tokens?: number;
	/**
	 * ReasoningTokens counts reasoning, which output_tokens already
	 * includes, so do not add the two.
	 */
	readonly reasoning_tokens?: number;
	readonly cache_creation_tokens?: number;
	readonly cache_read_tokens?: number;
	readonly context_limit?: number;
}

// From codersdk/chats.go
/**
 * GetChatMessages returns the messages and queued messages for a chat.
 * ChatMessagesPaginationOptions are optional pagination params for
 * GetChatMessages.
 */
export interface ChatMessagesPaginationOptions {
	readonly BeforeID: number;
	/**
	 * AfterID, when > 0, restricts results to messages with id strictly
	 * greater than AfterID. When set without BeforeID, results come back
	 * in ASCENDING id order so a polling caller can advance its cursor
	 * to max(returned_ids) without gaps. When combined with BeforeID,
	 * results come back in DESC order over the open range
	 * (AfterID, BeforeID).
	 */
	readonly AfterID: number;
	readonly Limit: number;
}

// From codersdk/chats.go
/**
 * ChatMessagesResponse contains the messages and queued messages for a chat.
 */
export interface ChatMessagesResponse {
	readonly messages: readonly ChatMessage[];
	readonly queued_messages: readonly ChatQueuedMessage[];
	readonly has_more: boolean;
	/**
	 * TurnStartID is the ID of the user prompt that starts the turn
	 * containing the page's oldest message. Omitted for after_id-only polls
	 * and when no prompt is at or before that message.
	 */
	readonly turn_start_id?: number;
}

// From codersdk/chats.go
/**
 * ChatModel is an org-scoped model configuration.
 */
export interface ChatModel {
	readonly id: string;
	readonly organization_id: string;
	readonly ai_provider_id: string;
	readonly model: string;
	readonly display_name: string;
	readonly enabled: boolean;
	readonly is_default: boolean;
	readonly context_limit: number;
	readonly compression_threshold: number;
	readonly model_config?: ChatModelCallConfig;
	/**
	 * ReasoningEfforts lists selectable reasoning effort values through
	 * the model's configured maximum.
	 */
	readonly reasoning_efforts?: readonly string[];
	readonly created_at: string;
	readonly updated_at: string;
}

// From codersdk/chats.go
/**
 * ChatModelACL is the access control list for an organization-scoped chat
 * model. Each principal includes the identity details needed to display and
 * manage the ACL without separate directory lookups.
 */
export interface ChatModelACL {
	readonly users: readonly ChatUser[];
	readonly groups: readonly ChatGroup[];
}

// From codersdk/chats.go
/**
 * ChatModelAnthropicProviderOptions configures Anthropic provider behavior.
 */
export interface ChatModelAnthropicProviderOptions {
	readonly send_reasoning?: boolean;
	readonly thinking?: ChatModelAnthropicThinkingOptions;
	readonly thinking_display?: string;
	readonly disable_parallel_tool_use?: boolean;
	readonly web_search_enabled?: boolean;
	readonly allowed_domains?: readonly string[];
	readonly blocked_domains?: readonly string[];
	readonly context_1m_enabled?: boolean;
}

// From codersdk/chats.go
/**
 * ChatModelAnthropicThinkingOptions configures Anthropic thinking budget.
 */
export interface ChatModelAnthropicThinkingOptions {
	readonly budget_tokens?: number;
}

// From codersdk/chats.go
/**
 * ChatModelCallConfig configures per-call model behavior defaults.
 */
export interface ChatModelCallConfig {
	readonly max_output_tokens?: number;
	readonly temperature?: number;
	readonly top_p?: number;
	readonly top_k?: number;
	readonly presence_penalty?: number;
	readonly frequency_penalty?: number;
	readonly reasoning_effort?: ChatModelReasoningEffortConfig;
	readonly openai_config?: ChatModelOpenAIConfig;
	readonly provider_options?: ChatModelProviderOptions;
}

// From codersdk/chats.go
/**
 * ChatModelGoogleProviderOptions configures Google provider behavior.
 */
export interface ChatModelGoogleProviderOptions {
	readonly thinking_config?: ChatModelGoogleThinkingConfig;
	readonly cached_content?: string;
	readonly safety_settings?: readonly ChatModelGoogleSafetySetting[];
	readonly threshold?: string;
	readonly web_search_enabled?: boolean;
}

// From codersdk/chats.go
/**
 * ChatModelGoogleSafetySetting configures Google safety filtering.
 */
export interface ChatModelGoogleSafetySetting {
	readonly category?: string;
	readonly threshold?: string;
}

// From codersdk/chats.go
/**
 * ChatModelGoogleThinkingConfig configures Google thinking behavior.
 */
export interface ChatModelGoogleThinkingConfig {
	readonly thinking_budget?: number;
	readonly thinking_level?: string;
	readonly include_thoughts?: boolean;
}

// From codersdk/chats.go
/**
 * ChatModelOpenAICompatProviderOptions configures OpenAI-compatible behavior.
 */
export interface ChatModelOpenAICompatProviderOptions {
	readonly user?: string;
}

// From codersdk/chats.go
/**
 * ChatModelOpenAIConfig holds settings applied once when the OpenAI client
 * is built, not per request, including OpenAI-format models on Bedrock.
 */
export interface ChatModelOpenAIConfig {
	readonly use_responses_api?: boolean;
	readonly reasoning_model?: boolean;
}

// From codersdk/chats.go
/**
 * ChatModelOpenAIProviderOptions configures OpenAI provider behavior.
 */
export interface ChatModelOpenAIProviderOptions {
	readonly include?: readonly string[];
	readonly instructions?: string;
	readonly logit_bias?: Record<string, number>;
	readonly log_probs?: boolean;
	readonly top_log_probs?: number;
	readonly max_tool_calls?: number;
	readonly parallel_tool_calls?: boolean;
	readonly user?: string;
	readonly reasoning_summary?: string;
	readonly max_completion_tokens?: number;
	readonly text_verbosity?: string;
	// empty interface{} type, falling back to unknown
	readonly prediction?: Record<string, unknown>;
	readonly store?: boolean;
	// empty interface{} type, falling back to unknown
	readonly metadata?: Record<string, unknown>;
	readonly prompt_cache_key?: string;
	readonly safety_identifier?: string;
	readonly reasoning_mode?: string;
	readonly service_tier?: string;
	readonly structured_outputs?: boolean;
	readonly strict_json_schema?: boolean;
	readonly web_search_enabled?: boolean;
	readonly search_context_size?: string;
	readonly allowed_domains?: readonly string[];
}

// From codersdk/chats.go
/**
 * ChatModelOpenRouterProvider configures OpenRouter routing preferences.
 */
export interface ChatModelOpenRouterProvider {
	readonly order?: readonly string[];
	readonly allow_fallbacks?: boolean;
	readonly require_parameters?: boolean;
	readonly data_collection?: string;
	readonly only?: readonly string[];
	readonly ignore?: readonly string[];
	readonly quantizations?: readonly string[];
	readonly sort?: string;
}

// From codersdk/chats.go
/**
 * ChatModelOpenRouterProviderOptions configures OpenRouter provider behavior.
 */
export interface ChatModelOpenRouterProviderOptions {
	readonly reasoning?: ChatModelReasoningOptions;
	// empty interface{} type, falling back to unknown
	readonly extra_body?: Record<string, unknown>;
	readonly include_usage?: boolean;
	readonly logit_bias?: Record<string, number>;
	readonly log_probs?: boolean;
	readonly parallel_tool_calls?: boolean;
	readonly user?: string;
	readonly provider?: ChatModelOpenRouterProvider;
}

// From codersdk/chats.go
export type ChatModelOverrideContext =
	| "advisor"
	| "compaction"
	| "computer_use"
	| "explore"
	| "general"
	| "title_generation";

export const ChatModelOverrideContexts: ChatModelOverrideContext[] = [
	"advisor",
	"compaction",
	"computer_use",
	"explore",
	"general",
	"title_generation",
];

// From codersdk/chats.go
/**
 * ChatModelOverrideResponse is the response body for one chat model override.
 */
export interface ChatModelOverrideResponse {
	readonly context: ChatModelOverrideContext;
	readonly model_config_id: string;
	readonly reasoning_effort?: string;
}

// From codersdk/chats.go
/**
 * ChatModelOverridesResponse is the response body for organization chat model overrides.
 */
export interface ChatModelOverridesResponse {
	readonly overrides: readonly ChatModelOverrideResponse[];
}

// From codersdk/chats.go
/**
 * ChatModelProviderDescriptor is the redacted view of an AI provider carried
 * on the org model collection response. It carries only the capability
 * metadata the Models UI needs; key material, base URLs, and headers are
 * never exposed. The fields mirror the provider descriptors returned by the
 * organization-scoped chat models collection.
 */
export interface ChatModelProviderDescriptor {
	readonly id: string;
	readonly type: string;
	readonly display_name: string;
	readonly icon: string;
	readonly enabled: boolean;
	readonly has_api_key: boolean;
	readonly has_user_api_key: boolean;
	readonly has_effective_api_key: boolean;
	readonly allow_user_api_key: boolean;
	readonly available: boolean;
	readonly unavailable_reason?: ChatModelProviderUnavailableReason;
}

// From codersdk/chats.go
/**
 * ChatModelProviderOptions contains typed provider-specific options.
 *
 * Note: Azure models use the `openai` options shape.
 * Note: Bedrock models use the `anthropic` options shape.
 */
export interface ChatModelProviderOptions {
	readonly openai?: ChatModelOpenAIProviderOptions;
	readonly anthropic?: ChatModelAnthropicProviderOptions;
	readonly google?: ChatModelGoogleProviderOptions;
	readonly openaicompat?: ChatModelOpenAICompatProviderOptions;
	readonly openrouter?: ChatModelOpenRouterProviderOptions;
	readonly vercel?: ChatModelVercelProviderOptions;
}

// From codersdk/chats.go
export type ChatModelProviderUnavailableReason =
	| "fetch_failed"
	| "missing_api_key"
	| "user_api_key_required";

export const ChatModelProviderUnavailableReasons: ChatModelProviderUnavailableReason[] =
	["fetch_failed", "missing_api_key", "user_api_key_required"];

// From codersdk/chats.go
/**
 * ChatModelReasoningEffortConfig configures per-model reasoning effort
 * bounds. When configured, Default and Max must both be provided before
 * storing.
 */
export interface ChatModelReasoningEffortConfig {
	readonly default?: string;
	readonly max?: string;
}

// From codersdk/chats.go
/**
 * Reasoning effort levels, ordered low to high for clamping and comparison.
 */
export const ChatModelReasoningEffortHigh = "high";

// From codersdk/chats.go
/**
 * Reasoning effort levels, ordered low to high for clamping and comparison.
 */
export const ChatModelReasoningEffortLow = "low";

// From codersdk/chats.go
/**
 * Reasoning effort levels, ordered low to high for clamping and comparison.
 */
export const ChatModelReasoningEffortMax = "max";

// From codersdk/chats.go
/**
 * Reasoning effort levels, ordered low to high for clamping and comparison.
 */
export const ChatModelReasoningEffortMedium = "medium";

// From codersdk/chats.go
/**
 * Reasoning effort levels, ordered low to high for clamping and comparison.
 */
export const ChatModelReasoningEffortMinimal = "minimal";

// From codersdk/chats.go
/**
 * Reasoning effort levels, ordered low to high for clamping and comparison.
 */
export const ChatModelReasoningEffortNone = "none";

// From codersdk/chats.go
/**
 * Reasoning effort levels, ordered low to high for clamping and comparison.
 */
export const ChatModelReasoningEffortXHigh = "xhigh";

// From codersdk/chats.go
/**
 * ChatModelReasoningOptions configures reasoning behavior for model
 * providers that support it.
 */
export interface ChatModelReasoningOptions {
	readonly enabled?: boolean;
	readonly exclude?: boolean;
	readonly max_tokens?: number;
}

// From codersdk/chats.go
/**
 * ChatModelVercelGatewayProviderOptions configures Vercel routing behavior.
 */
export interface ChatModelVercelGatewayProviderOptions {
	readonly order?: readonly string[];
	readonly models?: readonly string[];
}

// From codersdk/chats.go
/**
 * ChatModelVercelProviderOptions configures Vercel provider behavior.
 */
export interface ChatModelVercelProviderOptions {
	readonly reasoning?: ChatModelReasoningOptions;
	readonly providerOptions?: ChatModelVercelGatewayProviderOptions;
	readonly user?: string;
	readonly logit_bias?: Record<string, number>;
	readonly logprobs?: boolean;
	readonly top_logprobs?: number;
	readonly parallel_tool_calls?: boolean;
	// empty interface{} type, falling back to unknown
	readonly extra_body?: Record<string, unknown>;
}

// From codersdk/chats.go
/**
 * ChatPersonalModelOverride is a resolved user personal model override.
 */
export interface ChatPersonalModelOverride {
	readonly context: ChatPersonalModelOverrideContext;
	readonly mode: ChatPersonalModelOverrideMode;
	readonly model_config_id: string;
	readonly reasoning_effort?: string;
	readonly is_set: boolean;
}

// From codersdk/chats.go
export type ChatPersonalModelOverrideContext = "explore" | "general" | "root";

export const ChatPersonalModelOverrideContexts: ChatPersonalModelOverrideContext[] =
	["explore", "general", "root"];

// From codersdk/chats.go
/**
 * ChatPersonalModelOverrideDeploymentDefaults describes the deployment-level
 * defaults used when a personal override selects deployment_default.
 */
export interface ChatPersonalModelOverrideDeploymentDefaults {
	readonly general: ChatModelOverrideResponse;
	readonly explore: ChatModelOverrideResponse;
}

// From codersdk/chats.go
export type ChatPersonalModelOverrideMode =
	| "chat_default"
	| "deployment_default"
	| "model";

export const ChatPersonalModelOverrideModes: ChatPersonalModelOverrideMode[] = [
	"chat_default",
	"deployment_default",
	"model",
];

// From codersdk/chats.go
/**
 * ChatPersonalModelOverridesAdminSettings describes whether users may manage
 * personal model override settings.
 */
export interface ChatPersonalModelOverridesAdminSettings {
	readonly allow_users: boolean;
}

// From codersdk/chats.go
export type ChatPlanMode = "plan";

// From codersdk/chats.go
/**
 * ChatPlanModeInstructionsResponse is the response body for the
 * plan mode instructions configuration endpoint.
 */
export interface ChatPlanModeInstructionsResponse {
	readonly plan_mode_instructions: string;
}

export const ChatPlanModes: ChatPlanMode[] = ["plan"];

// From codersdk/chats.go
/**
 * ChatProject groups related chats in an organization.
 */
export interface ChatProject {
	readonly id: string;
	readonly organization_id: string;
	readonly owner_id: string;
	/**
	 * Name is a display label and is not unique; ID identifies the project.
	 */
	readonly name: string;
	readonly description: string;
	/**
	 * Icon is a URL, typically an emoji image under /emojis, or empty for the
	 * default folder glyph.
	 */
	readonly icon: string;
	readonly created_at: string;
	readonly updated_at: string;
	readonly permissions: ChatProjectPermissions;
}

// From codersdk/chats.go
/**
 * ChatProjectACL lists who a chat project is shared with. The Everyone
 * group's ID is the organization ID.
 */
export interface ChatProjectACL {
	readonly users: readonly ChatProjectUser[];
	readonly groups: readonly ChatProjectGroup[];
}

// From codersdk/chats.go
export interface ChatProjectGroup extends Group {
	readonly role: ChatProjectRole;
}

// From codersdk/chats.go
/**
 * ChatProjectInstructions are instructions added to the system prompt of
 * every chat in a project, except chats whose owner can no longer use the
 * project. A project without instructions has an empty Instructions
 * string and nil UpdatedBy and UpdatedAt.
 */
export interface ChatProjectInstructions {
	readonly project_id: string;
	readonly instructions: string;
	/**
	 * UpdatedBy is the user who last changed the instructions. It is nil
	 * when the instructions are unset or the user was deleted.
	 */
	readonly updated_by: MinimalUser | null;
	readonly updated_at: string | null;
}

// From codersdk/chats.go
/**
 * ChatProjectMemory is a durable memory shared by chats in a project.
 */
export interface ChatProjectMemory {
	readonly id: string;
	readonly project_id: string;
	readonly organization_id: string;
	readonly name: string;
	readonly description: string;
	readonly body: string;
	readonly created_by: string;
	readonly created_by_username: string;
	readonly created_at: string;
}

// From codersdk/chats.go
/**
 * ChatProjectPermissions are the actions the caller may take on a project.
 */
export interface ChatProjectPermissions {
	readonly update: boolean;
	readonly delete: boolean;
	readonly share: boolean;
}

// From codersdk/chats.go
export type ChatProjectRole = "admin" | "" | "use";

export const ChatProjectRoles: ChatProjectRole[] = ["admin", "", "use"];

// From codersdk/chats.go
export interface ChatProjectUser extends MinimalUser {
	readonly role: ChatProjectRole;
}

// From codersdk/chats.go
/**
 * ChatPrompt is a single user-authored prompt in a chat, returned by
 * GET /api/v2/chats/{chat}/prompts. The text field contains
 * the concatenated text payload of the underlying chat message; non-text
 * parts (tool calls, files, attachments) are omitted by the server.
 */
export interface ChatPrompt {
	readonly id: number;
	readonly text: string;
}

// From codersdk/chats.go
/**
 * ChatPromptsOptions are optional query parameters for GetChatPrompts.
 */
export interface ChatPromptsOptions {
	/**
	 * Limit caps the number of prompts returned. The server enforces a
	 * minimum of 1 and a maximum of 2000; passing 0 (or negative)
	 * applies the server-side default of 500.
	 */
	readonly Limit: number;
}

// From codersdk/chats.go
/**
 * ChatPromptsResponse is the payload of
 * GET /api/v2/chats/{chat}/prompts. Prompts are returned
 * newest first so the client can index directly into the slice for
 * up/down arrow history cycling.
 */
export interface ChatPromptsResponse {
	readonly prompts: readonly ChatPrompt[];
}

// From codersdk/chats.go
/**
 * ChatProviderConfig is an admin-managed provider configuration.
 */
export interface ChatProviderConfig {
	readonly id: string;
	readonly provider: string;
	readonly display_name: string;
	readonly icon: string;
	readonly enabled: boolean;
	readonly has_api_key: boolean;
	readonly central_api_key_enabled: boolean;
	readonly allow_user_api_key: boolean;
	readonly allow_central_api_key_fallback: boolean;
	readonly base_url?: string;
	readonly source: ChatProviderConfigSource;
	readonly created_at?: string;
	readonly updated_at?: string;
}

// From codersdk/chats.go
export type ChatProviderConfigSource = "database" | "env_preset" | "supported";

export const ChatProviderConfigSources: ChatProviderConfigSource[] = [
	"database",
	"env_preset",
	"supported",
];

// From codersdk/chats.go
/**
 * ChatQueuedMessage represents a queued message waiting to be processed.
 */
export interface ChatQueuedMessage {
	readonly id: number;
	readonly chat_id: string;
	readonly model_config_id?: string;
	readonly content: readonly ChatMessagePart[];
	readonly created_at: string;
	readonly reasoning_effort?: string;
	/**
	 * EditingSince is set while the owner edits the message. A message
	 * under edit and every message behind it wait until the edit ends; a
	 * turn that ends at a message under edit pauses the chat.
	 */
	readonly editing_since?: string;
}

// From codersdk/chats.go
export interface ChatReasoningPart {
	readonly type: "reasoning";
	readonly text: string;
	/**
	 * CreatedAt is the timestamp this part carries. The semantics
	 * depend on the part type: for tool-call and tool-result parts
	 * it is the time the call was emitted or the result was
	 * produced (tool duration is the result's created_at minus the
	 * call's created_at); for reasoning parts it is the time
	 * reasoning started streaming.
	 */
	readonly created_at?: string;
	/**
	 * CompletedAt is the time a reasoning part finished streaming,
	 * so reasoning duration can be computed as completed_at minus
	 * created_at. For interrupted reasoning, this is the
	 * interruption time. Absent when reasoning timestamp data was
	 * not recorded (e.g. messages persisted before this feature
	 * was added).
	 */
	readonly completed_at?: string;
}

// From codersdk/chats.go
/**
 * ChatRetentionDaysResponse contains the current chat retention setting.
 */
export interface ChatRetentionDaysResponse {
	readonly retention_days: number;
}

// From codersdk/chats.go
export type ChatRole = "" | "read";

export const ChatRoles: ChatRole[] = ["", "read"];

// From codersdk/chats.go
export interface ChatSkillInvocationPart {
	readonly type: "skill-invocation";
	/**
	 * SkillName is the kebab-case name of the skill.
	 */
	readonly skill_name: string;
	/**
	 * SkillSource is where the invoked skill of a skill-invocation
	 * part is stored.
	 */
	readonly skill_source: ChatSkillSource;
}

// From codersdk/chats.go
export interface ChatSkillPart {
	readonly type: "skill";
	/**
	 * SkillName is the kebab-case name of the skill.
	 */
	readonly skill_name: string;
	/**
	 * SkillDescription is the short description from the skill's
	 * SKILL.md frontmatter.
	 */
	readonly skill_description?: string;
}

// From codersdk/chats.go
export type ChatSkillSource = "org" | "personal" | "project" | "workspace";

export const ChatSkillSources: ChatSkillSource[] = [
	"org",
	"personal",
	"project",
	"workspace",
];

// From codersdk/chats.go
export interface ChatSourcePart {
	readonly type: "source";
	readonly url: string;
	readonly tool_call_id?: string;
	readonly source_id?: string;
	readonly title?: string;
}

// From codersdk/chats.go
export type ChatStatus =
	| "error"
	| "interrupting"
	| "paused"
	| "requires_action"
	| "running"
	| "waiting";

export const ChatStatuses: ChatStatus[] = [
	"error",
	"interrupting",
	"paused",
	"requires_action",
	"running",
	"waiting",
];

// From codersdk/chats.go
/**
 * ChatStreamActionRequired is the payload of an action_required stream event.
 */
export interface ChatStreamActionRequired {
	readonly tool_calls: readonly ChatStreamToolCall[];
}

// From codersdk/chats.go
/**
 * ChatStreamEvent represents a real-time update for chat streaming.
 */
export interface ChatStreamEvent {
	readonly type: ChatStreamEventType;
	readonly chat_id: string;
	readonly message?: ChatMessage;
	readonly message_part?: ChatStreamMessagePart;
	readonly status?: ChatStreamStatus;
	readonly error?: ChatError;
	readonly retry?: ChatStreamRetry;
	readonly queued_messages?: readonly ChatQueuedMessage[];
	readonly action_required?: ChatStreamActionRequired;
}

// From codersdk/chats.go
export type ChatStreamEventType =
	| "action_required"
	| "error"
	| "history_reset"
	| "message"
	| "message_part"
	| "preview_reset"
	| "queue_update"
	| "retry"
	| "status";

export const ChatStreamEventTypes: ChatStreamEventType[] = [
	"action_required",
	"error",
	"history_reset",
	"message",
	"message_part",
	"preview_reset",
	"queue_update",
	"retry",
	"status",
];

// From codersdk/chats.go
/**
 * ChatStreamMessagePart is a streamed message part update.
 */
export interface ChatStreamMessagePart {
	readonly role?: ChatMessageRole;
	readonly part: ChatMessagePart;
	readonly history_version?: number;
	readonly generation_attempt?: number;
	readonly seq?: number;
}

// From codersdk/chats.go
/**
 * ChatStreamRetry represents an auto-retry status event in the stream.
 * Published when the server automatically retries a failed LLM call.
 */
export interface ChatStreamRetry {
	/**
	 * Attempt is the 1-indexed retry attempt number.
	 */
	readonly attempt: number;
	/**
	 * DelayMs is the backoff delay in milliseconds before the retry.
	 */
	readonly delay_ms: number;
	/**
	 * Error is the normalized error message from the failed attempt.
	 */
	readonly error: string;
	/**
	 * Kind classifies the retry reason for consistent client rendering.
	 */
	readonly kind?: ChatErrorKind;
	/**
	 * Provider identifies the upstream model provider when known.
	 */
	readonly provider?: string;
	/**
	 * StatusCode is the best-effort upstream HTTP status code.
	 */
	readonly status_code?: number;
	/**
	 * RetryingAt is the timestamp when the retry will be attempted.
	 */
	readonly retrying_at: string;
}

// From codersdk/chats.go
/**
 * ChatStreamStatus represents an updated chat status.
 */
export interface ChatStreamStatus {
	readonly status: ChatStatus;
}

// From codersdk/chats.go
/**
 * ChatStreamToolCall describes a pending dynamic tool call that the client
 * must execute.
 */
export interface ChatStreamToolCall {
	readonly tool_call_id: string;
	readonly tool_name: string;
	readonly args: string;
}

// From codersdk/chats.go
/**
 * ChatSystemPromptResponse is the response body for the chat system prompt
 * configuration endpoint.
 */
export interface ChatSystemPromptResponse {
	readonly system_prompt: string;
	readonly include_default_system_prompt: boolean;
	readonly default_system_prompt: string;
}

// From codersdk/chats.go
export interface ChatTextPart {
	readonly type: "text";
	readonly text: string;
	/**
	 * Narration marks assistant text that the model labeled as commentary
	 * on its work rather than its answer. Only providers that label their
	 * output set it.
	 */
	readonly narration?: boolean;
}

// From codersdk/chats.go
export type ChatTitleSource = "fallback" | "generated" | "user";

export const ChatTitleSources: ChatTitleSource[] = [
	"fallback",
	"generated",
	"user",
];

// From codersdk/chats.go
export interface ChatToolCallPart {
	readonly type: "tool-call";
	readonly tool_call_id?: string;
	readonly tool_name?: string;
	readonly mcp_server_config_id?: string;
	readonly args?: Record<string, string>;
	readonly args_delta?: string;
	/**
	 * ParsedCommands holds parsed programs from an execute tool call's
	 * shell command, one entry per simple command in source order. Each
	 * entry is [program] or [program, arg] where arg is the first non-flag
	 * positional argument. Program names are normalized to their base
	 * name (e.g. /usr/bin/go becomes go). Only populated when ToolName
	 * is "execute" and the command parses successfully; nil otherwise.
	 */
	readonly parsed_commands?: readonly string[][];
	/**
	 * ProviderExecuted indicates the tool call was executed by
	 * the provider (e.g. Anthropic computer use).
	 */
	readonly provider_executed?: boolean;
	/**
	 * HookRewritten indicates that a lifecycle hook replaced model-proposed tool input.
	 */
	readonly hook_rewritten?: boolean;
	/**
	 * CreatedAt is the timestamp this part carries. The semantics
	 * depend on the part type: for tool-call and tool-result parts
	 * it is the time the call was emitted or the result was
	 * produced (tool duration is the result's created_at minus the
	 * call's created_at); for reasoning parts it is the time
	 * reasoning started streaming.
	 */
	readonly created_at?: string;
}

// From codersdk/chats.go
export interface ChatToolResultPart {
	readonly type: "tool-result";
	readonly tool_call_id?: string;
	readonly tool_name?: string;
	readonly mcp_server_config_id?: string;
	readonly result?: unknown;
	readonly result_delta?: string;
	readonly reasoning_delta?: string;
	readonly result_reset?: boolean;
	readonly is_error?: boolean;
	readonly is_media?: boolean;
	/**
	 * ProviderExecuted indicates the tool call was executed by
	 * the provider (e.g. Anthropic computer use).
	 */
	readonly provider_executed?: boolean;
	/**
	 * CreatedAt is the timestamp this part carries. The semantics
	 * depend on the part type: for tool-call and tool-result parts
	 * it is the time the call was emitted or the result was
	 * produced (tool duration is the result's created_at minus the
	 * call's created_at); for reasoning parts it is the time
	 * reasoning started streaming.
	 */
	readonly created_at?: string;
}

// From codersdk/chats.go
/**
 * ChatUnsupportedProvider is a configured provider the Agents harness cannot
 * use.
 */
export interface ChatUnsupportedProvider {
	/**
	 * Provider is the provider type, e.g. "copilot".
	 */
	readonly provider: string;
	readonly display_name: string;
}

// From codersdk/chats.go
export interface ChatUser extends MinimalUser {
	readonly role: ChatRole;
}

// From codersdk/chats.go
/**
 * ChatWatchEvent represents an event from the global chat watch stream.
 * It delivers lifecycle events (created, status change, summary change,
 * title change) for all of the authenticated user's chats. When Kind is
 * ActionRequired, ToolCalls contains the pending dynamic tool
 * invocations the client must execute and submit back.
 */
export interface ChatWatchEvent {
	readonly kind: ChatWatchEventKind;
	readonly chat: Chat;
	readonly tool_calls?: readonly ChatStreamToolCall[];
	/**
	 * ChangedDiffStatus is set when Kind is
	 * ChatWatchEventKindDiffStatusChange. It identifies the ref that
	 * changed.
	 */
	readonly changed_diff_status?: ChangedDiffStatus;
}

// From codersdk/chats.go
export type ChatWatchEventKind =
	| "action_required"
	| "chat_summary_change"
	| "context_dirty"
	| "created"
	| "deleted"
	| "diff_status_change"
	| "project_change"
	| "status_change"
	| "summary_change"
	| "title_change";

export const ChatWatchEventKinds: ChatWatchEventKind[] = [
	"action_required",
	"chat_summary_change",
	"context_dirty",
	"created",
	"deleted",
	"diff_status_change",
	"project_change",
	"status_change",
	"summary_change",
	"title_change",
];

// From codersdk/chats.go
export interface ChatWorkspaceFileReferencePart {
	readonly type: "workspace-file-reference";
	/**
	 * WorkspaceFilePath is the absolute path of a workspace upload.
	 * The bytes live on the workspace filesystem; only metadata is
	 * persisted on the message.
	 */
	readonly workspace_file_path: string;
	/**
	 * WorkspaceFileName is the sanitized basename of a workspace upload.
	 */
	readonly workspace_file_name: string;
	/**
	 * WorkspaceFileSize is the byte size of a workspace upload.
	 */
	readonly workspace_file_size: number;
	/**
	 * WorkspaceFileWorkspaceID identifies the workspace whose
	 * filesystem holds the uploaded bytes. References are only
	 * readable while the chat stays bound to that workspace.
	 */
	readonly workspace_file_workspace_id: string;
	/**
	 * WorkspaceFileMediaType is the best-effort declared MIME type.
	 */
	readonly workspace_file_media_type?: string;
}

// From codersdk/chats.go
/**
 * ChatWorkspaceTTLResponse is the response for getting the chat
 * workspace TTL setting.
 */
export interface ChatWorkspaceTTLResponse {
	/**
	 * WorkspaceTTLMillis is the workspace TTL in milliseconds.
	 * Zero means disabled; the template's own autostop setting applies.
	 */
	readonly workspace_ttl_ms: number;
}

// From codersdk/deployment.go
export interface ClusterConfig {
	readonly host: string;
}

// From codersdk/client.go
/**
 * CoderDesktopTelemetryHeader contains a JSON-encoded representation of Desktop telemetry
 * fields, including device ID, OS, and Desktop version.
 */
export const CoderDesktopTelemetryHeader = "Coder-Desktop-Telemetry";

// From codersdk/disconnect.go
export type ConnectionDirection =
	| "agent_to_client"
	| "client_to_server"
	| "server_to_agent";

export const ConnectionDirections: ConnectionDirection[] = [
	"agent_to_client",
	"client_to_server",
	"server_to_agent",
];

// From codersdk/insights.go
/**
 * ConnectionLatency shows the latency for a connection.
 */
export interface ConnectionLatency {
	readonly p50: number;
	readonly p95: number;
}

// From codersdk/connectionlog.go
export interface ConnectionLog {
	readonly id: string;
	readonly connect_time: string;
	readonly organization: MinimalOrganization;
	readonly workspace_owner_id: string;
	readonly workspace_owner_username: string;
	readonly workspace_id: string;
	readonly workspace_name: string;
	readonly agent_name: string;
	readonly ip?: string;
	/**
	 * @deprecated Use ConnectionMethod and AppName.
	 */
	readonly type: string;
	readonly connection_method: ConnectionLogMethod;
	/**
	 * AppName is the name of the connecting application, when known. The
	 * client reports it, so it is a normalized label and not a guarantee.
	 * Workspace app slugs and forwarded ports are reported in WebInfo.
	 */
	readonly app_name?: string;
	/**
	 * AppDisplayName is the registry display name for AppName, or AppName
	 * itself when the app is unregistered.
	 */
	readonly app_display_name?: string;
	/**
	 * WebInfo is set for connections that coderd records.
	 */
	readonly web_info?: ConnectionLogWebInfo;
	/**
	 * SSHInfo is set for connections that agents report.
	 */
	readonly ssh_info?: ConnectionLogSSHInfo;
}

// From codersdk/dbenums_gen.go
export type ConnectionLogMethod =
	| "port_forwarding"
	| "reconnecting_pty"
	| "ssh"
	| "tunnel"
	| "workspace_app";

export const ConnectionLogMethods: ConnectionLogMethod[] = [
	"port_forwarding",
	"reconnecting_pty",
	"ssh",
	"tunnel",
	"workspace_app",
];

// From codersdk/connectionlog.go
export interface ConnectionLogResponse {
	readonly connection_logs: readonly ConnectionLog[];
	readonly count: number;
	readonly count_cap: number;
}

// From codersdk/connectionlog.go
export interface ConnectionLogSSHInfo {
	readonly connection_id: string;
	/**
	 * ClientSessionID is set by the client and isn't guaranteed to be unique.
	 */
	readonly client_session_id?: string;
	/**
	 * DisconnectTime is omitted if a disconnect event with the same connection ID
	 * has not yet been seen.
	 */
	readonly disconnect_time?: string;
	/**
	 * DisconnectReason is omitted if a disconnect event with the same connection ID
	 * has not yet been seen.
	 */
	readonly disconnect_reason?: string;
	/**
	 * ExitCode is the exit code of the SSH session. It is omitted if a
	 * disconnect event with the same connection ID has not yet been seen.
	 */
	readonly exit_code?: number;
}

// From codersdk/connectionlog.go
export type ConnectionLogStatus = "completed" | "ongoing";

export const ConnectionLogStatuses: ConnectionLogStatus[] = [
	"completed",
	"ongoing",
];

// From codersdk/connectionlog.go
export interface ConnectionLogWebInfo {
	readonly user_agent: string;
	/**
	 * User is omitted if the connection event was unauthenticated.
	 */
	readonly user: User | null;
	readonly slug_or_port: string;
	/**
	 * StatusCode is the HTTP status code or tunnel authorization outcome.
	 */
	readonly status_code: number;
}

// From codersdk/connectionlog.go
export interface ConnectionLogsRequest extends Pagination {
	readonly q?: string;
}

// From codersdk/disconnect.go
export type ConnectionMethod = "derp" | "direct" | "";

export const ConnectionMethods: ConnectionMethod[] = ["derp", "direct", ""];

// From codersdk/connectionlog.go
export type ConnectionType =
	| "jetbrains"
	| "port_forwarding"
	| "reconnecting_pty"
	| "ssh"
	| "tunnel"
	| "vscode"
	| "workspace_app";

export const ConnectionTypes: ConnectionType[] = [
	"jetbrains",
	"port_forwarding",
	"reconnecting_pty",
	"ssh",
	"tunnel",
	"vscode",
	"workspace_app",
];

// From codersdk/files.go
export const ContentTypeTar = "application/x-tar";

// From codersdk/files.go
export const ContentTypeZip = "application/zip";

// From codersdk/users.go
export interface ConvertLoginRequest {
	/**
	 * ToType is the login type to convert to.
	 */
	readonly to_type: LoginType;
	readonly password: string;
}

// From codersdk/aigatewaykeys.go
/**
 * CreateAIGatewayKeyRequest requests a new AI Gateway key.
 */
export interface CreateAIGatewayKeyRequest {
	readonly name: string;
}

// From codersdk/aigatewaykeys.go
/**
 * CreateAIGatewayKeyResponse returns all key information.
 * Key value is only returned here and cannot be recovered afterwards.
 */
export interface CreateAIGatewayKeyResponse {
	readonly id: string;
	readonly name: string;
	readonly key: string;
	readonly key_prefix: string;
	readonly created_at: string;
}

// From codersdk/aiproviders.go
/**
 * CreateAIProviderRequest is the payload for creating a new AI
 * provider. Name and Type are required. APIKeys carries the plaintext
 * keys for OpenAI/Anthropic providers; Bedrock and Copilot providers
 * must omit APIKeys (Bedrock authenticates via Settings, Copilot via
 * request-time GitHub OAuth tokens).
 */
export interface CreateAIProviderRequest {
	readonly type: AIProviderType;
	readonly name: string;
	readonly display_name?: string;
	readonly icon?: string;
	readonly enabled: boolean;
	readonly base_url: string;
	readonly api_keys?: readonly string[];
	readonly settings?: AIProviderSettings;
}

// From codersdk/chats.go
/**
 * CreateChatMessageRequest is the request to add a message to a chat.
 */
export interface CreateChatMessageRequest {
	readonly content: readonly ChatInputPart[];
	readonly model_config_id?: string;
	readonly mcp_server_ids?: string[];
	/**
	 * InlineMCPServers replaces the inline MCP servers.
	 * nil: no change, empty: remove all.
	 */
	readonly inline_mcp_servers?: InlineMCPServerRequest[];
	readonly busy_behavior?: ChatBusyBehavior;
	/**
	 * PlanMode switches the chat's persistent plan mode.
	 * nil: no change, ptr to "plan": enable, ptr to "": clear.
	 */
	readonly plan_mode?: ChatPlanMode;
	readonly reasoning_effort?: string;
}

// From codersdk/chats.go
/**
 * CreateChatMessageResponse is the response from adding a message to a chat.
 */
export interface CreateChatMessageResponse {
	readonly message?: ChatMessage;
	/**
	 * Messages contains all user-visible messages inserted by the send, in
	 * insertion order. A queued send on an errored chat may promote the
	 * previous queue head, so clients must upsert the full batch.
	 */
	readonly messages?: readonly ChatMessage[];
	readonly queued_message?: ChatQueuedMessage;
	readonly queued: boolean;
	readonly warnings?: readonly string[];
}

// From codersdk/chats.go
/**
 * CreateChatModelRequest is the request body for an organization-scoped
 * ChatModel. AIProviderID, Model, and a positive ContextLimit are required.
 * Enabled defaults to true. IsDefault defaults to false when the organization
 * already has a default model. The first model created in an organization is
 * automatically promoted to default. CompressionThreshold defaults to 70, or
 * 30 when ContextLimit is at least 500k tokens. An omitted ModelConfig uses the
 * provider defaults.
 */
export interface CreateChatModelRequest {
	readonly ai_provider_id?: string;
	readonly model: string;
	readonly display_name?: string;
	readonly enabled?: boolean;
	readonly is_default?: boolean;
	readonly context_limit?: number;
	readonly compression_threshold?: number;
	readonly model_config?: ChatModelCallConfig;
}

// From codersdk/chats.go
export interface CreateChatProjectMemoryRequest {
	readonly name: string;
	readonly description: string;
	readonly body: string;
}

// From codersdk/chats.go
/**
 * CreateChatProjectRequest creates a chat project in the organization named
 * by the route.
 */
export interface CreateChatProjectRequest {
	readonly name: string;
	readonly description: string;
	readonly icon?: string;
}

// From codersdk/chats.go
/**
 * CreateChatRequest is the request to create a new chat.
 */
export interface CreateChatRequest {
	readonly organization_id: string;
	/**
	 * OwnerID makes another user the chat owner. It defaults to the
	 * caller. The chat runs with the owner's credentials, so setting it
	 * requires site-wide authority over that user.
	 */
	readonly owner_id?: string;
	/**
	 * Content is the initial user message. It is optional: when
	 * empty, the chat is created idle with no initial user message
	 * and generation starts with the first message POSTed to
	 * /chats/{chat}/messages.
	 */
	readonly content: readonly ChatInputPart[];
	/**
	 * Title, when set, is stored as the user title and automatic title
	 * generation does not run. It is trimmed and must then be non-empty
	 * and at most 200 Unicode code points (MaxChatTitleRunes), else the
	 * request fails with 400. When omitted, the title is derived from the
	 * first prompt and may later be replaced by a generated title.
	 */
	readonly title?: string;
	readonly system_prompt?: string;
	readonly workspace_id?: string;
	readonly project_id?: string;
	readonly model_config_id?: string;
	readonly reasoning_effort?: string;
	readonly mcp_server_ids?: readonly string[];
	readonly labels?: Record<string, string>;
	/**
	 * UnsafeDynamicTools declares client-executed tools that the
	 * LLM can invoke. This API is highly experimental and highly
	 * subject to change.
	 */
	readonly unsafe_dynamic_tools?: readonly DynamicTool[];
	/**
	 * InlineMCPServers declares MCP servers by value on this chat, next
	 * to the org-configured servers selected by MCPServerIDs. Experimental.
	 */
	readonly inline_mcp_servers?: readonly InlineMCPServerRequest[];
	readonly plan_mode?: ChatPlanMode;
	readonly client_type?: ChatClientType;
}

// From codersdk/users.go
/**
 * CreateFirstUserOnboardingInfo contains optional newsletter preference
 * data collected during first user setup.
 */
export interface CreateFirstUserOnboardingInfo {
	readonly newsletter_marketing: boolean;
	readonly newsletter_releases: boolean;
}

// From codersdk/users.go
export interface CreateFirstUserRequest {
	readonly email: string;
	readonly username: string;
	readonly name: string;
	readonly password: string;
	readonly trial: boolean;
	readonly trial_info: CreateFirstUserTrialInfo;
	readonly onboarding_info?: CreateFirstUserOnboardingInfo;
}

// From codersdk/users.go
/**
 * CreateFirstUserResponse contains IDs for newly created user info.
 */
export interface CreateFirstUserResponse {
	readonly user_id: string;
	readonly organization_id: string;
}

// From codersdk/users.go
export interface CreateFirstUserTrialInfo {
	readonly first_name: string;
	readonly last_name: string;
	readonly phone_number: string;
	readonly job_title: string;
	readonly company_name: string;
	readonly country: string;
	readonly developers: string;
}

// From codersdk/groups.go
export interface CreateGroupRequest {
	readonly name: string;
	readonly display_name: string;
	readonly avatar_url: string;
	readonly quota_allowance: number;
}

// From codersdk/mcp.go
/**
 * CreateMCPServerConfigRequest is the request to create a new MCP server config.
 */
export interface CreateMCPServerConfigRequest {
	readonly display_name: string;
	readonly slug: string;
	readonly description: string;
	readonly icon_url: string;
	readonly transport: string;
	readonly url: string;
	readonly auth_type: string;
	readonly oauth2_client_id?: string;
	readonly oauth2_client_secret?: string;
	readonly oauth2_auth_url?: string;
	readonly oauth2_token_url?: string;
	/**
	 * OAuth2RevocationURL is the provider's RFC 7009 revocation
	 * endpoint; auto-populated by OAuth2 discovery when omitted.
	 */
	readonly oauth2_revocation_url?: string;
	/**
	 * OAuth2Resource is the RFC 8707 resource indicator sent on OAuth2
	 * requests when set. Auto-discovery fills it if empty.
	 */
	readonly oauth2_resource?: string;
	readonly oauth2_scopes?: string;
	readonly api_key_header?: string;
	readonly api_key_value?: string;
	readonly custom_headers?: Record<string, string>;
	readonly tool_allow_list?: readonly string[];
	readonly tool_deny_list?: readonly string[];
	readonly availability: string;
	readonly enabled: boolean;
	readonly model_intent: boolean;
	readonly allow_in_plan_mode: boolean;
	/**
	 * ForwardCoderHeaders, when true, forwards Coder identity
	 * headers on every outgoing MCP request. See MCPServerConfig.
	 */
	readonly forward_coder_headers: boolean;
	/**
	 * SigningSecret signs forwarded identity headers and request bodies.
	 * Configure the same secret on the MCP server. It is never returned.
	 */
	readonly signing_secret?: string;
}

// From codersdk/organizations.go
export interface CreateOrganizationRequest {
	readonly name: string;
	/**
	 * DisplayName will default to the same value as `Name` if not provided.
	 */
	readonly display_name?: string;
	readonly description?: string;
	readonly icon?: string;
}

// From codersdk/provisionerdaemons.go
export interface CreateProvisionerKeyRequest {
	readonly name: string;
	readonly tags: Record<string, string>;
}

// From codersdk/provisionerdaemons.go
export interface CreateProvisionerKeyResponse {
	readonly key: string;
}

// From codersdk/skills.go
/**
 * CreateSkillRequest is the payload for creating a skill.
 */
export interface CreateSkillRequest {
	/**
	 * Content must be SKILL.md-format Markdown with YAML frontmatter. The
	 * frontmatter must include name, may include description, and must be
	 * followed by a non-empty body.
	 */
	readonly content: string;
	/**
	 * Action is only accepted for project skills and defaults to false.
	 */
	readonly action?: boolean;
	/**
	 * ModelInvocable defaults to false when the content's frontmatter sets
	 * disable-model-invocation: true, and to true otherwise.
	 */
	readonly model_invocable?: boolean;
}

// From codersdk/organizations.go
/**
 * CreateTemplateRequest provides options when creating a template.
 */
export interface CreateTemplateRequest {
	/**
	 * Name is the name of the template.
	 */
	readonly name: string;
	/**
	 * DisplayName is the displayed name of the template.
	 */
	readonly display_name?: string;
	/**
	 * Description is a description of what the template contains. It must be
	 * no longer than 128 Unicode code points.
	 */
	readonly description?: string;
	/**
	 * Icon is a relative path or external URL that specifies
	 * an icon to be displayed in the dashboard.
	 */
	readonly icon?: string;
	/**
	 * VersionID is an in-progress or completed job to use as an initial version
	 * of the template.
	 *
	 * This is required on creation to enable a user-flow of validating a
	 * template works. There is no reason the data-model cannot support empty
	 * templates, but it doesn't make sense for users.
	 */
	readonly template_version_id: string;
	/**
	 * DefaultTTLMillis allows optionally specifying the default TTL
	 * for all workspaces created from this template.
	 */
	readonly default_ttl_ms?: number;
	/**
	 * ActivityBumpMillis allows optionally specifying the activity bump
	 * duration for all workspaces created from this template. Defaults to 1h
	 * but can be set to 0 to disable activity bumping.
	 */
	readonly activity_bump_ms?: number;
	/**
	 * TimeTilAutostopNotifyMillis allows optionally specifying the duration
	 * before the autostop deadline at which a reminder notification is sent for
	 * workspaces created from this template. Defaults to 0 (disabled).
	 */
	readonly time_til_autostop_notify_ms?: number;
	/**
	 * AutostopRequirement allows optionally specifying the autostop requirement
	 * for workspaces created from this template. This is a Premium feature.
	 */
	readonly autostop_requirement?: TemplateAutostopRequirement;
	/**
	 * AutostartRequirement allows optionally specifying the autostart allowed days
	 * for workspaces created from this template. This is a Premium feature.
	 */
	readonly autostart_requirement?: TemplateAutostartRequirement;
	/**
	 * Allow users to cancel in-progress workspace jobs.
	 * *bool as the default value is "true".
	 */
	readonly allow_user_cancel_workspace_jobs: boolean | null;
	/**
	 * AllowUserAutostart allows users to set a schedule for autostarting their
	 * workspace. By default this is true. This can only be disabled with a
	 * Premium license.
	 */
	readonly allow_user_autostart?: boolean;
	/**
	 * AllowUserAutostop allows users to set a custom workspace TTL to use in
	 * place of the template's DefaultTTL field. By default this is true. If
	 * false, the DefaultTTL will always be used. This can only be disabled with a
	 * Premium license.
	 */
	readonly allow_user_autostop?: boolean;
	/**
	 * FailureTTLMillis allows optionally specifying the max lifetime before Coder
	 * stops all resources for failed workspaces created from this template.
	 */
	readonly failure_ttl_ms?: number;
	/**
	 * TimeTilDormantMillis allows optionally specifying the max lifetime before Coder
	 * locks inactive workspaces created from this template.
	 */
	readonly dormant_ttl_ms?: number;
	/**
	 * TimeTilDormantAutoDeleteMillis allows optionally specifying the max lifetime before Coder
	 * permanently deletes dormant workspaces created from this template.
	 */
	readonly delete_ttl_ms?: number;
	/**
	 * DisableEveryoneGroupAccess allows optionally disabling the default
	 * behavior of granting the 'everyone' group access to use the template.
	 * If this is set to true, the template will not be available to all users,
	 * and must be explicitly granted to users or groups in the permissions settings
	 * of the template.
	 */
	readonly disable_everyone_group_access: boolean;
	/**
	 * RequireActiveVersion mandates that workspaces are built with the active
	 * template version.
	 */
	readonly require_active_version: boolean;
	/**
	 * MaxPortShareLevel allows optionally specifying the maximum port share level
	 * for workspaces created from the template.
	 */
	readonly max_port_share_level: WorkspaceAgentPortShareLevel | null;
	/**
	 * UseClassicParameterFlow allows optionally specifying whether
	 * the template should use the classic parameter flow. The default if unset is
	 * true, and is why `*bool` is used here. When dynamic parameters becomes
	 * the default, this will default to false.
	 */
	readonly template_use_classic_parameter_flow?: boolean;
	/**
	 * CORSBehavior allows optionally specifying the CORS behavior for all shared ports.
	 */
	readonly cors_behavior: CORSBehavior | null;
	/**
	 * AgentsAllowed controls whether Coder Agents can create workspaces using
	 * this template. Defaults to true.
	 */
	readonly agents_allowed?: boolean;
	/**
	 * AllowWorkspaceRenames permits users to rename workspaces built from this
	 * template. Renaming can be destructive for templates whose Terraform
	 * references the workspace name, so this defaults to false.
	 */
	readonly allow_workspace_renames?: boolean;
}

// From codersdk/templateversions.go
/**
 * CreateTemplateVersionDryRunRequest defines the request parameters for
 * CreateTemplateVersionDryRun.
 */
export interface CreateTemplateVersionDryRunRequest {
	readonly workspace_name: string;
	readonly rich_parameter_values: readonly WorkspaceBuildParameter[];
	readonly user_variable_values?: readonly VariableValue[];
}

// From codersdk/organizations.go
/**
 * CreateTemplateVersionRequest enables callers to create a new Template Version.
 */
export interface CreateTemplateVersionRequest {
	readonly name?: string;
	readonly message?: string;
	/**
	 * TemplateID optionally associates a version with a template.
	 */
	readonly template_id?: string;
	readonly storage_method: ProvisionerStorageMethod;
	readonly file_id?: string;
	readonly example_id?: string;
	readonly provisioner: ProvisionerType;
	readonly tags: Record<string, string>;
	readonly user_variable_values?: readonly VariableValue[];
}

// From codersdk/audit.go
export interface CreateTestAuditLogRequest {
	readonly action?: AuditAction;
	readonly resource_type?: ResourceType;
	readonly resource_id?: string;
	readonly additional_fields?: Record<string, string>;
	readonly time?: string;
	readonly build_reason?: BuildReason;
	readonly organization_id?: string;
	readonly request_id?: string;
}

// From codersdk/apikey.go
export interface CreateTokenRequest {
	readonly lifetime: number;
	readonly scope?: APIKeyScope; // Deprecated: use Scopes instead.
	readonly scopes?: readonly APIKeyScope[];
	readonly token_name: string;
	readonly allow_list?: readonly APIAllowListTarget[];
}

// From codersdk/licenses.go
/**
 * Defines the input payload for requesting a trial license.
 */
export interface CreateTrialLicenseRequest {
	readonly email: string;
	readonly first_name: string;
	readonly last_name: string;
	readonly phone_number: string;
	readonly job_title: string;
	readonly company_name: string;
	readonly country: string;
	readonly developers: string;
	/**
	 * Source is the premium paywall the request came from, for telemetry. It
	 * is not forwarded to the licensor. Omit it to report "direct".
	 */
	readonly source?: PremiumFunnelSource;
	/**
	 * AttributionID is the ID of the cta_click funnel event that led here, so
	 * that a signup can be joined back to the paywall that produced it.
	 */
	readonly attribution_id?: string;
}

// From codersdk/chats.go
/**
 * CreateUserAIProviderKeyRequest creates or replaces a user's API key
 * for an AI provider.
 */
export interface CreateUserAIProviderKeyRequest {
	readonly api_key: string;
}

// From codersdk/chats.go
/**
 * CreateUserChatProviderKeyRequest creates or replaces a user's API key
 * for a provider.
 */
export interface CreateUserChatProviderKeyRequest {
	readonly api_key: string;
}

// From codersdk/users.go
export interface CreateUserRequest {
	readonly email: string;
	readonly username: string;
	readonly name: string;
	readonly password: string;
	/**
	 * UserLoginType defaults to LoginTypePassword.
	 */
	readonly login_type: LoginType;
	/**
	 * UserStatus defaults to UserStatusDormant. Service accounts default to
	 * UserStatusActive and can't be dormant.
	 */
	readonly user_status: UserStatus | null;
	/**
	 * OrganizationIDs is a list of organization IDs that the user should be a member of.
	 */
	readonly organization_ids: readonly string[];
	/**
	 * Service accounts are admin-managed accounts that cannot login.
	 */
	readonly service_account?: boolean;
	/**
	 * Roles is an optional list of site-level roles to assign at creation.
	 */
	readonly roles?: readonly string[];
}

// From codersdk/usersecrets.go
/**
 * CreateUserSecretRequest is the payload for creating a new user
 * secret. Name and Value are required. An enabled secret must have at
 * least one of EnvName or FilePath non-empty so it has an injection
 * target; to keep a secret without injecting it, set Enabled to false.
 * A deployment may disable file path delivery, which rejects a
 * non-empty FilePath. All other fields are optional and default to
 * empty string. Enabled defaults to true when omitted.
 */
export interface CreateUserSecretRequest {
	readonly name: string;
	readonly value: string;
	readonly description?: string;
	readonly env_name?: string;
	readonly file_path?: string;
	readonly enabled?: boolean;
}

// From codersdk/workspaces.go
/**
 * CreateWorkspaceBuildOnSuccessRequest queues a follow-up build that
 * runs after the parent build succeeds. It currently supports
 * restarting a workspace: the parent build must be a "stop" and this
 * child build a "start". The child build inherits LogLevel and Reason
 * from the parent CreateWorkspaceBuildRequest.
 */
export interface CreateWorkspaceBuildOnSuccessRequest {
	/**
	 * TemplateVersionID pins the child build to a specific template
	 * version. Pinning requires permission to update the template,
	 * since the active version may change before the child build
	 * runs. When empty, the child build uses the template's active
	 * version at the time it runs.
	 */
	readonly template_version_id?: string;
	/**
	 * Transition must be "start". The parent build's transition must
	 * be "stop".
	 */
	readonly transition: WorkspaceTransition;
	/**
	 * RichParameterValues are applied to the child build. Parameters
	 * not listed here fall back to their values from the previous
	 * build, matching normal build behavior.
	 */
	readonly rich_parameter_values?: readonly WorkspaceBuildParameter[];
	/**
	 * TemplateVersionPresetID selects a preset for the child build.
	 * It requires TemplateVersionID to also be set.
	 */
	readonly template_version_preset_id?: string;
}

// From codersdk/workspaces.go
export type CreateWorkspaceBuildReason =
	| "cli"
	| "dashboard"
	| "jetbrains_connection"
	| "ssh_connection"
	| "vscode_connection";

export const CreateWorkspaceBuildReasons: CreateWorkspaceBuildReason[] = [
	"cli",
	"dashboard",
	"jetbrains_connection",
	"ssh_connection",
	"vscode_connection",
];

// From codersdk/workspaces.go
/**
 * CreateWorkspaceBuildRequest provides options to update the latest workspace build.
 */
export interface CreateWorkspaceBuildRequest {
	readonly template_version_id?: string;
	readonly transition: WorkspaceTransition;
	readonly dry_run?: boolean;
	readonly state?: string;
	/**
	 * Orphan may be set for the Destroy transition.
	 */
	readonly orphan?: boolean;
	/**
	 * ParameterValues are optional. It will write params to the 'workspace' scope.
	 * This will overwrite any existing parameters with the same name.
	 * This will not delete old params not included in this list.
	 */
	readonly rich_parameter_values?: readonly WorkspaceBuildParameter[];
	/**
	 * Secrets sets or removes workspace secrets for this build. Previous
	 * secrets that are not listed carry forward unless they were ephemeral or
	 * a listed secret uses their env_name or file_path.
	 */
	readonly secrets?: readonly WorkspaceSecretInput[];
	/**
	 * Log level changes the default logging verbosity of a provider ("info" if empty).
	 */
	readonly log_level?: ProvisionerLogLevel;
	/**
	 * TemplateVersionPresetID is the ID of the template version preset to use for the build.
	 */
	readonly template_version_preset_id?: string;
	/**
	 * Reason sets the reason for the workspace build.
	 */
	readonly reason?: CreateWorkspaceBuildReason;
	/**
	 * OnSuccess queues a follow-up workspace build after this build succeeds.
	 * It currently supports restarting a workspace by starting it after a
	 * successful stop build.
	 */
	readonly on_success?: CreateWorkspaceBuildOnSuccessRequest;
}

// From codersdk/workspaceproxy.go
export interface CreateWorkspaceProxyRequest {
	readonly name: string;
	readonly display_name: string;
	readonly icon: string;
}

// From codersdk/organizations.go
/**
 * CreateWorkspaceRequest provides options for creating a new workspace.
 * Either TemplateID or TemplateVersionID must be specified. They cannot both be present.
 * @Description CreateWorkspaceRequest provides options for creating a new workspace.
 * @Description Only one of TemplateID or TemplateVersionID can be specified, not both.
 * @Description If TemplateID is specified, the active version of the template will be used.
 * @Description Workspace names:
 * @Description - Must start with a letter or number
 * @Description - Can only contain letters, numbers, and hyphens
 * @Description - Cannot contain spaces or special characters
 * @Description - Cannot be named `new` or `create`
 * @Description - Must be unique within your workspaces
 * @Description - Maximum length of 32 characters
 */
export interface CreateWorkspaceRequest {
	/**
	 * TemplateID specifies which template should be used for creating the workspace.
	 */
	readonly template_id?: string;
	/**
	 * TemplateVersionID can be used to specify a specific version of a template for creating the workspace.
	 */
	readonly template_version_id?: string;
	readonly name: string;
	readonly autostart_schedule?: string;
	readonly ttl_ms?: number;
	/**
	 * RichParameterValues allows for additional parameters to be provided
	 * during the initial provision.
	 */
	readonly rich_parameter_values?: readonly WorkspaceBuildParameter[];
	/**
	 * Secrets sets the workspace's initial secrets.
	 */
	readonly secrets?: readonly WorkspaceSecretInput[];
	readonly automatic_updates?: AutomaticUpdates;
	readonly template_version_preset_id?: string;
}

// From codersdk/deployment.go
export interface CryptoKey {
	readonly feature: CryptoKeyFeature;
	readonly secret: string;
	readonly deletes_at: string;
	readonly sequence: number;
	readonly starts_at: string;
}

// From codersdk/deployment.go
export type CryptoKeyFeature =
	| "chat_files_token"
	| "nats_ca"
	| "oidc_convert"
	| "tailnet_resume"
	| "workspace_apps_api_key"
	| "workspace_apps_token";

export const CryptoKeyFeatures: CryptoKeyFeature[] = [
	"chat_files_token",
	"nats_ca",
	"oidc_convert",
	"tailnet_resume",
	"workspace_apps_api_key",
	"workspace_apps_token",
];

// From codersdk/notifications.go
export interface CustomNotificationContent {
	readonly title: string;
	readonly message: string;
}

// From codersdk/notifications.go
export interface CustomNotificationRequest {
	readonly content: CustomNotificationContent | null;
}

// From codersdk/roles.go
/**
 * CustomRoleRequest is used to edit custom roles.
 */
export interface CustomRoleRequest {
	readonly name: string;
	readonly display_name: string;
	readonly site_permissions: readonly Permission[];
	readonly user_permissions: readonly Permission[];
	/**
	 * OrganizationPermissions are specific to the organization the role belongs to.
	 */
	readonly organization_permissions: readonly Permission[];
	/**
	 * OrganizationMemberPermissions are specific to the organization the role belongs to.
	 */
	readonly organization_member_permissions: readonly Permission[];
}

// From codersdk/deployment.go
export interface DAUEntry {
	/**
	 * Date is a string formatted as 2024-01-31.
	 * Timezone and time information is not included.
	 */
	readonly date: string;
	readonly amount: number;
}

// From codersdk/deployment.go
export interface DAURequest {
	readonly TZHourOffset: number;
}

// From codersdk/deployment.go
export interface DAUsResponse {
	readonly entries: readonly DAUEntry[];
	readonly tz_hour_offset: number;
}

// From codersdk/deployment.go
export interface DERP {
	readonly server: DERPServerConfig;
	readonly config: DERPConfig;
}

// From codersdk/deployment.go
export interface DERPConfig {
	readonly block_direct: boolean;
	readonly force_websockets: boolean;
	readonly url: string;
	readonly path: string;
}

// From healthsdk/healthsdk.go
/**
 * DERPHealthReport includes health details of each configured DERP/STUN region.
 */
export interface DERPHealthReport extends BaseReport {
	/**
	 * Healthy is deprecated and left for backward compatibility purposes, use `Severity` instead.
	 */
	readonly healthy: boolean;
	readonly regions: Record<number, DERPRegionReport | null>;
	readonly netcheck?: NetcheckReport;
	readonly netcheck_err?: string;
	readonly netcheck_logs: readonly string[];
}

// From healthsdk/healthsdk.go
/**
 * DERPHealthReport includes health details of a single node in a single region.
 */
export interface DERPNodeReport {
	/**
	 * Healthy is deprecated and left for backward compatibility purposes, use `Severity` instead.
	 */
	readonly healthy: boolean;
	readonly severity: HealthSeverity;
	readonly warnings: readonly HealthMessage[];
	readonly error?: string;
	readonly node: TailDERPNode | null;
	readonly node_info: ServerInfoMessage;
	readonly can_exchange_messages: boolean;
	readonly round_trip_ping: string;
	readonly round_trip_ping_ms: number;
	readonly uses_websocket: boolean;
	readonly client_logs: readonly string[][];
	readonly client_errs: readonly string[][];
	readonly stun: STUNReport;
}

// From codersdk/workspaceagents.go
export interface DERPRegion {
	readonly preferred: boolean;
	readonly latency_ms: number;
}

// From healthsdk/healthsdk.go
/**
 * DERPHealthReport includes health details of each node in a single region.
 */
export interface DERPRegionReport {
	/**
	 * Healthy is deprecated and left for backward compatibility purposes, use `Severity` instead.
	 */
	readonly healthy: boolean;
	readonly severity: HealthSeverity;
	readonly warnings: readonly HealthMessage[];
	readonly error?: string;
	readonly region: TailDERPRegion | null;
	readonly node_reports: readonly DERPNodeReport[];
}

// From codersdk/deployment.go
export interface DERPServerConfig {
	readonly enable: boolean;
	readonly region_id: number;
	readonly region_code: string;
	readonly region_name: string;
	readonly stun_addresses: string;
	readonly relay_url: string;
}

// From codersdk/deployment.go
export interface DangerousConfig {
	readonly allow_path_app_sharing: boolean;
	readonly allow_path_app_site_owner_access: boolean;
	readonly allow_all_cors: boolean;
}

// From codersdk/database.go
export const DatabaseNotReachable = "database not reachable";

// From healthsdk/healthsdk.go
/**
 * DatabaseReport shows the results of pinging the configured database.Conn.
 */
export interface DatabaseReport extends BaseReport {
	/**
	 * Healthy is deprecated and left for backward compatibility purposes, use `Severity` instead.
	 */
	readonly healthy: boolean;
	readonly reachable: boolean;
	readonly latency: string;
	readonly latency_ms: number;
	readonly threshold_ms: number;
}

// From codersdk/debug.go
/**
 * DebugProfileOptions are options for collecting debug profiles from the
 * server via the consolidated /debug/profile endpoint.
 */
export interface DebugProfileOptions {
	/**
	 * Duration controls how long time-based profiles (cpu, trace) run.
	 * Zero uses the server default (10s).
	 */
	readonly Duration: number;
	/**
	 * Profiles is the list of profile types to collect. Nil or empty uses
	 * the server default (cpu, heap, allocs, block, mutex, goroutine).
	 */
	readonly Profiles: readonly string[];
}

// From codersdk/chats.go
/**
 * DefaultChatAutoArchiveDays is the default auto-archive window, in
 * days, applied when no site config row exists. Zero disables
 * auto-archival.
 */
export const DefaultChatAutoArchiveDays = 0;

// From codersdk/chats.go
/**
 * DefaultChatDebugRetentionDays is the default chat debug run retention
 * window, in days, applied when no site config row exists. Set the
 * config value to zero to disable the purge.
 */
export const DefaultChatDebugRetentionDays = 30;

// From codersdk/chats.go
/**
 * Defaults for the chat limits in [ChatConfig].
 * DefaultChatMaxAttachmentsPerChat is the default maximum number of
 * files linked to a chat.
 */
export const DefaultChatMaxAttachmentsPerChat = 50;

// From codersdk/chats.go
/**
 * Defaults for the chat limits in [ChatConfig].
 * DefaultChatMaxConcurrentRecordingUploads is the default maximum
 * number of virtual desktop recordings that each Coder server stores
 * at the same time.
 */
export const DefaultChatMaxConcurrentRecordingUploads = 25;

// From codersdk/chats.go
/**
 * Defaults for the chat limits in [ChatConfig].
 * DefaultChatMaxGenerationRetries is the default maximum number of
 * consecutive retries after a model generation fails with a transient
 * error.
 */
export const DefaultChatMaxGenerationRetries = 25;

// From codersdk/chats.go
/**
 * Defaults for the chat limits in [ChatConfig].
 * DefaultChatMaxPromptBytes is the default maximum size in bytes of the
 * deployment system prompt, the plan mode instructions, and each
 * user's custom prompt.
 */
export const DefaultChatMaxPromptBytes = 131072;

// From codersdk/chats.go
/**
 * Defaults for the chat limits in [ChatConfig].
 * DefaultChatMaxQueuedMessagesPerChat is the default maximum number of
 * messages that can be queued in a chat.
 */
export const DefaultChatMaxQueuedMessagesPerChat = 20;

// From codersdk/chats.go
/**
 * Defaults for the chat limits in [ChatConfig].
 * DefaultChatMaxStepsPerTurn is the default maximum number of steps in
 * a chat turn.
 */
export const DefaultChatMaxStepsPerTurn = 1200;

// From codersdk/chats.go
/**
 * DefaultChatWorkspaceTTL is the default TTL for chat workspaces.
 * Zero means disabled; the template's own autostop setting applies.
 */
export const DefaultChatWorkspaceTTL = 0;

// From codersdk/externalauth.go
export interface DeleteExternalAuthByIDResponse {
	/**
	 * TokenRevoked set to true if token revocation was attempted and was successful
	 */
	readonly token_revoked: boolean;
	readonly token_revocation_error?: string;
}

// From codersdk/notifications.go
export interface DeleteWebpushSubscription {
	readonly endpoint: string;
}

// From codersdk/workspaceagentportshare.go
export interface DeleteWorkspaceAgentPortShareRequest {
	readonly agent_name: string;
	readonly port: number;
}

// From codersdk/deployment.go
/**
 * DeploymentConfig contains both the deployment values and how they're set.
 */
export interface DeploymentConfig {
	readonly config?: DeploymentValues;
	readonly options?: SerpentOptionSet;
}

// From codersdk/deployment.go
export interface DeploymentStats {
	/**
	 * AggregatedFrom is the time in which stats are aggregated from.
	 * This might be back in time a specific duration or interval.
	 */
	readonly aggregated_from: string;
	/**
	 * CollectedAt is the time in which stats are collected at.
	 */
	readonly collected_at: string;
	/**
	 * NextUpdateAt is the time when the next batch of stats will
	 * be updated.
	 */
	readonly next_update_at: string;
	readonly workspaces: WorkspaceDeploymentStats;
	readonly session_count: SessionCountDeploymentStats;
}

// From codersdk/deployment.go
/**
 * DeploymentValues is the central configuration values the coder server.
 */
export interface DeploymentValues {
	readonly verbose?: boolean;
	readonly access_url?: string;
	readonly wildcard_access_url?: string;
	readonly docs_url?: string;
	readonly redirect_to_access_url?: boolean;
	/**
	 * HTTPAddress is a string because it may be set to zero to disable.
	 */
	readonly http_address?: string;
	readonly autobuild_poll_interval?: number;
	readonly job_hang_detector_interval?: number;
	readonly cluster?: ClusterConfig;
	readonly derp?: DERP;
	readonly prometheus?: PrometheusConfig;
	readonly pprof?: PprofConfig;
	readonly proxy_trusted_headers?: string;
	readonly proxy_trusted_origins?: string;
	readonly cache_directory?: string;
	readonly ephemeral_deployment?: boolean;
	readonly pg_connection_url?: string;
	readonly pg_auth?: string;
	readonly pg_conn_max_open?: number;
	readonly pg_conn_max_idle?: string;
	readonly oauth2?: OAuth2Config;
	readonly oidc?: OIDCConfig;
	readonly telemetry?: TelemetryConfig;
	readonly tls?: TLSConfig;
	readonly trace?: TraceConfig;
	readonly http_cookies?: HTTPCookieConfig;
	readonly strict_transport_security?: number;
	readonly strict_transport_security_options?: string;
	readonly ssh_keygen_algorithm?: string;
	readonly metrics_cache_refresh_interval?: number;
	readonly agent_stat_refresh_interval?: number;
	readonly agent_fallback_troubleshooting_url?: string;
	readonly browser_only?: boolean;
	readonly scim_api_key?: string;
	readonly scim_use_legacy?: boolean;
	readonly external_token_encryption_keys?: string;
	readonly provisioner?: ProvisionerConfig;
	readonly rate_limit?: RateLimitConfig;
	readonly experiments?: string;
	readonly update_check?: boolean;
	readonly swagger?: SwaggerConfig;
	readonly logging?: LoggingConfig;
	readonly dangerous?: DangerousConfig;
	readonly disable_path_apps?: boolean;
	readonly session_lifetime?: SessionLifetime;
	readonly disable_password_auth?: boolean;
	readonly support?: SupportConfig;
	readonly enable_authz_recording?: boolean;
	readonly external_auth?: SerpentStruct<ExternalAuthConfig[]>;
	readonly external_auth_github_default_provider_enable?: boolean;
	readonly config_ssh?: SSHConfig;
	readonly wgtunnel_host?: string;
	readonly disable_owner_workspace_exec?: boolean;
	readonly disable_workspace_sharing?: boolean;
	readonly disable_chat_sharing?: boolean;
	readonly disable_chat_caller_supplied_tools?: boolean;
	readonly disable_workspace_agent_context_sync?: boolean;
	readonly disable_user_secret_file_path?: boolean;
	readonly proxy_health_status_interval?: number;
	readonly enable_terraform_debug_mode?: boolean;
	readonly dynamic_parameters_full_evaluation?: boolean;
	readonly user_quiet_hours_schedule?: UserQuietHoursScheduleConfig;
	readonly web_terminal_renderer?: string;
	/**
	 * @deprecated Use the per-template allow_workspace_renames setting instead.
	 */
	readonly allow_workspace_renames?: boolean;
	readonly healthcheck?: HealthcheckConfig;
	readonly retention?: RetentionConfig;
	readonly cli_upgrade_message?: string;
	readonly terms_of_service_url?: string;
	readonly notifications?: NotificationsConfig;
	readonly additional_csp_policy?: string;
	readonly workspace_hostname_suffix?: string;
	readonly workspace_prebuilds?: PrebuildsConfig;
	readonly mcp_allowed_private_cidrs?: string;
	readonly ai?: AIConfig;
	readonly stats_collection?: StatsCollectionConfig;
	readonly template_builder?: TemplateBuilderConfig;
	readonly config?: string;
	readonly write_config?: boolean;
	/**
	 * @deprecated Use HTTPAddress or TLS.Address instead.
	 */
	readonly address?: string;
}

// From codersdk/parameters.go
export interface DiagnosticExtra {
	readonly code: string;
}

// From codersdk/parameters.go
export type DiagnosticSeverityString = "error" | "warning";

export const DiagnosticSeverityStrings: DiagnosticSeverityString[] = [
	"error",
	"warning",
];

// From codersdk/chats.go
/**
 * DiffStatusRef identifies one ref that a chat tracks. A chat has
 * one diff status for each ref.
 */
export interface DiffStatusRef {
	readonly remote_origin: string;
	readonly git_branch: string;
}

// From codersdk/disconnect.go
export type DisconnectInitiator =
	| "agent"
	| "client"
	| "network"
	| "server"
	| "";

export const DisconnectInitiators: DisconnectInitiator[] = [
	"agent",
	"client",
	"network",
	"server",
	"",
];

// From codersdk/disconnect.go
export type DisconnectReason =
	| "client_closed"
	| "control_plane_lost"
	| "graceful"
	| "network_error"
	| "protocol_error"
	| "server_shutdown"
	| ""
	| "workspace_stopped";

export const DisconnectReasons: DisconnectReason[] = [
	"client_closed",
	"control_plane_lost",
	"graceful",
	"network_error",
	"protocol_error",
	"server_shutdown",
	"",
	"workspace_stopped",
];

// From codersdk/workspaceagents.go
export type DisplayApp =
	| "port_forwarding_helper"
	| "ssh_helper"
	| "vscode"
	| "vscode_insiders"
	| "web_terminal";

export const DisplayApps: DisplayApp[] = [
	"port_forwarding_helper",
	"ssh_helper",
	"vscode",
	"vscode_insiders",
	"web_terminal",
];

// From codersdk/parameters.go
export interface DynamicParametersRequest {
	/**
	 * ID identifies the request. The response contains the same
	 * ID so that the client can match it to the request.
	 */
	readonly id: number;
	readonly inputs: Record<string, string>;
	/**
	 * OwnerID if uuid.Nil, it defaults to `codersdk.Me`
	 */
	readonly owner_id?: string;
}

// From codersdk/parameters.go
export interface DynamicParametersResponse {
	readonly id: number;
	readonly diagnostics: readonly FriendlyDiagnostic[];
	readonly parameters: readonly PreviewParameter[];
}

// From codersdk/chats.go
/**
 * DynamicTool describes a client-declared tool definition. On the
 * client side, the Handler callback executes the tool when the LLM
 * invokes it. On the server side, only Name, Description, and
 * InputSchema are used (Handler is not serialized).
 */
export interface DynamicTool {
	readonly name: string;
	readonly description?: string;
	/**
	 * InputSchema's JSON key "input_schema" uses snake_case for
	 * SDK consistency, deviating from the camelCase "inputSchema"
	 * convention used by MCP.
	 */
	readonly input_schema: Record<string, string>;
}

// From codersdk/chats.go
/**
 * DynamicToolCall represents a pending tool invocation from the
 * chat stream that the client must execute and submit back.
 */
export interface DynamicToolCall {
	readonly tool_call_id: string;
	readonly tool_name: string;
	readonly args: string;
}

// From codersdk/chats.go
/**
 * DynamicToolResponse holds the output of a dynamic tool
 * execution. IsError indicates a tool-level error the LLM
 * should see, as opposed to an infrastructure failure
 * (returned as the error return value).
 */
export interface DynamicToolResponse {
	readonly content: string;
	readonly is_error: boolean;
}

// From codersdk/chats.go
/**
 * EditChatMessageRequest is the request to edit a user message in a chat.
 */
export interface EditChatMessageRequest {
	readonly content: readonly ChatInputPart[];
	/**
	 * ModelConfigID, when set, overrides the model used for the
	 * replacement user message and the assistant turn that follows.
	 * When nil the original message's model is preserved.
	 */
	readonly model_config_id?: string;
	readonly reasoning_effort?: string;
	/**
	 * MCPServerIDs, when set, replaces the chat's MCP server selection
	 * before the replacement turn runs. When nil the current selection
	 * is preserved.
	 */
	readonly mcp_server_ids?: string[];
}

// From codersdk/chats.go
/**
 * EditChatMessageResponse is the response from editing a message in a chat.
 */
export interface EditChatMessageResponse {
	readonly message: ChatMessage;
	/**
	 * Messages holds every user-visible message inserted by the edit, in
	 * insertion order. Hook-generated suffix messages may follow Message,
	 * so clients must upsert the full batch.
	 */
	readonly messages?: readonly ChatMessage[];
	/**
	 * DeletedMessageIDs holds the IDs of previously visible messages the
	 * edit removed, including stale hook notices from the edited turn.
	 * Clients should drop them from local caches.
	 */
	readonly deleted_message_ids?: readonly number[];
	readonly warnings?: readonly string[];
}

// From codersdk/chats.go
/**
 * EditChatQueuedMessageRequest edits a queued message. Omitted fields
 * are left unchanged; a request with no fields is rejected.
 */
export interface EditChatQueuedMessageRequest {
	/**
	 * Content replaces the queued content. An empty array is rejected.
	 */
	readonly content?: readonly ChatInputPart[];
	/**
	 * ModelConfigID and ReasoningEffort require Content; sending either
	 * without it returns 400. Omitted values keep the stored ones.
	 */
	readonly model_config_id?: string;
	readonly reasoning_effort?: string;
	/**
	 * Editing begins (true) or ends (false) an edit of the message. A
	 * chat has at most one message under edit; beginning another ends the
	 * first. While the chat is paused, beginning an edit on another
	 * message returns 409. Ending the edit of a paused chat's head sends
	 * it.
	 */
	readonly editing?: boolean;
}

// From codersdk/externalauth.go
export type EnhancedExternalAuthProvider =
	| "azure-devops"
	| "azure-devops-entra"
	| "bitbucket-cloud"
	| "bitbucket-server"
	| "github"
	| "gitlab"
	| "gitea"
	| "jfrog"
	| "slack";

export const EnhancedExternalAuthProviders: EnhancedExternalAuthProvider[] = [
	"azure-devops",
	"azure-devops-entra",
	"bitbucket-cloud",
	"bitbucket-server",
	"github",
	"gitlab",
	"gitea",
	"jfrog",
	"slack",
];

// From codersdk/deployment.go
export type Entitlement = "entitled" | "grace_period" | "not_entitled";

// From codersdk/deployment.go
export interface Entitlements {
	readonly features: Record<FeatureName, Feature>;
	readonly warnings: readonly string[];
	readonly errors: readonly string[];
	readonly has_license: boolean;
	readonly trial: boolean;
	readonly require_telemetry: boolean;
	readonly refreshed_at: string;
}

// From codersdk/client.go
/**
 * EntitlementsWarnings contains active warnings for the user's entitlements.
 */
export const EntitlementsWarningHeader = "X-Coder-Entitlements-Warning";

// From codersdk/deployment.go
export type Experiment =
	| "ai-gateway-reverse-proxy"
	| "ai-gateway-seat-exclusion"
	| "agent-lifecycle-hooks"
	| "chat-acp-subagents"
	| "chat-inline-mcp-servers"
	| "chat-project-sharing"
	| "chat-stage-metrics"
	| "chat-virtual-desktop"
	| "enable-ai-workspace-debug"
	| "example"
	| "mcp-server-http"
	| "no_nats_pubsub"
	| "notifications"
	| "workspace-build-updates"
	| "workspace-capable-licensing"
	| "workspace-usage";

// From codersdk/experimentrules.go
/**
 * ExperimentRule is the stored runtime rule of one experiment.
 */
export interface ExperimentRule {
	/**
	 * Mode is one of the ExperimentRuleMode values, or empty when the
	 * stored rule is malformed. A malformed rule decides off until it is
	 * replaced. It is a plain string so that clients can represent the
	 * malformed state.
	 */
	readonly mode: string;
	/**
	 * Condition is the CEL expression of a condition rule.
	 */
	readonly condition?: string;
	/**
	 * Revision increases on every change and starts at 1. Zero means the
	 * stored rule has no readable positive revision, so it is malformed.
	 */
	readonly revision: number;
	readonly updated_by: string;
	readonly updated_at: string;
}

// From codersdk/experimentrules.go
/**
 * ExperimentRuleEntry describes the runtime rule state of one experiment.
 */
export interface ExperimentRuleEntry {
	/**
	 * Experiment is the experiment name. Ignored entries can name
	 * experiments this version does not know, so it is a plain string.
	 */
	readonly experiment: string;
	/**
	 * StaticDefault reports whether the experiment is in the startup
	 * --experiments list of the replica that answered.
	 */
	readonly static_default: boolean;
	/**
	 * Rule is null when no rule was ever stored.
	 */
	readonly rule: ExperimentRule | null;
	/**
	 * Ignored is true for a stored rule of an experiment that does not
	 * accept runtime rules. Such a rule has no effect.
	 */
	readonly ignored: boolean;
}

// From codersdk/experimentrules.go
export type ExperimentRuleMode = "condition" | "inherit" | "off" | "on";

export const ExperimentRuleModes: ExperimentRuleMode[] = [
	"condition",
	"inherit",
	"off",
	"on",
];

export const Experiments: Experiment[] = [
	"ai-gateway-reverse-proxy",
	"ai-gateway-seat-exclusion",
	"agent-lifecycle-hooks",
	"chat-acp-subagents",
	"chat-inline-mcp-servers",
	"chat-project-sharing",
	"chat-stage-metrics",
	"chat-virtual-desktop",
	"enable-ai-workspace-debug",
	"example",
	"mcp-server-http",
	"no_nats_pubsub",
	"notifications",
	"workspace-build-updates",
	"workspace-capable-licensing",
	"workspace-usage",
];

// From codersdk/scopes_catalog.go
export interface ExternalAPIKeyScopes {
	readonly external: readonly APIKeyScope[];
}

// From codersdk/workspaces.go
/**
 * ExternalAgentCredentials contains the credentials needed for an external agent to connect to Coder.
 */
export interface ExternalAgentCredentials {
	readonly command: string;
	readonly agent_token: string;
}

// From codersdk/externalauth.go
export interface ExternalAuth {
	readonly authenticated: boolean;
	readonly device: boolean;
	readonly display_name: string;
	readonly supports_revocation: boolean;
	/**
	 * User is the user that authenticated with the provider.
	 */
	readonly user: ExternalAuthUser | null;
	/**
	 * AppInstallable is true if the request for app installs was successful.
	 */
	readonly app_installable: boolean;
	/**
	 * AppInstallations are the installations that the user has access to.
	 */
	readonly installations: readonly ExternalAuthAppInstallation[];
	/**
	 * AppInstallURL is the URL to install the app.
	 */
	readonly app_install_url: string;
}

// From codersdk/externalauth.go
export interface ExternalAuthAppInstallation {
	readonly id: number;
	readonly account: ExternalAuthUser;
	readonly configure_url: string;
}

// From codersdk/deployment.go
export interface ExternalAuthConfig {
	/**
	 * Type is the type of external auth config.
	 */
	readonly type: string;
	readonly client_id: string;
	/**
	 * ID is a unique identifier for the auth config.
	 * It defaults to `type` when not provided.
	 */
	readonly id: string;
	readonly auth_url: string;
	readonly token_url: string;
	readonly validate_url: string;
	/**
	 * RedirectURL is optional, defaulting to 'ACCESS_URL'. Only useful in niche
	 * situations where the OAuth callback domain is different from the ACCESS_URL
	 * domain. The path component is ignored.
	 */
	readonly redirect_url: string;
	readonly revoke_url: string;
	readonly app_install_url: string;
	readonly app_installations_url: string;
	readonly no_refresh: boolean;
	readonly scopes: readonly string[];
	readonly device_flow: boolean;
	readonly device_code_url: string;
	/**
	 * @deprecated Injected MCP in AI Bridge is deprecated and will be removed in a future release.
	 */
	readonly mcp_url: string;
	/**
	 * @deprecated Injected MCP in AI Bridge is deprecated and will be removed in a future release.
	 */
	readonly mcp_tool_allow_regex: string;
	/**
	 * @deprecated Injected MCP in AI Bridge is deprecated and will be removed in a future release.
	 */
	readonly mcp_tool_deny_regex: string;
	/**
	 * Regex allows API requesters to match an auth config by
	 * a string (e.g. coder.com) instead of by it's type.
	 *
	 * Git clone makes use of this by parsing the URL from:
	 * 'Username for "https://github.com":'
	 * And sending it to the control plane to match against the Regex.
	 */
	readonly regex: string;
	/**
	 * APIBaseURL is the base URL for provider REST API calls
	 * (e.g., "https://api.github.com" for GitHub). Derived from
	 * defaults when not explicitly configured.
	 */
	readonly api_base_url: string;
	/**
	 * DisplayName is shown in the UI to identify the auth config.
	 */
	readonly display_name: string;
	/**
	 * DisplayIcon is a URL to an icon to display in the UI.
	 */
	readonly display_icon: string;
	/**
	 * CodeChallengeMethodsSupported lists the PKCE code challenge methods
	 * The only one supported by Coder is "S256".
	 */
	readonly code_challenge_methods_supported: readonly string[];
}

// From codersdk/externalauth.go
/**
 * ExternalAuthDevice is the response from the device authorization endpoint.
 * See: https://tools.ietf.org/html/rfc8628#section-3.2
 */
export interface ExternalAuthDevice {
	readonly device_code: string;
	readonly user_code: string;
	readonly verification_uri: string;
	readonly expires_in: number;
	readonly interval: number;
}

// From codersdk/externalauth.go
export interface ExternalAuthDeviceExchange {
	readonly device_code: string;
}

// From codersdk/externalauth.go
/**
 * ExternalAuthLink is a link between a user and an external auth provider.
 * It excludes information that requires a token to access, so can be statically
 * built from the database and configs.
 */
export interface ExternalAuthLink {
	readonly provider_id: string;
	readonly created_at: string;
	readonly updated_at: string;
	readonly has_refresh_token: boolean;
	readonly expires: string;
	readonly authenticated: boolean;
	readonly validate_error: string;
}

// From codersdk/externalauth.go
/**
 * ExternalAuthLinkProvider are the static details of a provider.
 */
export interface ExternalAuthLinkProvider {
	readonly id: string;
	readonly type: string;
	readonly device: boolean;
	readonly display_name: string;
	readonly display_icon: string;
	readonly allow_refresh: boolean;
	readonly allow_validate: boolean;
	readonly supports_revocation: boolean;
	readonly code_challenge_methods_supported: readonly string[];
}

// From codersdk/externalauth.go
export interface ExternalAuthUser {
	readonly id: number;
	readonly login: string;
	readonly avatar_url: string;
	readonly profile_url: string;
	readonly name: string;
}

// From codersdk/deployment.go
export interface Feature {
	readonly entitlement: Entitlement;
	readonly enabled: boolean;
	/**
	 * Limit is the maximum value the license grants for the feature, in the
	 * feature's own unit. For FeatureAgentRuntimeHours, an enabled feature
	 * with Limit omitted means the license grants unlimited runtime hours.
	 */
	readonly limit?: number;
	/**
	 * SoftLimit is the advisory warning threshold that accompanies Limit for
	 * features whose license carries it. For these features, Limit carries
	 * the purchased allocation; an unlimited allocation has no thresholds,
	 * so SoftLimit is omitted alongside the omitted Limit. Only
	 * FeatureAgentRuntimeHours sets this field.
	 */
	readonly soft_limit?: number;
	/**
	 * HardLimit is the enforcement threshold that accompanies Limit for
	 * features whose license carries it. See SoftLimit for the set of
	 * features that use these thresholds.
	 */
	readonly hard_limit?: number;
	/**
	 * Actual is the usage measured against Limit, when known: a
	 * point-in-time count for most features, or usage accumulated over
	 * UsagePeriod for features that set one. Its unit matches Limit's;
	 * FeatureAgentRuntimeHours reports whole hours floored from the
	 * recorded milliseconds, with the precise value available in
	 * ActualMs. FeatureAgentRuntimeHours usage can trail by roughly one
	 * hour because the current hour is not emitted, plus the entitlement
	 * refresh interval.
	 */
	readonly actual?: number;
	/**
	 * ActualMs is the precise usage backing Actual, in milliseconds, for
	 * features measured in time. It has the same freshness as Actual.
	 * Only FeatureAgentRuntimeHours sets this field.
	 */
	readonly actual_ms?: number;
	/**
	 * UsagePeriod denotes that the usage is a counter that accumulates over
	 * this period (and most likely resets with the issuance of the next
	 * license). These dates are determined from the license that this
	 * entitlement comes from, see enterprise/coderd/license/license.go.
	 * Only FeatureManagedAgentLimit and FeatureAgentRuntimeHours set this
	 * field.
	 */
	readonly usage_period?: UsagePeriod;
}

// From codersdk/deployment.go
export type FeatureName =
	| "aibridge"
	| "ai_governance_user_limit"
	| "access_control"
	| "advanced_template_scheduling"
	| "agent_runtime_hours"
	| "appearance"
	| "audit_log"
	| "boundary"
	| "browser_only"
	| "connection_log"
	| "control_shared_ports"
	| "custom_roles"
	| "external_provisioner_daemons"
	| "external_token_encryption"
	| "high_availability"
	| "managed_agent_limit"
	| "multiple_external_auth"
	| "multiple_organizations"
	| "scim"
	| "service_accounts"
	| "template_rbac"
	| "user_limit"
	| "user_role_management"
	| "workspace_batch_actions"
	| "workspace_external_agent"
	| "workspace_prebuilds"
	| "workspace_proxy";

export const FeatureNames: FeatureName[] = [
	"aibridge",
	"ai_governance_user_limit",
	"access_control",
	"advanced_template_scheduling",
	"agent_runtime_hours",
	"appearance",
	"audit_log",
	"boundary",
	"browser_only",
	"connection_log",
	"control_shared_ports",
	"custom_roles",
	"external_provisioner_daemons",
	"external_token_encryption",
	"high_availability",
	"managed_agent_limit",
	"multiple_external_auth",
	"multiple_organizations",
	"scim",
	"service_accounts",
	"template_rbac",
	"user_limit",
	"user_role_management",
	"workspace_batch_actions",
	"workspace_external_agent",
	"workspace_prebuilds",
	"workspace_proxy",
];

// From codersdk/deployment.go
export type FeatureSet = "enterprise" | "" | "premium";

export const FeatureSets: FeatureSet[] = ["enterprise", "", "premium"];

// From codersdk/files.go
export const FormatZip = "zip";

// From codersdk/parameters.go
/**
 * FriendlyDiagnostic == previewtypes.FriendlyDiagnostic
 * Copied to avoid import deps
 */
export interface FriendlyDiagnostic {
	readonly severity: DiagnosticSeverityString;
	readonly summary: string;
	readonly detail: string;
	readonly extra: DiagnosticExtra;
}

// From codersdk/apikey.go
/**
 * GenerateAPIKeyResponse contains an API key for a user.
 */
export interface GenerateAPIKeyResponse {
	readonly key: string;
}

// From codersdk/inboxnotification.go
export interface GetInboxNotificationResponse {
	readonly notification: InboxNotification;
	readonly unread_count: number;
}

// From codersdk/insights.go
export interface GetUserStatusCountsRequest {
	readonly timezone: string;
	/**
	 * @deprecated Use Timezone instead. Offset is ignored when Timezone is provided.
	 */
	readonly offset?: number;
}

// From codersdk/insights.go
export interface GetUserStatusCountsResponse {
	readonly status_counts: Record<UserStatus, UserStatusChangeCount[]>;
}

// From codersdk/users.go
export interface GetUsersResponse {
	readonly users: readonly User[];
	readonly count: number;
}

// From codersdk/gitsshkey.go
export interface GitSSHKey {
	readonly user_id: string;
	readonly created_at: string;
	readonly updated_at: string;
	/**
	 * PublicKey is the SSH public key in OpenSSH format.
	 * Example: "ssh-ed25519 AAAAC3NzaC1lZDI1NTE5AAAAID3OmYJvT7q1cF1azbybYy0OZ9yrXfA+M6Lr4vzX5zlp\n"
	 * Note: The key includes a trailing newline (\n).
	 */
	readonly public_key: string;
}

// From codersdk/users.go
export interface GithubAuthMethod {
	readonly enabled: boolean;
	readonly default_provider_configured: boolean;
}

// From codersdk/groups.go
export interface Group {
	readonly id: string;
	readonly name: string;
	readonly display_name: string;
	readonly organization_id: string;
	readonly members: readonly ReducedUser[];
	/**
	 * How many members are in this group. Shows the total count,
	 * even if the user is not authorized to read group member details.
	 * May be greater than `len(Group.Members)`.
	 */
	readonly total_member_count: number;
	readonly avatar_url: string;
	readonly quota_allowance: number;
	readonly source: GroupSource;
	readonly organization_name: string;
	readonly organization_display_name: string;
}

// From codersdk/aibridge.go
export interface GroupAIBudget {
	readonly group_id: string;
	readonly spend_limit_micros: number;
	readonly created_at: string;
	readonly updated_at: string;
}

// From codersdk/aibridge.go
/**
 * GroupAISpend is the current AI spend snapshot for a single group within
 * the active budget period.
 */
export interface GroupAISpend
	extends AISpendPeriodWindow,
		OrganizationGroupAISpend {}

// From codersdk/groups.go
export interface GroupArguments {
	/**
	 * Organization can be an org UUID or name
	 */
	readonly Organization: string;
	/**
	 * HasMember can be a user uuid or username
	 */
	readonly HasMember: string;
	/**
	 * GroupIDs is a list of group UUIDs to filter by.
	 * If not set, all groups will be returned.
	 */
	readonly GroupIDs: readonly string[];
}

// From codersdk/aibridge.go
/**
 * GroupMemberAISpend is a single member's AI spend attributed to the queried
 * group in the current budget period.
 */
export interface GroupMemberAISpend {
	readonly user_id: string;
	/**
	 * EffectiveGroupID is the user's effective budget group within the queried
	 * group's organization, falling back to the Everyone group when no budget
	 * applies. Null when the effective group belongs to a different organization
	 * than the queried group.
	 */
	readonly effective_group_id: string | null;
	/**
	 * EffectiveBudget is the spend limit that currently applies to the user.
	 * Null when no budget applies or the effective group belongs to a different
	 * organization than the queried group.
	 */
	readonly effective_budget: AIBudgetLimit | null;
	/**
	 * GroupBudget is the budget when the queried group is this user's
	 * effective budget source. When populated, it matches EffectiveBudget. Null
	 * when the user's budget resolves to another group or no budget applies.
	 * @deprecated Use EffectiveBudget instead.
	 */
	readonly group_budget: AIBudgetLimit | null;
	/**
	 * GroupSpendMicros is the user's spend attributed to the queried group
	 * over the current budget period.
	 */
	readonly group_spend_micros: number;
}

// From codersdk/aibridge.go
/**
 * GroupMembersAISpend reports per-member AI spend attributed to a specific
 * group in the active budget period.
 */
export interface GroupMembersAISpend extends AISpendPeriodWindow {
	readonly members: readonly GroupMemberAISpend[];
}

// From codersdk/groups.go
export interface GroupMembersResponse {
	readonly users: readonly ReducedUser[];
	readonly count: number;
}

// From codersdk/groups.go
export interface GroupRequest {
	readonly exclude_members: boolean;
}

// From codersdk/groups.go
export type GroupSource = "oidc" | "user";

export const GroupSources: GroupSource[] = ["oidc", "user"];

// From codersdk/idpsync.go
export interface GroupSyncSettings {
	/**
	 * Field is the name of the claim field that specifies what groups a user
	 * should be in. If empty, no groups will be synced.
	 */
	readonly field: string;
	/**
	 * Mapping is a map from OIDC groups to Coder group IDs
	 */
	readonly mapping: Record<string, string[]>;
	/**
	 * RegexFilter is a regular expression that filters the groups returned by
	 * the OIDC provider. Any group not matched by this regex will be ignored.
	 * If the group filter is nil, then no group filtering will occur.
	 */
	readonly regex_filter: string | null;
	/**
	 * AutoCreateMissing controls whether groups returned by the OIDC provider
	 * are automatically created in Coder if they are missing.
	 */
	readonly auto_create_missing_groups: boolean;
	/**
	 * LegacyNameMapping is deprecated. It remaps an IDP group name to
	 * a Coder group name. Since configuration is now done at runtime,
	 * group IDs are used to account for group renames.
	 * For legacy configurations, this config option has to remain.
	 * @deprecated Use Mapping instead.
	 */
	readonly legacy_group_name_mapping?: Record<string, string>;
}

// From codersdk/deployment.go
export interface HTTPCookieConfig {
	readonly secure_auth_cookie?: boolean;
	readonly same_site?: string;
	readonly host_prefix?: boolean;
}

// From health/model.go
export type HealthCode =
	| "EACS03"
	| "EACS02"
	| "EACS04"
	| "EACS01"
	| "EDERP03"
	| "EDERP01"
	| "EDERP02"
	| "EDB01"
	| "EDB02"
	| "EPD03"
	| "EPD02"
	| "EPD01"
	| "EWP02"
	| "EWP04"
	| "EWP01"
	| "EUNKNOWN"
	| "EWS01"
	| "EWS02"
	| "EWS03";

// From health/model.go
export const HealthCodeInterfaceSmallMTU = "EIF01";

// From health/model.go
export const HealthCodeSTUNMapVaryDest = "ESTUN02";

// From health/model.go
export const HealthCodeSTUNNoNodes = "ESTUN01";

export const HealthCodes: HealthCode[] = [
	"EACS03",
	"EACS02",
	"EACS04",
	"EACS01",
	"EDERP03",
	"EDERP01",
	"EDERP02",
	"EDB01",
	"EDB02",
	"EPD03",
	"EPD02",
	"EPD01",
	"EWP02",
	"EWP04",
	"EWP01",
	"EUNKNOWN",
	"EWS01",
	"EWS02",
	"EWS03",
];

// From health/model.go
/**
 * @typescript-generate Message
 */
export interface HealthMessage {
	readonly code: HealthCode;
	readonly message: string;
}

// From healthsdk/healthsdk.go
export type HealthSection =
	| "AccessURL"
	| "DERP"
	| "Database"
	| "ProvisionerDaemons"
	| "Websocket"
	| "WorkspaceProxy";

export const HealthSections: HealthSection[] = [
	"AccessURL",
	"DERP",
	"Database",
	"ProvisionerDaemons",
	"Websocket",
	"WorkspaceProxy",
];

// From healthsdk/healthsdk.go
export interface HealthSettings {
	readonly dismissed_healthchecks: readonly HealthSection[];
}

export const HealthSeverities: HealthSeverity[] = ["error", "ok", "warning"];

// From health/model.go
export type HealthSeverity = "error" | "ok" | "warning";

// From codersdk/workspaceapps.go
export interface Healthcheck {
	/**
	 * URL specifies the endpoint to check for the app health.
	 */
	readonly url: string;
	/**
	 * Interval specifies the seconds between each health check.
	 */
	readonly interval: number;
	/**
	 * Threshold specifies the number of consecutive failed health checks before returning "unhealthy".
	 */
	readonly threshold: number;
}

// From codersdk/deployment.go
/**
 * HealthcheckConfig contains configuration for healthchecks.
 */
export interface HealthcheckConfig {
	readonly refresh: number;
	readonly threshold_database: number;
}

// From healthsdk/healthsdk.go
/**
 * HealthcheckReport contains information about the health status of a Coder deployment.
 */
export interface HealthcheckReport {
	/**
	 * Time is the time the report was generated at.
	 */
	readonly time: string;
	/**
	 * Healthy is true if the report returns no errors.
	 * @deprecated use `Severity` instead
	 */
	readonly healthy: boolean;
	/**
	 * Severity indicates the status of Coder health.
	 */
	readonly severity: HealthSeverity;
	readonly derp: DERPHealthReport;
	readonly access_url: AccessURLReport;
	readonly websocket: WebsocketReport;
	readonly database: DatabaseReport;
	readonly workspace_proxy: WorkspaceProxyReport;
	readonly provisioner_daemons: ProvisionerDaemonsReport;
	/**
	 * The Coder version of the server that the report was generated on.
	 */
	readonly coder_version: string;
}

// From codersdk/idpsync.go
export interface IDPSyncMapping<ResourceIdType extends string> {
	/**
	 * The IdP claim the user has
	 */
	readonly Given: string;
	/**
	 * The ID of the Coder resource the user should be added to
	 */
	readonly Gets: ResourceIdType;
}

// From codersdk/usersecrets.go
/**
 * ImportUserSecretsRequest is the payload for the bulk secret import
 * endpoint. Content is the raw file bytes and Format selects the parser.
 */
export interface ImportUserSecretsRequest {
	readonly format: SecretsFileFormat;
	readonly content: string;
}

// From codersdk/inboxnotification.go
export interface InboxNotification {
	readonly id: string;
	readonly user_id: string;
	readonly template_id: string;
	readonly targets: readonly string[];
	readonly title: string;
	readonly content: string;
	readonly icon: string;
	readonly actions: readonly InboxNotificationAction[];
	readonly read_at: string | null;
	readonly created_at: string;
}

// From codersdk/inboxnotification.go
export interface InboxNotificationAction {
	readonly label: string;
	readonly url: string;
}

// From codersdk/inboxnotification.go
export const InboxNotificationFallbackIconAccount = "DEFAULT_ICON_ACCOUNT";

// From codersdk/inboxnotification.go
export const InboxNotificationFallbackIconOther = "DEFAULT_ICON_OTHER";

// From codersdk/inboxnotification.go
export const InboxNotificationFallbackIconTemplate = "DEFAULT_ICON_TEMPLATE";

// From codersdk/inboxnotification.go
export const InboxNotificationFallbackIconWorkspace = "DEFAULT_ICON_WORKSPACE";

// From codersdk/chats.go
/**
 * InlineMCPServer is the redacted view of an inline MCP server.
 */
export interface InlineMCPServer {
	readonly id: string;
	readonly slug: string;
	/**
	 * URL is empty unless the chat owner makes the request.
	 */
	readonly url: string;
	readonly has_custom_headers: boolean;
	readonly tool_allow_list: readonly string[];
	readonly tool_deny_list: readonly string[];
	readonly allow_in_subagents: boolean;
	readonly forward_coder_headers: boolean;
	readonly created_at: string;
	readonly updated_at: string;
}

// From codersdk/chats.go
/**
 * InlineMCPServerRequest declares a streamable HTTP MCP server by value on
 * one chat. Headers are never returned. Header values are encrypted at
 * rest when database encryption is configured.
 */
export interface InlineMCPServerRequest {
	readonly slug: string;
	readonly url: string;
	readonly headers?: Record<string, string>;
	readonly tool_allow_list?: readonly string[];
	readonly tool_deny_list?: readonly string[];
	readonly allow_in_subagents?: boolean;
	readonly forward_coder_headers?: boolean;
}

// From codersdk/insights.go
export type InsightsReportInterval = "day" | "week";

export const InsightsReportIntervals: InsightsReportInterval[] = [
	"day",
	"week",
];

// From codersdk/templates.go
export interface InvalidatePresetsResponse {
	readonly invalidated: readonly InvalidatedPreset[];
}

// From codersdk/templates.go
export interface InvalidatedPreset {
	readonly template_name: string;
	readonly template_version_name: string;
	readonly preset_name: string;
}

// From codersdk/workspaceagents.go
export interface IssueReconnectingPTYSignedTokenRequest {
	/**
	 * URL is the URL of the reconnecting-pty endpoint you are connecting to.
	 */
	readonly url: string;
	readonly agentID: string;
}

// From codersdk/workspaceagents.go
export interface IssueReconnectingPTYSignedTokenResponse {
	readonly signed_token: string;
}

// From codersdk/provisionerdaemons.go
export type JobErrorCode = "INSUFFICIENT_QUOTA" | "REQUIRED_TEMPLATE_VARIABLES";

export const JobErrorCodes: JobErrorCode[] = [
	"INSUFFICIENT_QUOTA",
	"REQUIRED_TEMPLATE_VARIABLES",
];

// From codersdk/licenses.go
export interface License {
	readonly id: number;
	readonly uuid: string;
	readonly uploaded_at: string;
	/**
	 * Claims are the JWT claims asserted by the license.  Here we use
	 * a generic string map to ensure that all data from the server is
	 * parsed verbatim, not just the fields this version of Coder
	 * understands.
	 */
	// empty interface{} type, falling back to unknown
	readonly claims: Record<string, unknown>;
}

// From codersdk/licenses.go
export const LicenseAIGovernance90PercentWarningText =
	"You have used %d%% of your AI Governance add-on seats.";

// From codersdk/licenses.go
export const LicenseAIGovernanceOverLimitWarningText =
	"Your organization is using %d of %d AI Governance add-on seats (%d over the limit).";

// From codersdk/licenses.go
export const LicenseAgentRuntimeHoursAllocationReachedWarningText =
	"Your deployment has used %d of the %d Coder Agent runtime hours included in the current license term.";

// From codersdk/licenses.go
export const LicenseAgentRuntimeHoursClaimsIgnoredWarningText =
	"A license contains unusable Coder Agent runtime hour claims, which were ignored. The rest of that license is unaffected. Check the coderd logs for the affected license and claims, and contact support to have the license re-issued.";

// From codersdk/licenses.go
/**
 * The dashboard's LicenseBanner matches this text's pre-placeholder
 * prefix to render it muted and without a sales link, so the license
 * warning texts must stay pairwise distinct before their first
 * placeholder. See TestLicenseAgentRuntimeHoursWarningTexts.
 */
export const LicenseAgentRuntimeHoursSoftLimitWarningText =
	"Your deployment is approaching its Coder Agent runtime hours allocation: %d of the %d hours included in the current license term are used, at or above the advisory soft limit of %d hours.";

// From codersdk/licenses.go
export const LicenseAgentRuntimeUsageUnavailableErrorText =
	"Unable to determine Coder Agent runtime usage. Reported runtime hours are unavailable until the next successful refresh; workspaces are unaffected. Check the coderd logs for details.";

// From codersdk/licenses.go
export const LicenseExpiryClaim = "license_expires";

// From codersdk/licenses.go
export const LicenseTelemetryRequiredErrorText =
	"License requires telemetry but telemetry is disabled";

// From codersdk/users.go
/**
 * Trial request source origination reported to the licensor.
 * LicensorTrialSourceNewUser is the first user setup flow.
 */
export const LicensorTrialSourceNewUser = "NewUser";

// From codersdk/users.go
/**
 * Trial request source origination reported to the licensor.
 * LicensorTrialSourceProduct is a request from within the product in-app trial request
 */
export const LicensorTrialSourceProduct = "Product";

// From codersdk/deployment.go
export interface LinkConfig {
	readonly name: string;
	readonly target: string;
	readonly icon: string;
	readonly location?: string;
}

// From codersdk/chats.go
/**
 * ListChatsOptions are optional parameters for ListChats.
 */
export interface ListChatsOptions extends Pagination {
	/**
	 * Query supports raw chat search terms. If Query includes a source: term,
	 * Source must be empty.
	 */
	readonly Query: string;
	/**
	 * Source adds a source: term to Query.
	 */
	readonly Source: ChatListSource;
	readonly Labels: Record<string, string>;
	readonly ProjectID: string | null;
}

// From codersdk/inboxnotification.go
export interface ListInboxNotificationsRequest {
	readonly targets?: string;
	readonly templates?: string;
	readonly read_status?: string;
	readonly starting_before?: string;
}

// From codersdk/inboxnotification.go
export interface ListInboxNotificationsResponse {
	readonly notifications: readonly InboxNotification[];
	readonly unread_count: number;
}

// From codersdk/externalauth.go
export interface ListUserExternalAuthResponse {
	readonly providers: readonly ExternalAuthLinkProvider[];
	/**
	 * Links are all the authenticated links for the user.
	 * If a link has a provider ID that does not exist, then that provider
	 * is no longer configured, rendering it unusable. It is still valuable
	 * to include these links so that the user can unlink them.
	 */
	readonly links: readonly ExternalAuthLink[];
}

// From codersdk/provisionerdaemons.go
export type LogLevel = "debug" | "error" | "info" | "trace" | "warn";

export const LogLevels: LogLevel[] = [
	"debug",
	"error",
	"info",
	"trace",
	"warn",
];

// From codersdk/provisionerdaemons.go
export type LogSource = "provisioner" | "provisioner_daemon";

export const LogSources: LogSource[] = ["provisioner", "provisioner_daemon"];

// From codersdk/deployment.go
export interface LoggingConfig {
	readonly log_filter: string;
	readonly human: string;
	readonly json: string;
	readonly stackdriver: string;
}

// From codersdk/apikey.go
export type LoginType = "github" | "none" | "oidc" | "password" | "token" | "";

export const LoginTypes: LoginType[] = [
	"github",
	"none",
	"oidc",
	"password",
	"token",
	"",
];

// From codersdk/users.go
/**
 * LoginWithPasswordRequest enables callers to authenticate with email and password.
 */
export interface LoginWithPasswordRequest {
	readonly email: string;
	readonly password: string;
}

// From codersdk/users.go
/**
 * LoginWithPasswordResponse contains a session token for the newly authenticated user.
 */
export interface LoginWithPasswordResponse {
	readonly session_token: string;
}

// From codersdk/mcp.go
/**
 * MCPEndpoint is the remote MCP HTTP endpoint path.
 */
export const MCPEndpoint = "/api/experimental/mcp/http";

// From codersdk/mcp.go
/**
 * MCPServerConfig represents an admin-configured MCP server.
 */
export interface MCPServerConfig {
	readonly id: string;
	readonly organization_id: string;
	readonly display_name: string;
	readonly slug: string;
	readonly description: string;
	readonly icon_url: string;
	readonly transport: string; // "streamable_http" or "sse"
	readonly url: string;
	readonly auth_type: string; // "none", "oauth2", "api_key", "custom_headers", "user_oidc"
	/**
	 * OAuth2 fields (only populated for admins).
	 */
	readonly oauth2_client_id?: string;
	readonly has_oauth2_secret: boolean;
	readonly oauth2_auth_url?: string;
	readonly oauth2_token_url?: string;
	readonly oauth2_revocation_url?: string;
	readonly oauth2_resource?: string;
	readonly oauth2_scopes?: string;
	/**
	 * API key fields (only populated for admins).
	 */
	readonly api_key_header?: string;
	readonly has_api_key: boolean;
	readonly has_custom_headers: boolean;
	/**
	 * Tool governance.
	 */
	readonly tool_allow_list: readonly string[];
	readonly tool_deny_list: readonly string[];
	/**
	 * Availability policy set by admin.
	 */
	readonly availability: string; // "force_on", "default_on", "default_off"
	readonly enabled: boolean;
	readonly model_intent: boolean;
	readonly allow_in_plan_mode: boolean;
	/**
	 * ForwardCoderHeaders forwards the same Coder identity headers we
	 * send to LLM providers (X-Coder-Owner-Id, X-Coder-Chat-Id, and the
	 * optional X-Coder-Subchat-Id and X-Coder-Workspace-Id) to this
	 * MCP server on every request. Off by default to avoid leaking
	 * chat identity to third-party servers.
	 */
	readonly forward_coder_headers: boolean;
	readonly has_signing_secret: boolean;
	readonly created_at: string;
	readonly updated_at: string;
	/**
	 * Per-user state (populated for non-admin requests).
	 */
	readonly auth_connected: boolean;
}

// From codersdk/mcp.go
/**
 * MCPServerConfigACL is the resolved access control list of an MCP server
 * config.
 */
export interface MCPServerConfigACL {
	readonly users: readonly MCPServerConfigUser[];
	readonly groups: readonly MCPServerConfigGroup[];
}

// From codersdk/mcp.go
/**
 * MCPServerConfigGroup is a group entry in an MCP server config ACL.
 */
export interface MCPServerConfigGroup extends Group {
	readonly role: MCPServerConfigRole;
}

// From codersdk/mcp.go
export type MCPServerConfigRole = "" | "read";

export const MCPServerConfigRoles: MCPServerConfigRole[] = ["", "read"];

// From codersdk/mcp.go
/**
 * MCPServerConfigUser is a user entry in an MCP server config ACL.
 */
export interface MCPServerConfigUser extends MinimalUser {
	readonly role: MCPServerConfigRole;
}

// From codersdk/mcp.go
/**
 * MCPServerOAuth2DisconnectResponse reports whether the removed token
 * was also revoked at the OAuth provider.
 */
export interface MCPServerOAuth2DisconnectResponse {
	readonly token_revoked: boolean;
	readonly token_revocation_error?: string;
}

// From codersdk/provisionerdaemons.go
/**
 * MatchedProvisioners represents the number of provisioner daemons
 * available to take a job at a specific point in time.
 * Introduced in Coder version 2.18.0.
 */
export interface MatchedProvisioners {
	/**
	 * Count is the number of provisioner daemons that matched the given
	 * tags. If the count is 0, it means no provisioner daemons matched the
	 * requested tags.
	 */
	readonly count: number;
	/**
	 * Available is the number of provisioner daemons that are available to
	 * take jobs. This may be less than the count if some provisioners are
	 * busy or have been stopped.
	 */
	readonly available: number;
	/**
	 * MostRecentlySeen is the most recently seen time of the set of matched
	 * provisioners. If no provisioners matched, this field will be null.
	 */
	readonly most_recently_seen?: string;
}

// From codersdk/aimodelprices.go
/**
 * MaxAIModelPricesBytes bounds an upsert request body.
 */
export const MaxAIModelPricesBytes = 1048576; // 1 MiB

// From codersdk/aiproviders.go
/**
 * MaxAIProviderAPIKeys is the maximum number of API keys per AI provider.
 */
export const MaxAIProviderAPIKeys = 5;

// From codersdk/aibridge.go
/**
 * MaxAISpendLimitMicros is the highest AI spend limit that can be configured,
 * $1,000,000 per member per budget period.
 */
export const MaxAISpendLimitMicros = 1000000000000;

// From codersdk/aibridge.go
/**
 * MaxAISpendPeriodDays bounds explicit AI spend reporting windows.
 */
export const MaxAISpendPeriodDays = 31;

// From codersdk/chats.go
/**
 * MaxChatFileSizeBytes is the upload-endpoint cap for chat
 * attachments.
 */
export const MaxChatFileSizeBytes = 10485760;

// From codersdk/chats.go
/**
 * MaxChatTitleRunes is the longest title accepted at creation or rename,
 * counted in Unicode code points after trimming.
 */
export const MaxChatTitleRunes = 200;

// From codersdk/chats.go
/**
 * Inline MCP server declaration caps. Clients can validate before sending.
 */
export const MaxInlineMCPServerHeaderNameBytes = 128;

// From codersdk/chats.go
/**
 * Inline MCP server declaration caps. Clients can validate before sending.
 */
export const MaxInlineMCPServerHeaderValueBytes = 8192;

// From codersdk/chats.go
/**
 * Inline MCP server declaration caps. Clients can validate before sending.
 */
export const MaxInlineMCPServerHeaders = 16;

// From codersdk/chats.go
/**
 * Inline MCP server declaration caps. Clients can validate before sending.
 */
export const MaxInlineMCPServerSlugBytes = 32;

// From codersdk/chats.go
/**
 * Inline MCP server declaration caps. Clients can validate before sending.
 */
export const MaxInlineMCPServerToolFilters = 64;

// From codersdk/chats.go
/**
 * Inline MCP server declaration caps. Clients can validate before sending.
 */
export const MaxInlineMCPServerToolNameBytes = 128;

// From codersdk/chats.go
/**
 * Inline MCP server declaration caps. Clients can validate before sending.
 */
export const MaxInlineMCPServerURLBytes = 2048;

// From codersdk/chats.go
/**
 * Inline MCP server declaration caps. Clients can validate before sending.
 */
export const MaxInlineMCPServers = 5;

// From codersdk/chats.go
/**
 * Inline MCP server declaration caps. Clients can validate before sending.
 */
export const MaxInlineMCPServersBytes = 24576;

// From codersdk/usersecretsimport.go
/**
 * MaxSecretsFileBytes bounds the raw size of a secrets file before parsing.
 */
export const MaxSecretsFileBytes = 1048576; // 1 MiB

// From codersdk/usersecretvalidation.go
/**
 * MaxUserSecretEnvNameLength caps the length of an env_name when one
 * is provided. 256 is a generous round number that should allow any
 * realistic env name while still bounding inputs.
 *
 * This is a per-row syntactic check, not an aggregate. It does not
 * interact with the env_bytes aggregate (which is itself an
 * approximate budget; see MaxUserSecretsPerUserCount).
 */
export const MaxUserSecretEnvNameLength = 256;

// From codersdk/usersecretvalidation.go
/**
 * MaxUserSecretValueBytes is the maximum number of bytes for a
 * single secret value. It is enforced in two places:
 *
 *   - The HTTP handler validates the raw (plaintext) value with
 *     UserSecretValueValid before the row is written.
 *   - The Postgres trigger enforce_user_secrets_per_user_limits
 *     enforces the same number as an aggregate on stored bytes
 *     across a user's env-injected secrets. This defends the
 *     ~32 KiB Windows process env block.
 *
 * On deployments with secret encryption enabled, stored bytes
 * exceed plaintext by ~1.33x (AES-GCM + base64), so the trigger's
 * env-aggregate budget can be reached at less plaintext than the
 * handler's per-value check would suggest. The trigger is
 * authoritative; the handler's check is a fast pre-flight that
 * catches the common "one value is too big" case before the row
 * is encrypted and sent to the DB.
 *
 * One number serves both roles because the per-value cap can't
 * usefully exceed the smallest aggregate cap any single row could
 * trip: a value bigger than the env aggregate would be rejected
 * the moment its env_name was set, so allowing it at the per-value
 * layer would just move the failure later.
 *
 * See MaxUserSecretsPerUserCount for the rationale behind the other
 * two caps (count, total bytes).
 */
export const MaxUserSecretValueBytes = 24576; // 24 KiB

// From codersdk/usersecretvalidation.go
/**
 * MaxUserSecretsPerUserCount caps the number of secrets a single user
 * may own.
 *
 * Why a cap exists at all: user_secrets is user-scoped, so every
 * workspace the user owns loads the same set into its agent
 * manifest, and env-injected ones land in the workspace agent's
 * process env. Without a cap, a user can overflow one of three
 * external limits by accumulating enough secrets, or by making
 * them large enough. The failure surfaces at workspace start (or
 * as a truncated env), not at create-time.
 *
 * What drives each cap, and the rough math:
 *
 *   - Count (50): backstops row-count growth from many small
 *     secrets. The total-bytes cap binds first for large secrets;
 *     this cap binds first for typical-sized ones (~few KB).
 *
 *   - Total bytes (200 KiB): sized to cover realistic credential
 *     storage (API keys, SSH keys, kubeconfigs, cert bundles)
 *     with headroom. Well under the 4 MiB DRPC agent manifest
 *     budget (codersdk/drpcsdk.MaxMessageSize).
 *
 *   - Env bytes (24 KiB): an approximate budget for the value
 *     bytes of env-injected secrets. Leaves ~8 KiB of headroom
 *     under the ~32 KiB Windows process env block
 *     (CreateProcessW's lpEnvironment is capped at 32,767
 *     characters) for what this aggregate does not count:
 *     env_name bytes, per-entry overhead, agent-injected vars
 *     (CODER_*, PATH, HOME, ...), and template-defined env. Not
 *     a strict overflow guarantee. Linux/macOS ARG_MAX (~2 MiB)
 *     is far above this, so one Windows-safe cap works
 *     everywhere.
 *
 * Byte caps measure stored bytes (octet_length of encrypted+base64).
 * Plaintext is slightly tighter in encrypted deployments. That is
 * fine: the limits we defend all measure transmitted bytes, and
 * stored bytes upper-bound those.
 *
 * The Postgres trigger enforce_user_secrets_per_user_limits is the
 * source of truth; the HTTP handler maps its check_violation to a
 * 400. TestUserSecretLimits in coderd/usersecrets_test.go exercises
 * off-by-one at each cap across POST and PATCH, so any drift
 * between these constants and the trigger's literals fails an
 * assertion.
 */
export const MaxUserSecretsPerUserCount = 50;

// From codersdk/usersecretvalidation.go
/**
 * MaxUserSecretsTotalValueBytes caps the sum of stored value bytes
 * per user. See MaxUserSecretsPerUserCount for the full rationale and
 * math behind all three caps.
 */
export const MaxUserSecretsTotalValueBytes = 204800; // 200 KiB

// From codersdk/organizations.go
export interface MinimalOrganization {
	readonly id: string;
	readonly name: string;
	readonly display_name: string;
	readonly icon: string;
}

// From codersdk/users.go
/**
 * MinimalUser is the minimal information needed to identify a user and show
 * them on the UI.
 */
export interface MinimalUser {
	readonly id: string;
	readonly username: string;
	readonly name?: string;
	readonly avatar_url?: string;
}

// From netcheck/netcheck.go
/**
 * Report contains the result of a single netcheck.
 */
export interface NetcheckReport {
	readonly UDP: boolean; // a UDP STUN round trip completed
	readonly IPv6: boolean; // an IPv6 STUN round trip completed
	readonly IPv4: boolean; // an IPv4 STUN round trip completed
	readonly IPv6CanSend: boolean; // an IPv6 packet was able to be sent
	readonly IPv4CanSend: boolean; // an IPv4 packet was able to be sent
	readonly OSHasIPv6: boolean; // could bind a socket to ::1
	readonly ICMPv4: boolean; // an ICMPv4 round trip completed
	/**
	 * MappingVariesByDestIP is whether STUN results depend which
	 * STUN server you're talking to (on IPv4).
	 */
	readonly MappingVariesByDestIP: boolean | null;
	/**
	 * UPnP is whether UPnP appears present on the LAN.
	 * Empty means not checked.
	 */
	readonly UPnP: boolean | null;
	/**
	 * PMP is whether NAT-PMP appears present on the LAN.
	 * Empty means not checked.
	 */
	readonly PMP: boolean | null;
	/**
	 * PCP is whether PCP appears present on the LAN.
	 * Empty means not checked.
	 */
	readonly PCP: boolean | null;
	readonly PreferredDERP: number; // or 0 for unknown
	readonly RegionLatency: Record<number, number>; // keyed by DERP Region ID
	readonly RegionV4Latency: Record<number, number>; // keyed by DERP Region ID
	readonly RegionV6Latency: Record<number, number>; // keyed by DERP Region ID
	readonly GlobalV4: string; // ip:port of global IPv4
	readonly GlobalV6: string; // [ip]:port of global IPv6
	/**
	 * CaptivePortal is set when we think there's a captive portal that is
	 * intercepting HTTP traffic.
	 */
	readonly CaptivePortal: boolean | null;
}

// From codersdk/notifications.go
export interface NotificationMethodsResponse {
	readonly available: readonly string[];
	readonly default: string;
}

// From codersdk/notifications.go
export interface NotificationPreference {
	readonly id: string;
	readonly disabled: boolean;
	readonly updated_at: string;
}

// From codersdk/notifications.go
export interface NotificationTemplate {
	readonly id: string;
	readonly name: string;
	readonly title_template: string;
	readonly body_template: string;
	readonly actions: string;
	readonly group: string;
	readonly method: string;
	readonly kind: string;
	readonly enabled_by_default: boolean;
}

// From codersdk/deployment.go
export interface NotificationsConfig {
	/**
	 * The upper limit of attempts to send a notification.
	 */
	readonly max_send_attempts: number;
	/**
	 * The minimum time between retries.
	 */
	readonly retry_interval: number;
	/**
	 * The notifications system buffers message updates in memory to ease pressure on the database.
	 * This option controls how often it synchronizes its state with the database. The shorter this value the
	 * lower the change of state inconsistency in a non-graceful shutdown - but it also increases load on the
	 * database. It is recommended to keep this option at its default value.
	 */
	readonly sync_interval: number;
	/**
	 * The notifications system buffers message updates in memory to ease pressure on the database.
	 * This option controls how many updates are kept in memory. The lower this value the
	 * lower the change of state inconsistency in a non-graceful shutdown - but it also increases load on the
	 * database. It is recommended to keep this option at its default value.
	 */
	readonly sync_buffer_size: number;
	/**
	 * How long a notifier should lease a message. This is effectively how long a notification is 'owned'
	 * by a notifier, and once this period expires it will be available for lease by another notifier. Leasing
	 * is important in order for multiple running notifiers to not pick the same messages to deliver concurrently.
	 * This lease period will only expire if a notifier shuts down ungracefully; a dispatch of the notification
	 * releases the lease.
	 */
	readonly lease_period: number;
	/**
	 * How many notifications a notifier should lease per fetch interval.
	 */
	readonly lease_count: number;
	/**
	 * How often to query the database for queued notifications.
	 */
	readonly fetch_interval: number;
	/**
	 * Which delivery method to use (available options: 'smtp', 'webhook').
	 */
	readonly method: string;
	/**
	 * How long to wait while a notification is being sent before giving up.
	 */
	readonly dispatch_timeout: number;
	/**
	 * SMTP settings.
	 */
	readonly email: NotificationsEmailConfig;
	/**
	 * Webhook settings.
	 */
	readonly webhook: NotificationsWebhookConfig;
	/**
	 * Inbox settings.
	 */
	readonly inbox: NotificationsInboxConfig;
}

// From codersdk/deployment.go
export interface NotificationsEmailAuthConfig {
	/**
	 * Identity for PLAIN auth.
	 */
	readonly identity: string;
	/**
	 * Username for LOGIN/PLAIN auth.
	 */
	readonly username: string;
	/**
	 * Password for LOGIN/PLAIN auth.
	 */
	readonly password: string;
	/**
	 * File from which to load the password for LOGIN/PLAIN auth.
	 */
	readonly password_file: string;
}

// From codersdk/deployment.go
export interface NotificationsEmailConfig {
	/**
	 * The sender's address.
	 */
	readonly from: string;
	/**
	 * The intermediary SMTP host through which emails are sent (host:port).
	 */
	readonly smarthost: string;
	/**
	 * The hostname identifying the SMTP server.
	 */
	readonly hello: string;
	/**
	 * Authentication details.
	 */
	readonly auth: NotificationsEmailAuthConfig;
	/**
	 * TLS details.
	 */
	readonly tls: NotificationsEmailTLSConfig;
	/**
	 * ForceTLS causes a TLS connection to be attempted.
	 */
	readonly force_tls: boolean;
}

// From codersdk/deployment.go
export interface NotificationsEmailTLSConfig {
	/**
	 * StartTLS attempts to upgrade plain connections to TLS.
	 */
	readonly start_tls: boolean;
	/**
	 * ServerName to verify the hostname for the targets.
	 */
	readonly server_name: string;
	/**
	 * InsecureSkipVerify skips target certificate validation.
	 */
	readonly insecure_skip_verify: boolean;
	/**
	 * CAFile specifies the location of the CA certificate to use.
	 */
	readonly ca_file: string;
	/**
	 * CertFile specifies the location of the certificate to use.
	 */
	readonly cert_file: string;
	/**
	 * KeyFile specifies the location of the key to use.
	 */
	readonly key_file: string;
}

// From codersdk/deployment.go
export interface NotificationsInboxConfig {
	readonly enabled: boolean;
}

// From codersdk/notifications.go
export interface NotificationsSettings {
	readonly notifier_paused: boolean;
}

// From codersdk/deployment.go
export interface NotificationsWebhookConfig {
	/**
	 * The URL to which the payload will be sent with an HTTP POST request.
	 */
	readonly endpoint: string;
}

// From codersdk/parameters.go
/**
 * NullHCLString == `previewtypes.NullHCLString`.
 */
export interface NullHCLString {
	readonly value: string;
	readonly valid: boolean;
}

// From codersdk/oauth2.go
export interface OAuth2AppEndpoints {
	readonly authorization: string;
	readonly token: string;
	readonly token_revoke: string;
	/**
	 * DeviceAuth is optional.
	 */
	readonly device_authorization: string;
}

// From codersdk/name.go
/**
 * OAuth2AppNameMaxBytes is the maximum UTF-8 byte length of an OAuth2
 * application name.
 */
export const OAuth2AppNameMaxBytes = 64;

// From codersdk/oauth2.go
/**
 * OAuth2AuthorizationServerMetadata represents RFC 8414 OAuth 2.0 Authorization Server Metadata.
 */
export interface OAuth2AuthorizationServerMetadata {
	readonly issuer: string;
	readonly authorization_endpoint: string;
	readonly token_endpoint: string;
	readonly registration_endpoint?: string;
	readonly revocation_endpoint?: string;
	readonly response_types_supported: readonly OAuth2ProviderResponseType[];
	readonly grant_types_supported?: readonly OAuth2ProviderGrantType[];
	readonly code_challenge_methods_supported?: readonly OAuth2PKCECodeChallengeMethod[];
	readonly scopes_supported?: readonly string[];
	readonly token_endpoint_auth_methods_supported?: readonly OAuth2TokenEndpointAuthMethod[];
}

// From codersdk/oauth2.go
/**
 * OAuth2ClientConfiguration represents RFC 7592 Client Read Response.
 */
export interface OAuth2ClientConfiguration {
	readonly client_id: string;
	readonly client_id_issued_at: number;
	readonly client_secret_expires_at?: number;
	readonly redirect_uris?: readonly string[];
	readonly client_name?: string;
	readonly client_uri?: string;
	readonly logo_uri?: string;
	readonly tos_uri?: string;
	readonly policy_uri?: string;
	readonly jwks_uri?: string;
	readonly jwks?: Record<string, string>;
	readonly software_id?: string;
	readonly software_version?: string;
	readonly grant_types: readonly OAuth2ProviderGrantType[];
	readonly response_types: readonly OAuth2ProviderResponseType[];
	readonly token_endpoint_auth_method: OAuth2TokenEndpointAuthMethod;
	readonly scope?: string;
	readonly contacts?: readonly string[];
	readonly registration_access_token?: string;
	readonly registration_client_uri: string;
}

// From codersdk/oauth2.go
/**
 * OAuth2ClientRegistrationRequest represents RFC 7591 Dynamic Client Registration Request.
 */
export interface OAuth2ClientRegistrationRequest {
	readonly redirect_uris?: readonly string[];
	readonly client_name?: string;
	readonly client_uri?: string;
	readonly logo_uri?: string;
	readonly tos_uri?: string;
	readonly policy_uri?: string;
	readonly jwks_uri?: string;
	readonly jwks?: Record<string, string>;
	readonly software_id?: string;
	readonly software_version?: string;
	readonly software_statement?: string;
	readonly grant_types?: readonly OAuth2ProviderGrantType[];
	readonly response_types?: readonly OAuth2ProviderResponseType[];
	readonly token_endpoint_auth_method?: OAuth2TokenEndpointAuthMethod;
	readonly scope?: string;
	readonly contacts?: readonly string[];
}

// From codersdk/oauth2.go
/**
 * OAuth2ClientRegistrationResponse represents RFC 7591 Dynamic Client Registration Response.
 */
export interface OAuth2ClientRegistrationResponse {
	readonly client_id: string;
	readonly client_secret?: string;
	readonly client_id_issued_at?: number;
	readonly client_secret_expires_at?: number;
	readonly redirect_uris?: readonly string[];
	readonly client_name?: string;
	readonly client_uri?: string;
	readonly logo_uri?: string;
	readonly tos_uri?: string;
	readonly policy_uri?: string;
	readonly jwks_uri?: string;
	readonly jwks?: Record<string, string>;
	readonly software_id?: string;
	readonly software_version?: string;
	readonly grant_types: readonly OAuth2ProviderGrantType[];
	readonly response_types: readonly OAuth2ProviderResponseType[];
	readonly token_endpoint_auth_method: OAuth2TokenEndpointAuthMethod;
	readonly scope?: string;
	readonly contacts?: readonly string[];
	readonly registration_access_token: string;
	readonly registration_client_uri: string;
}

// From codersdk/oauth2.go
export type OAuth2ClientType = "confidential" | "public";

export const OAuth2ClientTypes: OAuth2ClientType[] = ["confidential", "public"];

// From codersdk/deployment.go
export interface OAuth2Config {
	readonly github: OAuth2GithubConfig;
	readonly provider: OAuth2ProviderConfig;
}

// From codersdk/oauth2.go
export interface OAuth2DeviceFlowCallbackResponse {
	readonly redirect_url: string;
}

// From codersdk/oauth2.go
/**
 * OAuth2Error represents an OAuth2-compliant error response per RFC 6749.
 */
export interface OAuth2Error {
	readonly error: OAuth2ErrorCode;
	readonly error_description?: string;
	readonly error_uri?: string;
}

// From codersdk/oauth2.go
export type OAuth2ErrorCode =
	| "access_denied"
	| "invalid_client"
	| "invalid_grant"
	| "invalid_request"
	| "invalid_scope"
	| "invalid_target"
	| "server_error"
	| "temporarily_unavailable"
	| "unauthorized_client"
	| "unsupported_grant_type"
	| "unsupported_response_type"
	| "unsupported_token_type";

export const OAuth2ErrorCodes: OAuth2ErrorCode[] = [
	"access_denied",
	"invalid_client",
	"invalid_grant",
	"invalid_request",
	"invalid_scope",
	"invalid_target",
	"server_error",
	"temporarily_unavailable",
	"unauthorized_client",
	"unsupported_grant_type",
	"unsupported_response_type",
	"unsupported_token_type",
];

// From codersdk/deployment.go
export interface OAuth2GithubConfig {
	readonly client_id: string;
	readonly client_secret: string;
	readonly device_flow: boolean;
	readonly default_provider_enable: boolean;
	readonly allowed_orgs: string;
	readonly allowed_teams: string;
	readonly allow_signups: boolean;
	readonly allow_everyone: boolean;
	readonly enterprise_base_url: string;
}

// From codersdk/oauth2.go
export type OAuth2PKCECodeChallengeMethod = "plain" | "S256";

export const OAuth2PKCECodeChallengeMethods: OAuth2PKCECodeChallengeMethod[] = [
	"plain",
	"S256",
];

// From codersdk/client.go
/**
 * OAuth2PKCEVerifier is the name of the cookie that stores the oauth2 PKCE
 * verifier. This is the raw verifier that when hashed, will match the challenge
 * sent in the initial oauth2 request.
 */
export const OAuth2PKCEVerifier = "oauth_pkce_verifier";

// From codersdk/oauth2.go
/**
 * OAuth2ProtectedResourceMetadata represents RFC 9728 OAuth 2.0 Protected Resource Metadata
 */
export interface OAuth2ProtectedResourceMetadata {
	readonly resource: string;
	readonly authorization_servers: readonly string[];
	readonly scopes_supported?: readonly string[];
	readonly bearer_methods_supported?: readonly string[];
}

// From codersdk/oauth2.go
export interface OAuth2ProviderApp {
	readonly id: string;
	readonly name: string;
	/**
	 * RedirectURIs are the app's registered redirect URIs, primary first.
	 */
	readonly redirect_uris: readonly string[];
	/**
	 * @deprecated equal to the first entry of redirect_uris. Read
	 * redirect_uris instead.
	 */
	readonly callback_url: string;
	readonly icon: string;
	/**
	 * Scope is the space-separated list of scopes this app's tokens may be
	 * granted. Empty means unrestricted. A non-empty value with no names is a
	 * configured allowlist that grants nothing.
	 */
	readonly scope: string;
	/**
	 * ClientType is "confidential" or "public".
	 */
	readonly client_type: OAuth2ClientType;
	/**
	 * DynamicallyRegistered is true when the app registered itself through
	 * Dynamic Client Registration rather than being created by an admin.
	 */
	readonly dynamically_registered: boolean;
	/**
	 * Endpoints are included in the app response for easier discovery. The OAuth2
	 * spec does not have a defined place to find these (for comparison, OIDC has
	 * a '/.well-known/openid-configuration' endpoint).
	 */
	readonly endpoints: OAuth2AppEndpoints;
}

// From codersdk/oauth2.go
export interface OAuth2ProviderAppFilter {
	readonly user_id?: string;
}

// From codersdk/oauth2.go
export interface OAuth2ProviderAppSecret {
	readonly id: string;
	readonly last_used_at: string | null;
	readonly client_secret_truncated: string;
}

// From codersdk/oauth2.go
export interface OAuth2ProviderAppSecretFull {
	readonly id: string;
	readonly client_secret_full: string;
}

// From codersdk/deployment.go
/**
 * OAuth2ProviderConfig configures Coder's own OAuth 2.1 authorization server.
 * This is separate from the GitHub login integration. It is also distinct
 * from OAuth2ProviderSettings: this struct decides whether the server is on
 * at all, while OAuth2ProviderSettings holds runtime behavior such as
 * dynamic client registration that admins change while it runs.
 */
export interface OAuth2ProviderConfig {
	readonly enable: boolean;
}

// From codersdk/oauth2.go
export type OAuth2ProviderGrantType =
	| "authorization_code"
	| "client_credentials"
	| "implicit"
	| "password"
	| "refresh_token";

export const OAuth2ProviderGrantTypes: OAuth2ProviderGrantType[] = [
	"authorization_code",
	"client_credentials",
	"implicit",
	"password",
	"refresh_token",
];

// From codersdk/oauth2.go
export type OAuth2ProviderResponseType = "code" | "token";

export const OAuth2ProviderResponseTypes: OAuth2ProviderResponseType[] = [
	"code",
	"token",
];

// From codersdk/oauth2.go
/**
 * OAuth2ProviderSettings controls deployment-wide OAuth2 provider behavior.
 *
 * DynamicClientRegistrationEnabled is a pointer so a PUT can omit it to leave
 * the current value unchanged, rather than a decoded zero value silently
 * resetting it to false. This matters once a second field lands in this
 * struct (e.g. a future initial-access-token requirement): a client built
 * against an older, single-field version of this struct would otherwise
 * always encode the newer field's zero value, silently clearing it on every
 * unrelated update. GET always returns a non-nil value.
 */
export interface OAuth2ProviderSettings {
	readonly dynamic_client_registration_enabled?: boolean;
}

// From codersdk/client.go
/**
 * OAuth2RedirectCookie is the name of the cookie that stores the oauth2 redirect.
 */
export const OAuth2RedirectCookie = "oauth_redirect";

// From codersdk/client.go
/**
 * OAuth2RedirectURICookie stores the dynamically computed OIDC redirect_uri
 * when CODER_OIDC_REDIRECT_ALLOWED_HOSTS is enabled. The same value must be
 * used for both the authorization request and the token exchange (RFC 6749
 * section 4.1.3).
 */
export const OAuth2RedirectURICookie = "oauth_redirect_uri";

// From codersdk/oauth2_validation.go
/**
 * OAuth2RedirectURIMaxBytes is the longest a single redirect URI may be.
 */
export const OAuth2RedirectURIMaxBytes = 2048;

// From codersdk/oauth2_validation.go
/**
 * OAuth2RedirectURIsMaxCount is the most redirect URIs an app may register.
 */
export const OAuth2RedirectURIsMaxCount = 32;

// From codersdk/oauth2.go
export type OAuth2RevocationTokenTypeHint = "access_token" | "refresh_token";

export const OAuth2RevocationTokenTypeHints: OAuth2RevocationTokenTypeHint[] = [
	"access_token",
	"refresh_token",
];

// From codersdk/oauth2_validation.go
/**
 * OAuth2ScopeListMaxBytes bounds the length of an app's stored scope list.
 * The full public catalog fits in well under this.
 */
export const OAuth2ScopeListMaxBytes = 4096;

// From codersdk/oauth2_validation.go
/**
 * OAuth2ScopeListMaxNames bounds how many space-separated names an app's
 * scope list may hold. The public catalog is about half this size.
 */
export const OAuth2ScopeListMaxNames = 100;

// From codersdk/client.go
/**
 * OAuth2StateCookie is the name of the cookie that stores the oauth2 state.
 */
export const OAuth2StateCookie = "oauth_state";

// From codersdk/oauth2.go
export type OAuth2TokenEndpointAuthMethod =
	| "client_secret_basic"
	| "client_secret_post"
	| "none";

export const OAuth2TokenEndpointAuthMethods: OAuth2TokenEndpointAuthMethod[] = [
	"client_secret_basic",
	"client_secret_post",
	"none",
];

// From codersdk/oauth2.go
/**
 * OAuth2TokenRequest represents a token request per RFC 6749. The actual wire
 * format is application/x-www-form-urlencoded; this struct is for SDK docs.
 */
export interface OAuth2TokenRequest {
	readonly grant_type: OAuth2ProviderGrantType;
	readonly code?: string;
	readonly redirect_uri?: string;
	readonly client_id?: string;
	readonly client_secret?: string;
	readonly code_verifier?: string;
	readonly refresh_token?: string;
	readonly resource?: string;
	readonly scope?: string;
}

// From codersdk/oauth2.go
/**
 * OAuth2TokenResponse represents a successful token response per RFC 6749.
 */
export interface OAuth2TokenResponse {
	readonly access_token: string;
	readonly token_type: OAuth2TokenType;
	readonly expires_in?: number;
	readonly refresh_token?: string;
	readonly scope?: string;
	/**
	 * Expiry is not part of RFC 6749 but is included for compatibility with
	 * golang.org/x/oauth2.Token and clients that expect a timestamp.
	 */
	readonly expiry?: string;
}

// From codersdk/oauth2.go
/**
 * OAuth2TokenRevocationRequest represents a token revocation request per RFC 7009.
 */
export interface OAuth2TokenRevocationRequest {
	readonly token: string;
	readonly token_type_hint?: OAuth2RevocationTokenTypeHint;
	readonly client_id?: string;
	readonly client_secret?: string;
}

// From codersdk/oauth2.go
export type OAuth2TokenType = "Bearer" | "DPoP";

export const OAuth2TokenTypes: OAuth2TokenType[] = ["Bearer", "DPoP"];

// From codersdk/users.go
export interface OAuthConversionResponse {
	readonly state_string: string;
	readonly expires_at: string;
	readonly to_type: LoginType;
	readonly user_id: string;
}

// From codersdk/users.go
export interface OIDCAuthMethod extends AuthMethod {
	readonly signInText: string;
	readonly iconUrl: string;
}

// From codersdk/users.go
/**
 * OIDCClaimsResponse represents the merged OIDC claims for a user.
 */
export interface OIDCClaimsResponse {
	/**
	 * Claims are the merged claims from the OIDC provider. These
	 * are the union of the ID token claims and the userinfo claims,
	 * where userinfo claims take precedence on conflict.
	 */
	// empty interface{} type, falling back to unknown
	readonly claims: Record<string, unknown>;
}

// From codersdk/deployment.go
export interface OIDCConfig {
	readonly allow_signups: boolean;
	readonly client_id: string;
	readonly client_secret: string;
	/**
	 * ClientKeyFile & ClientCertFile are used in place of ClientSecret for PKI auth.
	 */
	readonly client_key_file: string;
	readonly client_cert_file: string;
	readonly email_domain: string;
	readonly issuer_url: string;
	readonly scopes: string;
	readonly ignore_email_verified: boolean;
	readonly username_field: string;
	readonly name_field: string;
	readonly email_field: string;
	readonly auth_url_params: SerpentStruct<Record<string, string>>;
	/**
	 * IgnoreUserInfo & UserInfoFromAccessToken are mutually exclusive. Only 1
	 * can be set to true. Ideally this would be an enum with 3 states, ['none',
	 * 'userinfo', 'access_token']. However, for backward compatibility,
	 * `ignore_user_info` must remain. And `access_token` is a niche, non-spec
	 * compliant edge case. So it's use is rare, and should not be advised.
	 */
	readonly ignore_user_info: boolean;
	/**
	 * UserInfoFromAccessToken as mentioned above is an edge case. This allows
	 * sourcing the user_info from the access token itself instead of a user_info
	 * endpoint. This assumes the access token is a valid JWT with a set of claims to
	 * be merged with the id_token.
	 */
	readonly source_user_info_from_access_token: boolean;
	readonly organization_field: string;
	readonly organization_mapping: SerpentStruct<Record<string, string[]>>;
	readonly organization_assign_default: boolean;
	readonly group_auto_create: boolean;
	readonly group_regex_filter: string;
	readonly group_allow_list: string;
	readonly groups_field: string;
	readonly group_mapping: SerpentStruct<Record<string, string>>;
	readonly user_role_field: string;
	readonly user_role_mapping: SerpentStruct<Record<string, string[]>>;
	readonly user_roles_default: string;
	readonly sign_in_text: string;
	readonly icon_url: string;
	readonly signups_disabled_text: string;
	readonly skip_issuer_checks: boolean;
	/**
	 * RedirectURL is optional, defaulting to 'ACCESS_URL'. Only useful in niche
	 * situations where the OIDC callback domain is different from the ACCESS_URL
	 * domain. The path component is ignored.
	 */
	readonly redirect_url: string;
	readonly auto_repair_links: boolean;
	/**
	 * EmailFallback allows OIDC logins to fall back to email-based matching
	 * when the `linked_id` (issuer+subject) does not match an existing user
	 * link. INSECURE: weakens the linked_id check. It exists for IdP
	 * brokers that do not issue a stable `sub` for the same user across
	 * connections.
	 */
	readonly email_fallback: boolean;
	/**
	 * RedirectAllowedHosts is an allowlist of hostnames that may be used as
	 * the host of the OIDC redirect_uri. When non-empty, the redirect_uri is
	 * constructed from the incoming request's Host header (validated against
	 * this list) instead of from AccessURL. Every listed host must also be
	 * registered as a valid redirect URI in the OIDC provider. This setting
	 * is mutually exclusive with RedirectURL: if RedirectURL is set, this
	 * allowlist is ignored.
	 */
	readonly redirect_allowed_hosts: string;
}

// From codersdk/parameters.go
export type OptionType = "bool" | "list(string)" | "number" | "string";

export const OptionTypes: OptionType[] = [
	"bool",
	"list(string)",
	"number",
	"string",
];

// From codersdk/organizations.go
/**
 * Organization is the JSON representation of a Coder organization.
 */
export interface Organization extends MinimalOrganization {
	readonly description: string;
	readonly created_at: string;
	readonly updated_at: string;
	readonly is_default: boolean;
	/**
	 * DefaultOrgMemberRoles are unioned into every member's effective
	 * roles at request time. Changes propagate to all members on the
	 * next request.
	 */
	readonly default_org_member_roles: readonly string[];
}

// From codersdk/aibridge.go
/**
 * OrganizationAISpendDetailsFilter narrows organization AI spend.
 */
export interface OrganizationAISpendDetailsFilter {
	readonly period_start?: string;
	readonly period_end?: string;
	readonly user_id?: string;
	readonly group_id?: string;
	readonly provider_name?: string;
	readonly model?: string;
}

// From codersdk/aibridge.go
/**
 * OrganizationAISpendFilter narrows the organization per-user AI spend
 * report. Zero values apply no filter: the period falls back to the current
 * budget period on the server, and an empty dimension matches all usage.
 */
export interface OrganizationAISpendFilter {
	/**
	 * PeriodStart and PeriodEnd bound the [PeriodStart, PeriodEnd) window and
	 * must be supplied together.
	 */
	readonly period_start?: string;
	readonly period_end?: string;
	/**
	 * ProviderName matches the configured provider name recorded on the
	 * intercepted request.
	 */
	readonly provider_name?: string;
	readonly model?: string;
	/**
	 * Client matches the client recorded on the intercepted request. Unknown
	 * matches usage without a recorded client.
	 */
	readonly client?: string;
}

// From codersdk/aibridge.go
/**
 * OrganizationAISpendPage selects one page of the per-user report, which
 * pages by offset only. A zero Limit uses the server default.
 */
export interface OrganizationAISpendPage {
	readonly limit?: number;
	readonly offset?: number;
}

// From codersdk/aibridge.go
/**
 * OrganizationAISpendReport is one page of per-user AI spend for an
 * organization over the applied period. Count and Totals cover every
 * matching user, not only the returned page.
 */
export interface OrganizationAISpendReport extends AISpendPeriodWindow {
	/**
	 * RetentionStart is the oldest instant for which token usage is still
	 * retained. An explicit period must not start before it. Omitted when the
	 * deployment does not purge AI Gateway data.
	 */
	readonly retention_start?: string;
	/**
	 * Count is the number of users with token usage matching the filter.
	 */
	readonly count: number;
	readonly totals: OrganizationAISpendTotals;
	/**
	 * Users is the requested page, most expensive first.
	 */
	readonly users: readonly OrganizationAISpendUser[];
}

// From codersdk/aibridge.go
/**
 * OrganizationAISpendTotals aggregates every user matching the report's
 * filter, not only the returned page.
 */
export interface OrganizationAISpendTotals {
	/**
	 * CostMicros is the priced spend of every matching user.
	 */
	readonly cost_micros: number;
	/**
	 * UnpricedUsageCount is the number of token usage records without a cost
	 * across every matching user.
	 */
	readonly unpriced_usage_count: number;
}

// From codersdk/aibridge.go
/**
 * OrganizationAISpendUser is one user's AI spend within an organization
 * report.
 */
export interface OrganizationAISpendUser {
	readonly user_id: string;
	readonly username: string;
	readonly name: string;
	readonly avatar_url: string;
	/**
	 * CostMicros is the user's priced spend over the period.
	 */
	readonly cost_micros: number;
	/**
	 * UnpricedUsageCount is the number of the user's token usage records that
	 * carry no cost because their model had no price when they were recorded.
	 */
	readonly unpriced_usage_count: number;
	/**
	 * Providers are the configured provider names the user spent through,
	 * sorted.
	 */
	readonly providers: readonly string[];
	/**
	 * Clients are the clients the user spent through, sorted. Usage without a
	 * recorded client is reported as Unknown.
	 */
	readonly clients: readonly string[];
	/**
	 * Models are the models the user spent through, sorted.
	 */
	readonly models: readonly string[];
}

// From codersdk/chats.go
/**
 * OrganizationChatModelsResponse is the org chat model config collection:
 * the caller-readable configs plus the redacted provider descriptors the
 * authoring page needs.
 */
export interface OrganizationChatModelsResponse {
	readonly models: readonly ChatModel[];
	readonly providers: readonly ChatModelProviderDescriptor[];
	readonly unsupported_providers: readonly ChatUnsupportedProvider[];
}

// From codersdk/chats.go
/**
 * OrganizationChatSystemPromptResponse is the response body for the
 * organization chat system prompt endpoint.
 */
export interface OrganizationChatSystemPromptResponse {
	readonly system_prompt: string;
}

// From codersdk/aibridge.go
/**
 * OrganizationGroupAISpend is the current AI spend snapshot for a group
 * within the active budget period.
 */
export interface OrganizationGroupAISpend {
	readonly group_id: string;
	/**
	 * SpendLimitMicros is the group's configured AI spend budget per member.
	 * Null when the group has no configured budget.
	 */
	readonly spend_limit_micros: number | null;
	/**
	 * TotalSpendLimitMicros is the currently configured combined budget of the
	 * members attributed to this group, with each member's override replacing
	 * their share. Null when the group has no budget, and zero when no members
	 * are attributed to it.
	 */
	readonly total_spend_limit_micros: number | null;
	/**
	 * CurrentSpendMicros is the group's spend over the current budget period.
	 */
	readonly current_spend_micros: number;
}

// From codersdk/aibridge.go
/**
 * OrganizationGroupsAISpend reports AI spend for a set of groups in the
 * active budget period.
 */
export interface OrganizationGroupsAISpend extends AISpendPeriodWindow {
	readonly groups: readonly OrganizationGroupAISpend[];
}

// From codersdk/organizations.go
export interface OrganizationMember {
	readonly user_id: string;
	readonly organization_id: string;
	readonly created_at: string;
	readonly updated_at: string;
	readonly roles: readonly SlimRole[];
}

// From codersdk/organizations.go
export interface OrganizationMemberWithUserData extends OrganizationMember {
	readonly username: string;
	readonly name?: string;
	readonly avatar_url?: string;
	readonly email: string;
	readonly status: UserStatus;
	readonly login_type: LoginType;
	readonly last_seen_at?: string;
	readonly user_created_at: string;
	readonly user_updated_at: string;
	readonly is_service_account?: boolean;
	readonly global_roles: readonly SlimRole[];
	/**
	 * HasAISeat intentionally omits omitempty so the API always includes the
	 * field, even when false.
	 */
	readonly has_ai_seat: boolean;
}

// From codersdk/users.go
export interface OrganizationMembersQuery {
	readonly UserID: string;
	readonly IncludeSystem: boolean;
	readonly GithubUserID: number;
}

// From codersdk/organizations.go
export interface OrganizationProvisionerDaemonsOptions {
	readonly Limit: number;
	readonly Offline: boolean;
	readonly Status: readonly ProvisionerDaemonStatus[];
	readonly MaxAge: number;
	readonly IDs: readonly string[];
	readonly Tags: Record<string, string>;
}

// From codersdk/organizations.go
export interface OrganizationProvisionerJobsOptions {
	readonly Limit: number;
	readonly IDs: readonly string[];
	readonly Status: readonly ProvisionerJobStatus[];
	readonly Tags: Record<string, string>;
	readonly Initiator: string;
}

// From codersdk/skills.go
/**
 * OrganizationSkillACL is the resolved access control list of an
 * organization skill.
 */
export interface OrganizationSkillACL {
	readonly users: readonly OrganizationSkillUser[];
	readonly groups: readonly OrganizationSkillGroup[];
}

// From codersdk/skills.go
/**
 * OrganizationSkillGroup is a group entry in an organization skill ACL.
 */
export interface OrganizationSkillGroup extends Group {
	readonly role: OrganizationSkillRole;
}

// From codersdk/skills.go
/**
 * OrganizationSkillPreferences is an organization member's choices about the
 * organization skills that load into their chats. The choices apply only
 * while MembersCanDisableSkills is true.
 */
export interface OrganizationSkillPreferences {
	/**
	 * MembersCanDisableSkills is the organization's setting.
	 */
	readonly members_can_disable_skills: boolean;
	/**
	 * AllSkillsDisabled turns off every organization skill, including
	 * skills added later. DisabledSkillIDs is kept and applies again when it
	 * is false.
	 */
	readonly all_skills_disabled: boolean;
	/**
	 * DisabledSkillIDs lists the organization skills the member turned off.
	 */
	readonly disabled_skill_ids: readonly string[];
}

// From codersdk/skills.go
export type OrganizationSkillRole = "" | "read";

export const OrganizationSkillRoles: OrganizationSkillRole[] = ["", "read"];

// From codersdk/skills.go
/**
 * OrganizationSkillSettings is an organization's member opt-out setting for
 * organization skills.
 */
export interface OrganizationSkillSettings {
	/**
	 * MembersCanDisableSkills lets each organization member turn off
	 * organization skills for their own chats.
	 */
	readonly members_can_disable_skills: boolean;
}

// From codersdk/skills.go
/**
 * OrganizationSkillUser is a user entry in an organization skill ACL.
 */
export interface OrganizationSkillUser extends MinimalUser {
	readonly role: OrganizationSkillRole;
}

// From codersdk/idpsync.go
export interface OrganizationSyncSettings {
	/**
	 * Field selects the claim field to be used as the created user's
	 * organizations. If the field is the empty string, then no organization
	 * updates will ever come from the OIDC provider.
	 */
	readonly field: string;
	/**
	 * Mapping maps from an OIDC claim --> Coder organization uuid
	 */
	readonly mapping: Record<string, string[]>;
	/**
	 * AssignDefault will ensure the default org is always included
	 * for every user, regardless of their claims. This preserves legacy behavior.
	 */
	readonly organization_assign_default: boolean;
}

// From codersdk/groups.go
/**
 * PaginatedGroup is a group summary returned by the paginated groups endpoint.
 * It deliberately omits the member roster (which the endpoint does not return)
 * and exposes only the total member count. Fetch the roster via the group
 * members endpoint.
 */
export interface PaginatedGroup {
	readonly id: string;
	readonly name: string;
	readonly display_name: string;
	readonly organization_id: string;
	/**
	 * TotalMemberCount is the number of members in the group, shown even when
	 * the caller cannot read individual members. The roster itself is not
	 * returned by this endpoint.
	 */
	readonly total_member_count: number;
	readonly avatar_url: string;
	readonly quota_allowance: number;
	readonly source: GroupSource;
	readonly organization_name: string;
	readonly organization_display_name: string;
}

// From codersdk/groups.go
/**
 * PaginatedGroupsRequest are the filters for a paginated groups request.
 * Groups only support free-text search, so unlike UsersRequest it exposes no
 * key:value filters that the endpoint would reject.
 */
export interface PaginatedGroupsRequest extends Pagination {
	readonly q?: string;
}

// From codersdk/groups.go
export interface PaginatedGroupsResponse {
	readonly groups: readonly PaginatedGroup[];
	readonly count: number;
}

// From codersdk/organizations.go
export interface PaginatedMembersRequest {
	readonly limit?: number;
	readonly offset?: number;
}

// From codersdk/organizations.go
export interface PaginatedMembersResponse {
	readonly members: readonly OrganizationMemberWithUserData[];
	readonly count: number;
}

// From codersdk/pagination.go
/**
 * Pagination sets pagination options for the endpoints that support it.
 */
export interface Pagination {
	/**
	 * AfterID returns all or up to Limit results after the given
	 * UUID. This option can be used with or as an alternative to
	 * Offset for better performance. To use it as an alternative,
	 * set AfterID to the last UUID returned by the previous
	 * request.
	 */
	readonly after_id?: string;
	/**
	 * Limit sets the maximum number of users to be returned
	 * in a single page. If the limit is <= 0, there is no limit
	 * and all users are returned.
	 */
	readonly limit?: number;
	/**
	 * Offset is used to indicate which page to return. An offset of 0
	 * returns the first 'limit' number of users.
	 * To get the next page, use offset=<limit>*<page_number>.
	 * Offset is 0 indexed, so the first record sits at offset 0.
	 */
	readonly offset?: number;
}

// From codersdk/parameters.go
export type ParameterFormType =
	| "checkbox"
	| ""
	| "dropdown"
	| "error"
	| "input"
	| "multi-select"
	| "radio"
	| "slider"
	| "switch"
	| "tag-select"
	| "textarea";

export const ParameterFormTypes: ParameterFormType[] = [
	"checkbox",
	"",
	"dropdown",
	"error",
	"input",
	"multi-select",
	"radio",
	"slider",
	"switch",
	"tag-select",
	"textarea",
];

// From codersdk/idpsync.go
export interface PatchGroupIDPSyncConfigRequest {
	readonly field: string;
	readonly regex_filter: string | null;
	readonly auto_create_missing_groups: boolean;
}

// From codersdk/idpsync.go
/**
 * If the same mapping is present in both Add and Remove, Remove will take presidence.
 */
export interface PatchGroupIDPSyncMappingRequest {
	readonly Add: readonly IDPSyncMapping<string>[];
	readonly Remove: readonly IDPSyncMapping<string>[];
}

// From codersdk/groups.go
export interface PatchGroupRequest {
	readonly add_users: readonly string[];
	readonly remove_users: readonly string[];
	readonly name: string;
	readonly display_name: string | null;
	readonly avatar_url: string | null;
	readonly quota_allowance: number | null;
}

// From codersdk/idpsync.go
export interface PatchOrganizationIDPSyncConfigRequest {
	readonly field: string;
	readonly assign_default: boolean;
}

// From codersdk/idpsync.go
/**
 * If the same mapping is present in both Add and Remove, Remove will take presidence.
 */
export interface PatchOrganizationIDPSyncMappingRequest {
	readonly Add: readonly IDPSyncMapping<string>[];
	readonly Remove: readonly IDPSyncMapping<string>[];
}

// From codersdk/idpsync.go
export interface PatchRoleIDPSyncConfigRequest {
	readonly field: string;
}

// From codersdk/idpsync.go
/**
 * If the same mapping is present in both Add and Remove, Remove will take presidence.
 */
export interface PatchRoleIDPSyncMappingRequest {
	readonly Add: readonly IDPSyncMapping<string>[];
	readonly Remove: readonly IDPSyncMapping<string>[];
}

// From codersdk/templateversions.go
export interface PatchTemplateVersionRequest {
	readonly name: string;
	readonly message?: string;
}

// From codersdk/workspaceproxy.go
export interface PatchWorkspaceProxy {
	readonly id: string;
	readonly name: string;
	readonly display_name: string;
	readonly icon: string;
	readonly regenerate_token: boolean;
}

// From codersdk/client.go
/**
 * PathAppSessionTokenCookie is the name of the cookie that stores an
 * application-scoped API token on workspace proxy path app domains.
 *nolint:gosec
 */
export const PathAppSessionTokenCookie = "coder_path_app_session_token";

// From codersdk/roles.go
/**
 * Permission is the format passed into the rego.
 */
export interface Permission {
	/**
	 * Negate makes this a negative permission
	 */
	readonly negate: boolean;
	readonly resource_type: RBACResource;
	readonly action: RBACAction;
}

// From codersdk/oauth2.go
export interface PostOAuth2ProviderAppRequest {
	readonly name: string;
	/**
	 * RedirectURIs is the ordered list of URIs the app may redirect to. The
	 * first entry is the primary. Required, unless the deprecated
	 * callback_url is sent instead.
	 */
	readonly redirect_uris?: readonly string[];
	/**
	 * @deprecated send redirect_uris instead. If both are sent, callback_url
	 * must equal the first entry of redirect_uris.
	 */
	readonly callback_url?: string;
	readonly icon: string;
	/**
	 * Scope is the space-separated list of scopes this app's tokens may be
	 * granted. Leave empty, or omit, for unrestricted.
	 */
	readonly scope?: string;
}

// From codersdk/workspaces.go
export interface PostWorkspaceUsageRequest {
	readonly agent_id: string;
	/**
	 * AppName is any name for the app reporting usage. The server normalizes
	 * it at ingestion, so a new app needs no server change. The UsageAppName
	 * constants are the well-known names.
	 */
	readonly app_name: string;
}

// From codersdk/deployment.go
export type PostgresAuth = "awsiamrds" | "password";

export const PostgresAuths: PostgresAuth[] = ["awsiamrds", "password"];

// From codersdk/deployment.go
/**
 * PostgresConnMaxIdleAuto is the value for auto-computing max idle connections
 * based on max open connections.
 */
export const PostgresConnMaxIdleAuto = "auto";

// From codersdk/deployment.go
export interface PprofConfig {
	readonly enable: boolean;
	readonly address: string;
}

// From codersdk/deployment.go
export interface PrebuildsConfig {
	/**
	 * ReconciliationInterval defines how often the workspace prebuilds state should be reconciled.
	 */
	readonly reconciliation_interval: number;
	/**
	 * ReconciliationBackoffInterval specifies the amount of time to increase the backoff interval
	 * when errors occur during reconciliation.
	 */
	readonly reconciliation_backoff_interval: number;
	/**
	 * ReconciliationBackoffLookback determines the time window to look back when calculating
	 * the number of failed prebuilds, which influences the backoff strategy.
	 */
	readonly reconciliation_backoff_lookback: number;
	/**
	 * FailureHardLimit defines the maximum number of consecutive failed prebuild attempts allowed
	 * before a preset is considered to be in a hard limit state. When a preset hits this limit,
	 * no new prebuilds will be created until the limit is reset.
	 * FailureHardLimit is disabled when set to zero.
	 */
	readonly failure_hard_limit: number;
}

// From codersdk/prebuilds.go
export interface PrebuildsSettings {
	readonly reconciliation_paused: boolean;
}

// From codersdk/prebuilds.go
/**
 * PrebuildsSystemUserID is the UUID of the Coder prebuilds system
 * user. Prebuilt workspaces are owned by this user until they are
 * claimed; build #1 of a claimed workspace remains attributed to
 * this user as the initiator forever, which is how callers can
 * recognize a prebuild claim after the fact.
 */
export const PrebuildsSystemUserID = "c42fdf75-3097-471c-8c33-fb52454d81c0";

// From codersdk/premiumfunnel.go
/**
 * PremiumFunnelEventRequest is the request body for
 * POST /api/v2/deployment/premium-funnel-events.
 */
export interface PremiumFunnelEventRequest {
	/**
	 * ID identifies this click, and doubles as the attribution token that a
	 * later trial signup reports.
	 */
	readonly id: string;
	readonly source: PremiumFunnelSource;
	readonly variant: PremiumFunnelVariant;
}

// From codersdk/premiumfunnel.go
export type PremiumFunnelSource =
	| "aibridge_session_threads"
	| "aibridge_sessions"
	| "ai_gateway_keys"
	| "ai_governance"
	| "appearance"
	| "audit_log"
	| "browser_only"
	| "connection_log"
	| "custom_roles"
	| "direct"
	| "external_auth"
	| "groups"
	| "idp_org_sync"
	| "idp_sync"
	| "multiple_organizations"
	| "observability"
	| "provisioner_keys"
	| "provisioners"
	| "template_permissions"
	| "workspace_proxies";

export const PremiumFunnelSources: PremiumFunnelSource[] = [
	"aibridge_session_threads",
	"aibridge_sessions",
	"ai_gateway_keys",
	"ai_governance",
	"appearance",
	"audit_log",
	"browser_only",
	"connection_log",
	"custom_roles",
	"direct",
	"external_auth",
	"groups",
	"idp_org_sync",
	"idp_sync",
	"multiple_organizations",
	"observability",
	"provisioner_keys",
	"provisioners",
	"template_permissions",
	"workspace_proxies",
];

// From codersdk/premiumfunnel.go
export type PremiumFunnelVariant = "ai_governance" | "premium" | "small";

export const PremiumFunnelVariants: PremiumFunnelVariant[] = [
	"ai_governance",
	"premium",
	"small",
];

// From codersdk/presets.go
export interface Preset {
	readonly ID: string;
	readonly Name: string;
	readonly Parameters: readonly PresetParameter[];
	readonly Default: boolean;
	readonly DesiredPrebuildInstances: number | null;
	readonly Description: string;
	readonly Icon: string;
}

// From codersdk/presets.go
export interface PresetParameter {
	readonly Name: string;
	readonly Value: string;
}

// From codersdk/parameters.go
export interface PreviewParameter extends PreviewParameterData {
	readonly value: NullHCLString;
	readonly diagnostics: readonly FriendlyDiagnostic[];
}

// From codersdk/parameters.go
export interface PreviewParameterData {
	readonly name: string;
	readonly display_name: string;
	readonly description: string;
	readonly type: OptionType;
	readonly form_type: ParameterFormType;
	readonly styling: PreviewParameterStyling;
	readonly mutable: boolean;
	readonly default_value: NullHCLString;
	readonly icon: string;
	readonly options: readonly PreviewParameterOption[];
	readonly validations: readonly PreviewParameterValidation[];
	readonly required: boolean;
	/**
	 * legacy_variable_name was removed (= 14)
	 */
	readonly order: number;
	readonly ephemeral: boolean;
}

// From codersdk/parameters.go
export interface PreviewParameterOption {
	readonly name: string;
	readonly description: string;
	readonly value: NullHCLString;
	readonly icon: string;
}

// From codersdk/parameters.go
export interface PreviewParameterStyling {
	readonly placeholder?: string;
	readonly disabled?: boolean;
	readonly label?: string;
	readonly mask_input?: boolean;
}

// From codersdk/parameters.go
export interface PreviewParameterValidation {
	readonly validation_error: string;
	/**
	 * All validation attributes are optional.
	 */
	readonly validation_regex: string | null;
	readonly validation_min: number | null;
	readonly validation_max: number | null;
	readonly validation_monotonic: string | null;
}

// From codersdk/deployment.go
export interface PrometheusConfig {
	readonly enable: boolean;
	readonly address: string;
	readonly collect_agent_stats: boolean;
	readonly collect_db_metrics: boolean;
	readonly aggregate_agent_stats_by: string;
}

// From codersdk/chats.go
/**
 * ProposeChatTitleResponse is returned by the propose-title endpoint.
 */
export interface ProposeChatTitleResponse {
	readonly title: string;
}

// From codersdk/deployment.go
export interface ProvisionerConfig {
	/**
	 * Daemons is the number of built-in terraform provisioners.
	 */
	readonly daemons: number;
	readonly daemon_types: string;
	readonly daemon_poll_interval: number;
	readonly daemon_poll_jitter: number;
	readonly force_cancel_interval: number;
	readonly daemon_psk: string;
	/**
	 * DisableModuleCache disables the reuse of Terraform modules cached at
	 * template import for every template in the deployment. Templates cannot
	 * opt back in.
	 */
	readonly disable_module_cache: boolean;
}

// From codersdk/provisionerdaemons.go
export interface ProvisionerDaemon {
	readonly id: string;
	readonly organization_id: string;
	readonly key_id: string;
	readonly created_at: string;
	readonly last_seen_at?: string;
	readonly name: string;
	readonly version: string;
	readonly api_version: string;
	readonly provisioners: readonly ProvisionerType[];
	readonly tags: Record<string, string>;
	/**
	 * Optional fields.
	 */
	readonly key_name: string | null;
	readonly status: ProvisionerDaemonStatus | null;
	readonly current_job: ProvisionerDaemonJob | null;
	readonly previous_job: ProvisionerDaemonJob | null;
}

// From codersdk/provisionerdaemons.go
export interface ProvisionerDaemonJob {
	readonly id: string;
	readonly status: ProvisionerJobStatus;
	readonly template_name: string;
	readonly template_icon: string;
	readonly template_display_name: string;
}

// From codersdk/client.go
/**
 * ProvisionerDaemonKey contains the authentication key for an external provisioner daemon
 */
export const ProvisionerDaemonKey = "Coder-Provisioner-Daemon-Key";

// From codersdk/client.go
/**
 * ProvisionerDaemonPSK contains the authentication pre-shared key for an external provisioner daemon
 */
export const ProvisionerDaemonPSK = "Coder-Provisioner-Daemon-PSK";

// From codersdk/provisionerdaemons.go
export type ProvisionerDaemonStatus = "busy" | "idle" | "offline";

export const ProvisionerDaemonStatuses: ProvisionerDaemonStatus[] = [
	"busy",
	"idle",
	"offline",
];

// From healthsdk/healthsdk.go
/**
 * ProvisionerDaemonsReport includes health details of each connected provisioner daemon.
 */
export interface ProvisionerDaemonsReport extends BaseReport {
	readonly items: readonly ProvisionerDaemonsReportItem[];
}

// From healthsdk/healthsdk.go
export interface ProvisionerDaemonsReportItem {
	readonly provisioner_daemon: ProvisionerDaemon;
	readonly warnings: readonly HealthMessage[];
}

// From codersdk/provisionerdaemons.go
/**
 * ProvisionerJob describes the job executed by the provisioning daemon.
 */
export interface ProvisionerJob {
	readonly id: string;
	readonly created_at: string;
	readonly started_at?: string;
	readonly completed_at?: string;
	readonly canceled_at?: string;
	readonly error?: string;
	readonly error_code?: JobErrorCode;
	readonly status: ProvisionerJobStatus;
	readonly worker_id?: string;
	readonly worker_name?: string;
	readonly file_id: string;
	readonly tags: Record<string, string>;
	readonly queue_position: number;
	readonly queue_size: number;
	readonly organization_id: string;
	readonly initiator_id: string;
	readonly input: ProvisionerJobInput;
	readonly type: ProvisionerJobType;
	readonly available_workers?: readonly string[];
	readonly metadata: ProvisionerJobMetadata;
	readonly logs_overflowed: boolean;
}

// From codersdk/provisionerdaemons.go
/**
 * ProvisionerJobInput represents the input for the job.
 */
export interface ProvisionerJobInput {
	readonly template_version_id?: string;
	readonly workspace_build_id?: string;
	readonly error?: string;
}

// From codersdk/provisionerdaemons.go
/**
 * ProvisionerJobLog represents the provisioner log entry annotated with source and level.
 */
export interface ProvisionerJobLog {
	readonly id: number;
	readonly created_at: string;
	readonly log_source: LogSource;
	readonly log_level: LogLevel;
	readonly stage: string;
	readonly output: string;
}

// From codersdk/provisionerdaemons.go
/**
 * ProvisionerJobMetadata contains metadata for the job.
 */
export interface ProvisionerJobMetadata {
	readonly template_version_name: string;
	readonly template_id: string;
	readonly template_name: string;
	readonly template_display_name: string;
	readonly template_icon: string;
	readonly workspace_id?: string;
	readonly workspace_name?: string;
	readonly workspace_build_transition?: WorkspaceTransition;
}

// From codersdk/provisionerdaemons.go
export type ProvisionerJobStatus =
	| "canceled"
	| "canceling"
	| "failed"
	| "pending"
	| "running"
	| "succeeded"
	| "unknown";

export const ProvisionerJobStatuses: ProvisionerJobStatus[] = [
	"canceled",
	"canceling",
	"failed",
	"pending",
	"running",
	"succeeded",
	"unknown",
];

// From codersdk/provisionerdaemons.go
export type ProvisionerJobType =
	| "template_version_dry_run"
	| "template_version_import"
	| "workspace_build";

export const ProvisionerJobTypes: ProvisionerJobType[] = [
	"template_version_dry_run",
	"template_version_import",
	"workspace_build",
];

// From codersdk/provisionerdaemons.go
export interface ProvisionerKey {
	readonly id: string;
	readonly created_at: string;
	readonly organization: string;
	readonly name: string;
	readonly tags: ProvisionerKeyTags;
}

// From codersdk/provisionerdaemons.go
export interface ProvisionerKeyDaemons {
	readonly key: ProvisionerKey;
	readonly daemons: readonly ProvisionerDaemon[];
}

// From codersdk/provisionerdaemons.go
export const ProvisionerKeyIDBuiltIn = "00000000-0000-0000-0000-000000000001";

// From codersdk/provisionerdaemons.go
export const ProvisionerKeyIDPSK = "00000000-0000-0000-0000-000000000003";

// From codersdk/provisionerdaemons.go
export const ProvisionerKeyIDUserAuth = "00000000-0000-0000-0000-000000000002";

// From codersdk/provisionerdaemons.go
export const ProvisionerKeyNameBuiltIn = "built-in";

// From codersdk/provisionerdaemons.go
export const ProvisionerKeyNamePSK = "psk";

// From codersdk/provisionerdaemons.go
export const ProvisionerKeyNameUserAuth = "user-auth";

// From codersdk/provisionerdaemons.go
export type ProvisionerKeyTags = Record<string, string>;

// From codersdk/workspaces.go
export type ProvisionerLogLevel = "debug";

export const ProvisionerLogLevels: ProvisionerLogLevel[] = ["debug"];

// From codersdk/organizations.go
export type ProvisionerStorageMethod = "file";

export const ProvisionerStorageMethods: ProvisionerStorageMethod[] = ["file"];

// From codersdk/workspacebuilds.go
export interface ProvisionerTiming {
	readonly job_id: string;
	readonly started_at: string;
	readonly ended_at: string;
	readonly stage: TimingStage;
	readonly source: string;
	readonly action: string;
	readonly resource: string;
}

// From codersdk/organizations.go
export type ProvisionerType = "echo" | "terraform";

export const ProvisionerTypes: ProvisionerType[] = ["echo", "terraform"];

// From codersdk/workspaceproxy.go
/**
 * ProxyHealthReport is a report of the health of the workspace proxy.
 * A healthy report will have no errors. Warnings are not fatal.
 */
export interface ProxyHealthReport {
	/**
	 * Errors are problems that prevent the workspace proxy from being healthy
	 */
	readonly errors: readonly string[];
	/**
	 * Warnings do not prevent the workspace proxy from being healthy, but
	 * should be addressed.
	 */
	readonly warnings: readonly string[];
}

// From codersdk/workspaceproxy.go
export type ProxyHealthStatus =
	| "ok"
	| "unhealthy"
	| "unreachable"
	| "unregistered";

export const ProxyHealthStatuses: ProxyHealthStatus[] = [
	"ok",
	"unhealthy",
	"unreachable",
	"unregistered",
];

// From codersdk/experimentrules.go
/**
 * PutExperimentRuleRequest replaces the runtime rule of one experiment.
 */
export interface PutExperimentRuleRequest {
	readonly mode: ExperimentRuleMode;
	/**
	 * Condition is required for the condition mode and must be empty
	 * otherwise.
	 */
	readonly condition?: string;
	/**
	 * ExpectedRevision must equal the current revision of the stored rule,
	 * or zero when no rule is stored or the stored rule has no readable
	 * positive revision. A different revision fails with 409 Conflict.
	 */
	readonly expected_revision: number;
}

// From codersdk/workspaces.go
/**
 * PutExtendWorkspaceRequest is a request to extend the deadline of
 * the active workspace build.
 */
export interface PutExtendWorkspaceRequest {
	readonly deadline: string;
}

// From codersdk/oauth2.go
export interface PutOAuth2ProviderAppRequest {
	readonly name: string;
	/**
	 * RedirectURIs is the ordered list of URIs the app may redirect to. The
	 * first entry is the primary. Omit both this and callback_url to keep the
	 * stored redirect URIs. Other fields are replaced. Sending an empty list
	 * is an error, not a way to keep the stored list.
	 */
	readonly redirect_uris?: readonly string[];
	/**
	 * @deprecated send redirect_uris instead. If both are sent, callback_url
	 * must equal the first entry of redirect_uris.
	 */
	readonly callback_url?: string;
	/**
	 * Icon replaces the app's stored icon. Omitting it clears the stored
	 * icon rather than leaving it unchanged.
	 */
	readonly icon: string;
	/**
	 * Scope replaces the app's current allowlist. Omit to leave the existing
	 * allowlist untouched. Set to an empty string to clear it, making the app
	 * unrestricted.
	 */
	readonly scope?: string;
}

// From codersdk/rbacresources_gen.go
export type RBACAction =
	| "application_connect"
	| "assign"
	| "create"
	| "create_agent"
	| "delete"
	| "delete_agent"
	| "read"
	| "read_personal"
	| "read_secret"
	| "ssh"
	| "share"
	| "unassign"
	| "update"
	| "update_agent"
	| "update_personal"
	| "use"
	| "view_insights"
	| "start"
	| "stop";

export const RBACActions: RBACAction[] = [
	"application_connect",
	"assign",
	"create",
	"create_agent",
	"delete",
	"delete_agent",
	"read",
	"read_personal",
	"read_secret",
	"ssh",
	"share",
	"unassign",
	"update",
	"update_agent",
	"update_personal",
	"use",
	"view_insights",
	"start",
	"stop",
];

// From codersdk/rbacresources_gen.go
export type RBACResource =
	| "ai_gateway_key"
	| "ai_provider"
	| "ai_model_price"
	| "ai_seat"
	| "aibridge_interception"
	| "api_key"
	| "assign_org_role"
	| "assign_role"
	| "audit_log"
	| "boundary_log"
	| "boundary_usage"
	| "chat"
	| "chat_model_config"
	| "chat_project"
	| "chat_project_memory"
	| "connection_log"
	| "crypto_key"
	| "debug_info"
	| "deployment_config"
	| "deployment_stats"
	| "file"
	| "group"
	| "group_member"
	| "idpsync_settings"
	| "inbox_notification"
	| "license"
	| "mcp_server_config"
	| "notification_message"
	| "notification_preference"
	| "notification_template"
	| "oauth2_app"
	| "oauth2_app_code_token"
	| "oauth2_app_secret"
	| "organization"
	| "organization_member"
	| "organization_skill"
	| "prebuilt_workspace"
	| "provisioner_daemon"
	| "provisioner_jobs"
	| "replicas"
	| "system"
	| "tailnet_coordinator"
	| "template"
	| "usage_event"
	| "user"
	| "user_secret"
	| "user_skill"
	| "webpush_subscription"
	| "*"
	| "workspace"
	| "workspace_agent_devcontainers"
	| "workspace_agent_resource_monitor"
	| "workspace_build_orchestration"
	| "workspace_dormant"
	| "workspace_proxy"
	| "workspace_secret";

export const RBACResources: RBACResource[] = [
	"ai_gateway_key",
	"ai_provider",
	"ai_model_price",
	"ai_seat",
	"aibridge_interception",
	"api_key",
	"assign_org_role",
	"assign_role",
	"audit_log",
	"boundary_log",
	"boundary_usage",
	"chat",
	"chat_model_config",
	"chat_project",
	"chat_project_memory",
	"connection_log",
	"crypto_key",
	"debug_info",
	"deployment_config",
	"deployment_stats",
	"file",
	"group",
	"group_member",
	"idpsync_settings",
	"inbox_notification",
	"license",
	"mcp_server_config",
	"notification_message",
	"notification_preference",
	"notification_template",
	"oauth2_app",
	"oauth2_app_code_token",
	"oauth2_app_secret",
	"organization",
	"organization_member",
	"organization_skill",
	"prebuilt_workspace",
	"provisioner_daemon",
	"provisioner_jobs",
	"replicas",
	"system",
	"tailnet_coordinator",
	"template",
	"usage_event",
	"user",
	"user_secret",
	"user_skill",
	"webpush_subscription",
	"*",
	"workspace",
	"workspace_agent_devcontainers",
	"workspace_agent_resource_monitor",
	"workspace_build_orchestration",
	"workspace_dormant",
	"workspace_proxy",
	"workspace_secret",
];

// From codersdk/deployment.go
export interface RateLimitConfig {
	readonly disable_all: boolean;
	readonly api: number;
}

// From codersdk/users.go
/**
 * ReducedUser omits role and organization information. Roles are deduced from
 * the user's site and organization roles. This requires fetching the user's
 * organizational memberships. Fetching that is more expensive, and not usually
 * required by the frontend.
 */
export interface ReducedUser extends MinimalUser {
	readonly email: string;
	readonly created_at: string;
	readonly updated_at: string;
	readonly last_seen_at?: string;
	readonly status: UserStatus;
	readonly login_type: LoginType;
	readonly is_service_account?: boolean;
	/**
	 * @deprecated this value should be retrieved from
	 * `codersdk.UserPreferenceSettings` instead.
	 */
	readonly theme_preference?: string;
}

// From codersdk/workspaceproxy.go
export interface Region {
	readonly id: string;
	readonly name: string;
	readonly display_name: string;
	readonly icon_url: string;
	readonly healthy: boolean;
	/**
	 * PathAppURL is the URL to the base path for path apps. Optional
	 * unless wildcard_hostname is set.
	 * E.g. https://us.example.com
	 */
	readonly path_app_url: string;
	/**
	 * WildcardHostname is the wildcard hostname for subdomain apps.
	 * E.g. *.us.example.com
	 * E.g. *--suffix.au.example.com
	 * Optional. Does not need to be on the same domain as PathAppURL.
	 */
	readonly wildcard_hostname: string;
}

// From codersdk/workspaceproxy.go
export type RegionTypes = Region | WorkspaceProxy;

// From codersdk/workspaceproxy.go
export interface RegionsResponse<R extends RegionTypes> {
	readonly regions: readonly R[];
}

// From codersdk/replicas.go
export interface Replica {
	/**
	 * ID is the unique identifier for the replica.
	 */
	readonly id: string;
	/**
	 * Hostname is the hostname of the replica.
	 */
	readonly hostname: string;
	/**
	 * CreatedAt is the timestamp when the replica was first seen.
	 */
	readonly created_at: string;
	/**
	 * RelayAddress is the accessible address to relay DERP connections.
	 */
	readonly relay_address: string;
	/**
	 * RegionID is the region of the replica.
	 */
	readonly region_id: number;
	/**
	 * Error is the replica error.
	 */
	readonly error: string;
	/**
	 * DatabaseLatency is the latency in microseconds to the database.
	 */
	readonly database_latency: number;
}

// From codersdk/users.go
/**
 * RequestOneTimePasscodeRequest enables callers to request a one-time-passcode to change their password.
 */
export interface RequestOneTimePasscodeRequest {
	readonly email: string;
}

// From codersdk/workspaces.go
export interface ResolveAutostartResponse {
	readonly parameter_mismatch: boolean;
}

// From codersdk/audit.go
export type ResourceType =
	| "ai_gateway_key"
	| "ai_provider"
	| "ai_provider_key"
	| "ai_seat"
	| "api_key"
	| "chat"
	| "chat_automation"
	| "chat_instruction_settings"
	| "chat_model_config"
	| "chat_operational_settings"
	| "chat_organization_system_prompt"
	| "chat_project"
	| "chat_project_instructions"
	| "chat_project_memory"
	| "chat_project_skill"
	| "convert_login"
	| "custom_role"
	| "experiment_rule"
	| "git_ssh_key"
	| "group"
	| "group_ai_budget"
	| "health_settings"
	| "idp_sync_settings_group"
	| "idp_sync_settings_organization"
	| "idp_sync_settings_role"
	| "license"
	| "mcp_server_config"
	| "notification_template"
	| "notifications_settings"
	| "oauth2_provider_app"
	| "oauth2_provider_app_secret"
	| "oauth2_provider_settings"
	| "organization"
	| "organization_member"
	| "organization_skill"
	| "prebuilds_settings"
	| "task"
	| "template"
	| "template_version"
	| "user"
	| "user_ai_budget_override"
	| "user_secret"
	| "user_skill"
	| "workspace"
	| "workspace_agent"
	| "workspace_app"
	| "workspace_build"
	| "workspace_proxy";

export const ResourceTypes: ResourceType[] = [
	"ai_gateway_key",
	"ai_provider",
	"ai_provider_key",
	"ai_seat",
	"api_key",
	"chat",
	"chat_automation",
	"chat_instruction_settings",
	"chat_model_config",
	"chat_operational_settings",
	"chat_organization_system_prompt",
	"chat_project",
	"chat_project_instructions",
	"chat_project_memory",
	"chat_project_skill",
	"convert_login",
	"custom_role",
	"experiment_rule",
	"git_ssh_key",
	"group",
	"group_ai_budget",
	"health_settings",
	"idp_sync_settings_group",
	"idp_sync_settings_organization",
	"idp_sync_settings_role",
	"license",
	"mcp_server_config",
	"notification_template",
	"notifications_settings",
	"oauth2_provider_app",
	"oauth2_provider_app_secret",
	"oauth2_provider_settings",
	"organization",
	"organization_member",
	"organization_skill",
	"prebuilds_settings",
	"task",
	"template",
	"template_version",
	"user",
	"user_ai_budget_override",
	"user_secret",
	"user_skill",
	"workspace",
	"workspace_agent",
	"workspace_app",
	"workspace_build",
	"workspace_proxy",
];

// From codersdk/client.go
/**
 * Response represents a generic HTTP response.
 */
export interface Response {
	/**
	 * Message is an actionable message that depicts actions the request took.
	 * These messages should be fully formed sentences with proper punctuation.
	 * Examples:
	 * - "A user has been created."
	 * - "Failed to create a user."
	 */
	readonly message: string;
	/**
	 * Detail is a debug message that provides further insight into why the
	 * action failed. This information can be technical and a regular golang
	 * err.Error() text.
	 * - "database: too many open connections"
	 * - "stat: too many open files"
	 */
	readonly detail?: string;
	/**
	 * Validations are form field-specific friendly error messages. They will be
	 * shown on a form field in the UI. These can also be used to add additional
	 * context if there is a set of errors in the primary 'Message'.
	 */
	readonly validations?: readonly ValidationError[];
}

// From codersdk/deployment.go
/**
 * RetentionConfig contains configuration for data retention policies.
 * These settings control how long various types of data are retained in the database
 * before being automatically purged. Setting a value to 0 disables retention for that
 * data type (data is kept indefinitely).
 */
export interface RetentionConfig {
	/**
	 * AuditLogs controls how long audit log entries are retained.
	 * Set to 0 to disable (keep indefinitely).
	 */
	readonly audit_logs: number;
	/**
	 * ConnectionLogs controls how long connection log entries are retained.
	 * Set to 0 to disable (keep indefinitely).
	 */
	readonly connection_logs: number;
	/**
	 * APIKeys controls how long expired API keys are retained before being deleted.
	 * Keys are only deleted if they have been expired for at least this duration.
	 * Defaults to 7 days to preserve existing behavior.
	 */
	readonly api_keys: number;
	/**
	 * WorkspaceAgentLogs controls how long workspace agent logs are retained.
	 * Logs are deleted if the agent hasn't connected within this period.
	 * Logs from the latest build are always retained regardless of age.
	 * Defaults to 7 days to preserve existing behavior.
	 */
	readonly workspace_agent_logs: number;
	/**
	 * BoundaryLogs controls how long boundary audit log entries are
	 * retained. Boundary logs record every HTTP request processed by
	 * a Boundary confinement proxy. Set to 0 to disable automatic
	 * deletion (keep indefinitely). Adjust to match your
	 * organization's regulatory requirements.
	 */
	readonly boundary_logs: number;
}

// From codersdk/roles.go
/**
 * Role is a longer form of SlimRole that includes permissions details.
 */
export interface Role {
	readonly name: string;
	readonly organization_id?: string;
	readonly display_name: string;
	readonly site_permissions: readonly Permission[];
	readonly user_permissions: readonly Permission[];
	/**
	 * OrganizationPermissions are specific for the organization in the field 'OrganizationID' above.
	 */
	readonly organization_permissions: readonly Permission[];
	/**
	 * OrganizationMemberPermissions are specific for the organization in the field 'OrganizationID' above.
	 */
	readonly organization_member_permissions: readonly Permission[];
}

// From codersdk/rbacroles.go
/**
 * Ideally these roles would be generated from the rbac/roles.go package.
 * RoleAgentsAccess is the organization role that grants Coder Agents
 * chat access. Organizations include it in their default member roles
 * unless an administrator removes it.
 */
export const RoleAgentsAccess = "agents-access";

// From codersdk/rbacroles.go
/**
 * Ideally these roles would be generated from the rbac/roles.go package.
 */
export const RoleAuditor = "auditor";

// From codersdk/rbacroles.go
/**
 * Ideally these roles would be generated from the rbac/roles.go package.
 */
export const RoleMember = "member";

// From codersdk/rbacroles.go
/**
 * Ideally these roles would be generated from the rbac/roles.go package.
 */
export const RoleOrganizationAdmin = "organization-admin";

// From codersdk/rbacroles.go
/**
 * Ideally these roles would be generated from the rbac/roles.go package.
 */
export const RoleOrganizationAuditor = "organization-auditor";

// From codersdk/rbacroles.go
/**
 * Ideally these roles would be generated from the rbac/roles.go package.
 */
export const RoleOrganizationMember = "organization-member";

// From codersdk/rbacroles.go
/**
 * Ideally these roles would be generated from the rbac/roles.go package.
 */
export const RoleOrganizationTemplateAdmin = "organization-template-admin";

// From codersdk/rbacroles.go
/**
 * Ideally these roles would be generated from the rbac/roles.go package.
 */
export const RoleOrganizationUserAdmin = "organization-user-admin";

// From codersdk/rbacroles.go
/**
 * Ideally these roles would be generated from the rbac/roles.go package.
 */
export const RoleOrganizationWorkspaceAccess = "organization-workspace-access";

// From codersdk/rbacroles.go
/**
 * Ideally these roles would be generated from the rbac/roles.go package.
 */
export const RoleOrganizationWorkspaceCreationBan =
	"organization-workspace-creation-ban";

// From codersdk/rbacroles.go
/**
 * Ideally these roles would be generated from the rbac/roles.go package.
 */
export const RoleOwner = "owner";

// From codersdk/idpsync.go
export interface RoleSyncSettings {
	/**
	 * Field is the name of the claim field that specifies what organization roles
	 * a user should be given. If empty, no roles will be synced.
	 */
	readonly field: string;
	/**
	 * Mapping is a map from OIDC groups to Coder organization roles.
	 */
	readonly mapping: Record<string, string[]>;
}

// From codersdk/rbacroles.go
/**
 * Ideally these roles would be generated from the rbac/roles.go package.
 */
export const RoleTemplateAdmin = "template-admin";

// From codersdk/rbacroles.go
/**
 * Ideally these roles would be generated from the rbac/roles.go package.
 */
export const RoleUserAdmin = "user-admin";

// From codersdk/deployment.go
/**
 * SSHConfig is configuration the cli & vscode extension use for configuring
 * ssh connections.
 */
export interface SSHConfig {
	/**
	 * DeploymentName is the config-ssh Hostname prefix
	 */
	readonly DeploymentName: string;
	/**
	 * SSHConfigOptions are additional options to add to the ssh config file.
	 * This will override defaults.
	 */
	readonly SSHConfigOptions: string;
}

// From codersdk/deployment.go
export interface SSHConfigResponse {
	/**
	 * HostnamePrefix is the prefix we append to workspace names for SSH hostnames.
	 * @deprecated use HostnameSuffix instead.
	 */
	readonly hostname_prefix: string;
	/**
	 * HostnameSuffix is the suffix to append to workspace names for SSH hostnames.
	 */
	readonly hostname_suffix: string;
	readonly ssh_config_options: Record<string, string>;
}

// From healthsdk/healthsdk.go
/**
 * STUNReport contains information about a given node's STUN capabilities.
 */
export interface STUNReport {
	readonly Enabled: boolean;
	readonly CanSTUN: boolean;
	readonly Error: string | null;
}

// From codersdk/usersecretsimport.go
export type SecretsFileFormat = "env" | "json" | "yaml";

export const SecretsFileFormats: SecretsFileFormat[] = ["env", "json", "yaml"];

// From serpent/serpent.go
/**
 * Annotations is an arbitrary key-mapping used to extend the Option and Command types.
 * Its methods won't panic if the map is nil.
 */
export type SerpentAnnotations = Record<string, string>;

// From serpent/serpent.go
/**
 * Group describes a hierarchy of groups that an option or command belongs to.
 */
export interface SerpentGroup {
	readonly parent?: SerpentGroup;
	readonly name?: string;
	readonly yaml?: string;
	readonly description?: string;
}

// From serpent/option.go
/**
 * Option is a configuration option for a CLI application.
 */
export interface SerpentOption {
	readonly name?: string;
	readonly description?: string;
	/**
	 * Required means this value must be set by some means. It requires
	 * `ValueSource != ValueSourceNone`
	 * If `Default` is set, then `Required` is ignored.
	 */
	readonly required?: boolean;
	/**
	 * Flag is the long name of the flag used to configure this option. If unset,
	 * flag configuring is disabled.
	 */
	readonly flag?: string;
	/**
	 * FlagShorthand is the one-character shorthand for the flag. If unset, no
	 * shorthand is used.
	 */
	readonly flag_shorthand?: string;
	/**
	 * Env is the environment variable used to configure this option. If unset,
	 * environment configuring is disabled.
	 */
	readonly env?: string;
	/**
	 * YAML is the YAML key used to configure this option. If unset, YAML
	 * configuring is disabled.
	 */
	readonly yaml?: string;
	/**
	 * Default is parsed into Value if set.
	 * Must be `""` if `DefaultFn` != nil
	 */
	readonly default?: string;
	/**
	 * Value includes the types listed in values.go.
	 */
	// interface type, falling back to unknown
	// this is likely an enum in an external package "github.com/spf13/pflag.Value"
	readonly value?: unknown;
	/**
	 * Annotations enable extensions to serpent higher up in the stack. It's useful for
	 * help formatting and documentation generation.
	 */
	readonly annotations?: SerpentAnnotations;
	/**
	 * Group is a group hierarchy that helps organize this option in help, configs
	 * and other documentation.
	 */
	readonly group?: SerpentGroup;
	/**
	 * UseInstead is a list of options that should be used instead of this one.
	 * The field is used to generate a deprecation warning.
	 */
	readonly use_instead?: readonly SerpentOption[];
	readonly hidden?: boolean;
	readonly value_source?: SerpentValueSource;
}

// From serpent/option.go
/**
 * OptionSet is a group of options that can be applied to a command.
 */
export type SerpentOptionSet = readonly SerpentOption[];

// From serpent/values.go
export type SerpentStruct<T> = T;

// From serpent/option.go
export type SerpentValueSource = string;

// From derp/derp_client.go
/**
 * ServerInfoMessage is sent by the server upon first connect.
 */
export interface ServerInfoMessage {
	/**
	 * TokenBucketBytesPerSecond is how many bytes per second the
	 * server says it will accept, including all framing bytes.
	 *
	 * Zero means unspecified. There might be a limit, but the
	 * client need not try to respect it.
	 */
	readonly TokenBucketBytesPerSecond: number;
	/**
	 * TokenBucketBytesBurst is how many bytes the server will
	 * allow to burst, temporarily violating
	 * TokenBucketBytesPerSecond.
	 *
	 * Zero means unspecified. There might be a limit, but the
	 * client need not try to respect it.
	 */
	readonly TokenBucketBytesBurst: number;
}

// From codersdk/serversentevents.go
export interface ServerSentEvent {
	readonly type: ServerSentEventType;
	// empty interface{} type, falling back to unknown
	readonly data: unknown;
}

// From codersdk/serversentevents.go
export type ServerSentEventType = "data" | "error" | "ping";

export const ServerSentEventTypes: ServerSentEventType[] = [
	"data",
	"error",
	"ping",
];

// From codersdk/deployment.go
/**
 * @deprecated ServiceBannerConfig has been renamed to BannerConfig.
 */
export interface ServiceBannerConfig {
	readonly enabled: boolean;
	readonly message?: string;
	readonly background_color?: string;
}

// From codersdk/appname.go
/**
 * SessionCountApp is one app's session count and how to present it.
 */
export interface SessionCountApp {
	readonly count: number;
	/**
	 * DisplayName is the registry's name for a known app, otherwise the
	 * normalized identifier itself.
	 */
	readonly display_name: string;
	/**
	 * Icon is a bundled path under /icon/, empty if the app has none.
	 */
	readonly icon?: string;
	/**
	 * Family is the group this app totals under.
	 */
	readonly family: AppFamilyName;
}

// From codersdk/deployment.go
export interface SessionCountDeploymentStats {
	/**
	 * Apps holds one entry per reported app name, each carrying the family it
	 * totals under. The fields below duplicate those totals for one release.
	 */
	readonly apps: Record<string, SessionCountApp>;
	/**
	 * @deprecated total Apps by Family instead.
	 */
	readonly vscode: number;
	/**
	 * @deprecated total Apps by Family instead.
	 */
	readonly ssh: number;
	/**
	 * @deprecated total Apps by Family instead.
	 */
	readonly jetbrains: number;
	/**
	 * @deprecated total Apps by Family instead.
	 */
	readonly reconnecting_pty: number;
}

// From codersdk/deployment.go
/**
 * SessionLifetime refers to "sessions" authenticating into Coderd. Coder has
 * multiple different session types: api keys, tokens, workspace app tokens,
 * agent tokens, etc. This configuration struct should be used to group all
 * settings referring to any of these session lifetime controls.
 * TODO: These config options were created back when coder only had api keys.
 * Today, the config is ambigously used for all of them. For example:
 * - cli based api keys ignore all settings
 * - login uses the default lifetime, not the MaximumTokenDuration
 * - Tokens use the Default & MaximumTokenDuration
 * - ... etc ...
 * The rational behind each decision is undocumented. The naming behind these
 * config options is also confusing without any clear documentation.
 * 'CreateAPIKey' is used to make all sessions, and it's parameters are just
 * 'LifetimeSeconds' and 'DefaultLifetime'. Which does not directly correlate to
 * the config options here.
 */
export interface SessionLifetime {
	/**
	 * DisableExpiryRefresh will disable automatically refreshing api
	 * keys when they are used from the api. This means the api key lifetime at
	 * creation is the lifetime of the api key.
	 */
	readonly disable_expiry_refresh?: boolean;
	/**
	 * DefaultDuration is only for browser, workspace app and oauth sessions.
	 */
	readonly default_duration: number;
	/**
	 * RefreshDefaultDuration is the default lifetime for OAuth2 refresh tokens.
	 * This should generally be longer than access token lifetimes to allow
	 * refreshing after access token expiry.
	 */
	readonly refresh_default_duration?: number;
	readonly default_token_lifetime?: number;
	readonly max_token_lifetime?: number;
	readonly max_admin_token_lifetime?: number;
}

// From codersdk/client.go
/**
 * SessionTokenHeader is the custom header to use for authentication.
 */
export const SessionTokenHeader = "Coder-Session-Token";

// From codersdk/workspacesharing.go
export type ShareableWorkspaceOwners = "everyone" | "none" | "service_accounts";

export const ShareableWorkspaceOwnerses: ShareableWorkspaceOwners[] = [
	"everyone",
	"none",
	"service_accounts",
];

// From codersdk/workspaces.go
export interface SharedWorkspaceActor {
	readonly id: string;
	readonly actor_type: SharedWorkspaceActorType;
	readonly name: string;
	readonly avatar_url?: string;
	readonly roles: readonly WorkspaceRole[];
}

// From codersdk/workspaces.go
export type SharedWorkspaceActorType = "group" | "user";

export const SharedWorkspaceActorTypes: SharedWorkspaceActorType[] = [
	"group",
	"user",
];

// From codersdk/client.go
/**
 * SignedAppTokenCookie is the name of the cookie that stores a temporary
 * JWT that can be used to authenticate instead of the app session token.
 *nolint:gosec
 */
export const SignedAppTokenCookie = "coder_signed_app_token";

// From codersdk/client.go
/**
 * SignedAppTokenQueryParameter is the name of the query parameter that
 * stores a temporary JWT that can be used to authenticate instead of the
 * session token. This is only acceptable on reconnecting-pty requests, not
 * apps.
 *
 * It has a random suffix to avoid conflict with user query parameters on
 * apps.
 *nolint:gosec
 */
export const SignedAppTokenQueryParameter = "coder_signed_app_token_23db1dde";

// From codersdk/skills.go
/**
 * Skill represents a personal or organization skill with its raw Markdown
 * content.
 */
export interface Skill extends SkillMetadata {
	readonly content: string;
}

// From codersdk/skills.go
/**
 * SkillMetadata represents a personal or organization skill without its raw
 * Markdown content.
 */
export interface SkillMetadata {
	readonly id: string;
	readonly name: string;
	readonly description: string;
	readonly enabled: boolean;
	/**
	 * Action reports whether a project skill is offered as a one-click
	 * action above project composers. It is always false for personal and
	 * organization skills.
	 */
	readonly action: boolean;
	/**
	 * ModelInvocable reports whether agents can discover and load the skill
	 * on their own. Users can still invoke a skill explicitly when it is
	 * false.
	 */
	readonly model_invocable: boolean;
	readonly created_at: string;
	readonly updated_at: string;
}

// From codersdk/roles.go
/**
 * SlimRole omits permission information from a role.
 * At present, this is because our apis do not return permission information,
 * and it would require extra db calls to fetch this information. The UI does
 * not need it, so most api calls will use this structure that omits information.
 */
export interface SlimRole {
	readonly name: string;
	readonly display_name: string;
	readonly organization_id?: string;
}

// From codersdk/deployment.go
export interface StatsCollectionConfig {
	readonly usage_stats: UsageStatsConfig;
}

// From codersdk/chats.go
/**
 * StreamChatOptions are optional parameters for StreamChat.
 */
export interface StreamChatOptions {
	/**
	 * AfterID limits the initial snapshot to messages created
	 * after the given ID. This is useful for relay connections
	 * that only need live message_part events and can skip the
	 * full message history.
	 */
	readonly AfterID: number | null;
}

// From codersdk/client.go
/**
 * SubdomainAppSessionTokenCookie is the name of the cookie that stores an
 * application-scoped API token on subdomain app domains (both the primary
 * and proxies).
 *
 * To avoid conflicts between multiple proxies, we append an underscore and
 * a hash suffix to the cookie name.
 *nolint:gosec
 */
export const SubdomainAppSessionTokenCookie =
	"coder_subdomain_app_session_token";

// From codersdk/chats.go
/**
 * SubmitToolResultsRequest is the body for POST /chats/{id}/tool-results.
 */
export interface SubmitToolResultsRequest {
	readonly results: readonly ToolResult[];
}

// From codersdk/deployment.go
export interface SupportConfig {
	readonly links: SerpentStruct<LinkConfig[]>;
}

// From codersdk/deployment.go
export interface SwaggerConfig {
	readonly enable: boolean;
}

// From codersdk/deployment.go
export interface TLSConfig {
	readonly enable: boolean;
	readonly address: string;
	readonly redirect_http: boolean;
	readonly cert_file: string;
	readonly client_auth: string;
	readonly client_ca_file: string;
	readonly key_file: string;
	readonly min_version: string;
	readonly client_cert_file: string;
	readonly client_key_file: string;
	readonly supported_ciphers: string;
	readonly allow_insecure_ciphers: boolean;
}

// From tailcfg/derpmap.go
/**
 * DERPNode describes a DERP packet relay node running within a DERPRegion.
 */
export interface TailDERPNode {
	/**
	 * Name is a unique node name (across all regions).
	 * It is not a host name.
	 * It's typically of the form "1b", "2a", "3b", etc. (region
	 * ID + suffix within that region)
	 */
	readonly Name: string;
	/**
	 * RegionID is the RegionID of the DERPRegion that this node
	 * is running in.
	 */
	readonly RegionID: number;
	/**
	 * HostName is the DERP node's hostname.
	 *
	 * It is required but need not be unique; multiple nodes may
	 * have the same HostName but vary in configuration otherwise.
	 */
	readonly HostName: string;
	/**
	 * CertName optionally specifies the expected TLS cert common
	 * name. If empty, HostName is used. If CertName is non-empty,
	 * HostName is only used for the TCP dial (if IPv4/IPv6 are
	 * not present) + TLS ClientHello.
	 */
	readonly CertName?: string;
	/**
	 * IPv4 optionally forces an IPv4 address to use, instead of using DNS.
	 * If empty, A record(s) from DNS lookups of HostName are used.
	 * If the string is not an IPv4 address, IPv4 is not used; the
	 * conventional string to disable IPv4 (and not use DNS) is
	 * "none".
	 */
	readonly IPv4?: string;
	/**
	 * IPv6 optionally forces an IPv6 address to use, instead of using DNS.
	 * If empty, AAAA record(s) from DNS lookups of HostName are used.
	 * If the string is not an IPv6 address, IPv6 is not used; the
	 * conventional string to disable IPv6 (and not use DNS) is
	 * "none".
	 */
	readonly IPv6?: string;
	/**
	 * Port optionally specifies a STUN port to use.
	 * Zero means 3478.
	 * To disable STUN on this node, use -1.
	 */
	readonly STUNPort?: number;
	/**
	 * STUNOnly marks a node as only a STUN server and not a DERP
	 * server.
	 */
	readonly STUNOnly?: boolean;
	/**
	 * DERPPort optionally provides an alternate TLS port number
	 * for the DERP HTTPS server.
	 *
	 * If zero, 443 is used.
	 */
	readonly DERPPort?: number;
	/**
	 * InsecureForTests is used by unit tests to disable TLS verification.
	 * It should not be set by users.
	 */
	readonly InsecureForTests?: boolean;
	/**
	 * ForceHTTP is used by unit tests to force HTTP.
	 * It should not be set by users.
	 */
	readonly ForceHTTP?: boolean;
	/**
	 * STUNTestIP is used in tests to override the STUN server's IP.
	 * If empty, it's assumed to be the same as the DERP server.
	 */
	readonly STUNTestIP?: string;
	/**
	 * CanPort80 specifies whether this DERP node is accessible over HTTP
	 * on port 80 specifically. This is used for captive portal checks.
	 */
	readonly CanPort80?: boolean;
}

// From tailcfg/derpmap.go
/**
 * DERPRegion is a geographic region running DERP relay node(s).
 *
 * Client nodes discover which region they're closest to, advertise
 * that "home" DERP region (previously called "home node", when there
 * was only 1 node per region) and maintain a persistent connection
 * that region as long as it's the closest. Client nodes will further
 * connect to other regions as necessary to communicate with peers
 * advertising other regions as their homes.
 */
export interface TailDERPRegion {
	/**
	 * EmbeddedRelay is true when the region is bundled with the Coder
	 * control plane.
	 */
	readonly EmbeddedRelay: boolean;
	/**
	 * RegionID is a unique integer for a geographic region.
	 *
	 * It corresponds to the legacy derpN.tailscale.com hostnames
	 * used by older clients. (Older clients will continue to resolve
	 * derpN.tailscale.com when contacting peers, rather than use
	 * the server-provided DERPMap)
	 *
	 * RegionIDs must be non-zero, positive, and guaranteed to fit
	 * in a JavaScript number.
	 *
	 * RegionIDs in range 900-999 are reserved for end users to run their
	 * own DERP nodes.
	 */
	readonly RegionID: number;
	/**
	 * RegionCode is a short name for the region. It's usually a popular
	 * city or airport code in the region: "nyc", "sf", "sin",
	 * "fra", etc.
	 */
	readonly RegionCode: string;
	/**
	 * RegionName is a long English name for the region: "New York City",
	 * "San Francisco", "Singapore", "Frankfurt", etc.
	 */
	readonly RegionName: string;
	/**
	 * Avoid is whether the client should avoid picking this as its home
	 * region. The region should only be used if a peer is there.
	 * Clients already using this region as their home should migrate
	 * away to a new region without Avoid set.
	 */
	readonly Avoid?: boolean;
	/**
	 * Nodes are the DERP nodes running in this region, in
	 * priority order for the current client. Client TLS
	 * connections should ideally only go to the first entry
	 * (falling back to the second if necessary). STUN packets
	 * should go to the first 1 or 2.
	 *
	 * If nodes within a region route packets amongst themselves,
	 * but not to other regions. That said, each user/domain
	 * should get a the same preferred node order, so if all nodes
	 * for a user/network pick the first one (as they should, when
	 * things are healthy), the inter-cluster routing is minimal
	 * to zero.
	 */
	readonly Nodes: readonly TailDERPNode[];
}

// From codersdk/deployment.go
export interface TelemetryConfig {
	readonly enable: boolean;
	readonly trace: boolean;
	readonly url: string;
}

// From codersdk/templates.go
/**
 * Template is the JSON representation of a Coder template. This type matches the
 * database object for now, but is abstracted for ease of change later on.
 */
export interface Template {
	readonly id: string;
	readonly created_at: string;
	readonly updated_at: string;
	readonly organization_id: string;
	readonly organization_name: string;
	readonly organization_display_name: string;
	readonly organization_icon: string;
	readonly name: string;
	readonly display_name: string;
	readonly provisioner: ProvisionerType;
	readonly active_version_id: string;
	/**
	 * ActiveUserCount is set to -1 when loading.
	 */
	readonly active_user_count: number;
	readonly build_time_stats: TemplateBuildTimeStats;
	readonly description: string;
	readonly deprecated: boolean;
	readonly deprecation_message: string;
	readonly deleted: boolean;
	readonly icon: string;
	readonly default_ttl_ms: number;
	readonly activity_bump_ms: number;
	/**
	 * TimeTilAutostopNotifyMillis is the duration before the workspace's
	 * autostop deadline at which a reminder notification is sent. 0 disables
	 * the notification.
	 */
	readonly time_til_autostop_notify_ms: number;
	/**
	 * AutostopRequirement and AutostartRequirement are Premium features. Their
	 * values are only used if your license is entitled to use the advanced template
	 * scheduling feature.
	 */
	readonly autostop_requirement: TemplateAutostopRequirement;
	readonly autostart_requirement: TemplateAutostartRequirement;
	readonly created_by_id: string;
	readonly created_by_name: string;
	/**
	 * AllowUserAutostart and AllowUserAutostop are Premium features. Their
	 * values are only used if your license is entitled to use the advanced
	 * template scheduling feature.
	 */
	readonly allow_user_autostart: boolean;
	readonly allow_user_autostop: boolean;
	readonly allow_user_cancel_workspace_jobs: boolean;
	/**
	 * FailureTTLMillis, TimeTilDormantMillis, and TimeTilDormantAutoDeleteMillis are Premium features. Their
	 * values are used if your license is entitled to use the advanced
	 * template scheduling feature.
	 */
	readonly failure_ttl_ms: number;
	readonly time_til_dormant_ms: number;
	readonly time_til_dormant_autodelete_ms: number;
	/**
	 * RequireActiveVersion mandates that workspaces are built with the active
	 * template version.
	 */
	readonly require_active_version: boolean;
	readonly max_port_share_level: WorkspaceAgentPortShareLevel;
	readonly cors_behavior: CORSBehavior;
	readonly use_classic_parameter_flow: boolean;
	readonly agents_allowed: boolean;
	/**
	 * DisableModuleCache disables the use of cached Terraform modules during
	 * provisioning for this template. It is read-only while
	 * ModuleCacheDisabledByDeployment is true.
	 */
	readonly disable_module_cache: boolean;
	/**
	 * ModuleCacheDisabledByDeployment reports that the deployment disables the
	 * Terraform module cache for every template. Templates cannot opt back in,
	 * so the effective state is disabled regardless of DisableModuleCache.
	 */
	readonly module_cache_disabled_by_deployment: boolean;
	/**
	 * AllowWorkspaceRenames permits users to rename workspaces built from this
	 * template. Renaming can be destructive for templates whose Terraform
	 * references the workspace name.
	 */
	readonly allow_workspace_renames: boolean;
}

// From codersdk/templates.go
export interface TemplateACL {
	readonly users: readonly TemplateUser[];
	readonly group: readonly TemplateGroup[];
}

// From codersdk/insights.go
/**
 * TemplateAppUsage shows the usage of an app for one or more templates.
 */
export interface TemplateAppUsage {
	readonly template_ids: readonly string[];
	/**
	 * Method is how the app connected. Method and Slug identify an app.
	 */
	readonly method: ConnectionLogMethod;
	readonly display_name: string;
	readonly slug: string;
	readonly icon: string;
	readonly seconds: number;
	readonly times_used: number;
	/**
	 * SecondsIsUpperBound marks overflow usage that includes folded apps.
	 * Their overlap is not stored, so Seconds may overestimate active time.
	 */
	readonly seconds_is_upper_bound?: boolean;
}

// From codersdk/templates.go
export interface TemplateAutostartRequirement {
	/**
	 * DaysOfWeek is a list of days of the week in which autostart is allowed
	 * to happen. If no days are specified, autostart is not allowed.
	 */
	readonly days_of_week: readonly string[];
}

// From codersdk/templates.go
export interface TemplateAutostopRequirement {
	/**
	 * DaysOfWeek is a list of days of the week on which restarts are required.
	 * Restarts happen within the user's quiet hours (in their configured
	 * timezone). If no days are specified, restarts are not required. Weekdays
	 * cannot be specified twice.
	 *
	 * Restarts will only happen on weekdays in this list on weeks which line up
	 * with Weeks.
	 */
	readonly days_of_week: readonly string[];
	/**
	 * Weeks is the number of weeks between required restarts. Weeks are synced
	 * across all workspaces (and Coder deployments) using modulo math on a
	 * hardcoded epoch week of January 2nd, 2023 (the first Monday of 2023).
	 * Values of 0 or 1 indicate weekly restarts. Values of 2 indicate
	 * fortnightly restarts, etc.
	 */
	readonly weeks: number;
}

// From codersdk/templates.go
export type TemplateBuildTimeStats = Record<
	WorkspaceTransition,
	TransitionStats
>;

// From codersdk/templatebuilder.go
/**
 * TemplateBuilderBase is the API response type for a base template
 * returned by GET /api/v2/templatebuilder/bases.
 */
export interface TemplateBuilderBase {
	readonly id: string;
	readonly name: string;
	readonly description: string;
	readonly icon: string;
	readonly os: string;
	readonly variables: readonly TemplateBuilderModuleVariable[];
	readonly prerequisites: string;
	readonly agents: readonly TemplateBuilderBaseAgent[];
}

// From codersdk/templatebuilder.go
/**
 * TemplateBuilderBaseAgent is a coder_agent a base template declares. Modules
 * composed onto the base target one of these by Name.
 */
export interface TemplateBuilderBaseAgent {
	readonly name: string;
	readonly display_name: string;
	/**
	 * Default reports whether modules attach to this agent when they do not
	 * name one.
	 */
	readonly default: boolean;
}

// From codersdk/templatebuilder.go
/**
 * TemplateBuilderBasesResponse is the response body for listing template builder bases.
 */
export interface TemplateBuilderBasesResponse {
	readonly bases: readonly TemplateBuilderBase[];
}

// From codersdk/templatebuilder.go
/**
 * TemplateBuilderComposeModule identifies a module and its variable
 * values for the compose request.
 */
export interface TemplateBuilderComposeModule {
	readonly id: string;
	/**
	 * AgentName targets a base coder_agent by name. Empty uses the base default.
	 */
	readonly agent_name?: string;
	readonly variables?: Record<string, string>;
}

// From codersdk/templatebuilder.go
/**
 * TemplateBuilderComposeRequest is the request body for
 * POST /api/v2/templatebuilder/compose.
 */
export interface TemplateBuilderComposeRequest {
	readonly base_template_id: string;
	readonly base_variable_values?: Record<string, string>;
	readonly modules: readonly TemplateBuilderComposeModule[];
}

// From codersdk/deployment.go
export interface TemplateBuilderConfig {
	readonly disabled?: boolean;
	readonly registry_url?: string;
}

// From codersdk/templatebuilder.go
/**
 * TemplateBuilderCreateTemplateRequest is the request body for
 * POST /api/v2/templatebuilder/compose/template.
 */
export interface TemplateBuilderCreateTemplateRequest {
	readonly base_template_id: string;
	readonly base_variable_values?: Record<string, string>;
	readonly modules: readonly TemplateBuilderComposeModule[];
	readonly organization_id: string;
	readonly name: string;
	readonly display_name?: string;
	readonly description?: string;
	readonly icon?: string;
	readonly provisioner_tags?: Record<string, string>;
	/**
	 * SessionID is the wizard session this request belongs to, as reported to
	 * POST /api/v2/templatebuilder/sessions. It is optional and used only to
	 * attribute a build failure to the session that produced it.
	 */
	readonly session_id?: string;
}

// From codersdk/templatebuilder.go
/**
 * TemplateBuilderCreateTemplateResponse is the response body for
 * POST /api/v2/templatebuilder/compose/template.
 */
export interface TemplateBuilderCreateTemplateResponse {
	readonly template: Template;
}

// From codersdk/templatebuilder.go
/**
 * TemplateBuilderModule is the API response type returned by
 * GET /api/v2/templatebuilder/modules. The Version field is
 * populated from the catalog manifest's PinnedVersion at serving time.
 */
export interface TemplateBuilderModule {
	readonly id: string;
	readonly display_name: string;
	readonly description: string;
	readonly icon: string;
	readonly category: string;
	readonly version: string;
	readonly compatible_os: readonly string[];
	readonly conflicts_with: readonly string[];
	readonly variables: readonly TemplateBuilderModuleVariable[];
}

// From codersdk/templatebuilder.go
export interface TemplateBuilderModuleVariable {
	readonly name: string;
	readonly type: TemplateBuilderVariableType;
	readonly description: string;
	readonly default?: Record<string, string>;
	readonly required: boolean;
	readonly sensitive: boolean;
}

// From codersdk/templatebuilder.go
/**
 * TemplateBuilderModulesResponse is the response body for listing template builder modules.
 */
export interface TemplateBuilderModulesResponse {
	readonly modules: readonly TemplateBuilderModule[];
}

// From codersdk/templatebuilder.go
export type TemplateBuilderSessionEventType =
	| "compose_completion"
	| "wizard_entry";

export const TemplateBuilderSessionEventTypes: TemplateBuilderSessionEventType[] =
	["compose_completion", "wizard_entry"];

// From codersdk/templatebuilder.go
/**
 * TemplateBuilderSessionRequest is the request body for
 * POST /api/v2/templatebuilder/sessions.
 */
export interface TemplateBuilderSessionRequest {
	readonly session_id: string;
	readonly event_type: TemplateBuilderSessionEventType;
	readonly base_template_id?: string;
	readonly module_ids?: readonly string[];
	readonly duration_seconds?: number;
	readonly success?: boolean;
}

// From codersdk/templatebuilder.go
export type TemplateBuilderVariableType = "bool" | "number" | "string";

export const TemplateBuilderVariableTypes: TemplateBuilderVariableType[] = [
	"bool",
	"number",
	"string",
];

// From codersdk/templates.go
export interface TemplateExample {
	readonly id: string;
	readonly url: string;
	readonly name: string;
	readonly description: string;
	readonly icon: string;
	readonly tags: readonly string[];
	readonly markdown: string;
}

// From codersdk/organizations.go
export interface TemplateFilter {
	readonly q?: string;
}

// From codersdk/templates.go
export interface TemplateGroup extends Group {
	readonly role: TemplateRole;
}

// From codersdk/insights.go
/**
 * TemplateInsightsIntervalReport is the report from the template insights
 * endpoint for a specific interval.
 */
export interface TemplateInsightsIntervalReport {
	readonly start_time: string;
	readonly end_time: string;
	readonly template_ids: readonly string[];
	readonly interval: InsightsReportInterval;
	readonly active_users: number;
}

// From codersdk/insights.go
/**
 * TemplateInsightsMaxUnregisteredApps limits individually listed unregistered
 * apps to the busiest names. Registered apps are exempt; the rest fold into
 * overflow.
 */
export const TemplateInsightsMaxUnregisteredApps = 64;

// From codersdk/insights.go
/**
 * TemplateInsightsReport is the report from the template insights endpoint.
 */
export interface TemplateInsightsReport {
	readonly start_time: string;
	readonly end_time: string;
	readonly template_ids: readonly string[];
	readonly active_users: number;
	/**
	 * UsageTotalSeconds counts each user's active minutes once, even when apps
	 * overlap. Summed per-app seconds can exceed this total.
	 */
	readonly usage_total_seconds: number;
	readonly apps_usage: readonly TemplateAppUsage[];
	readonly parameters_usage: readonly TemplateParameterUsage[];
}

// From codersdk/insights.go
export interface TemplateInsightsRequest {
	readonly start_time: string;
	readonly end_time: string;
	readonly template_ids: readonly string[];
	readonly interval: InsightsReportInterval;
	readonly sections: readonly TemplateInsightsSection[];
}

// From codersdk/insights.go
/**
 * TemplateInsightsResponse is the response from the template insights endpoint.
 */
export interface TemplateInsightsResponse {
	readonly report?: TemplateInsightsReport;
	readonly interval_reports?: readonly TemplateInsightsIntervalReport[];
}

// From codersdk/insights.go
export type TemplateInsightsSection = "interval_reports" | "report";

export const TemplateInsightsSections: TemplateInsightsSection[] = [
	"interval_reports",
	"report",
];

// From codersdk/insights.go
/**
 * TemplateParameterUsage shows the usage of a parameter for one or more
 * templates.
 */
export interface TemplateParameterUsage {
	readonly template_ids: readonly string[];
	readonly display_name: string;
	readonly name: string;
	readonly type: string;
	readonly description: string;
	readonly options?: readonly TemplateVersionParameterOption[];
	readonly values: readonly TemplateParameterValue[];
}

// From codersdk/insights.go
/**
 * TemplateParameterValue shows the usage of a parameter value for one or more
 * templates.
 */
export interface TemplateParameterValue {
	readonly value: string;
	readonly count: number;
}

// From codersdk/templates.go
export type TemplateRole = "admin" | "" | "use";

export const TemplateRoles: TemplateRole[] = ["admin", "", "use"];

// From codersdk/templates.go
export interface TemplateUser extends User {
	readonly role: TemplateRole;
}

// From codersdk/templateversions.go
/**
 * TemplateVersion represents a single version of a template.
 */
export interface TemplateVersion {
	readonly id: string;
	readonly template_id?: string;
	readonly organization_id?: string;
	readonly created_at: string;
	readonly updated_at: string;
	readonly name: string;
	readonly message: string;
	readonly job: ProvisionerJob;
	readonly readme: string;
	readonly created_by: MinimalUser;
	readonly archived: boolean;
	readonly warnings?: readonly TemplateVersionWarning[];
	readonly matched_provisioners?: MatchedProvisioners;
	readonly has_external_agent: boolean;
}

// From codersdk/templateversions.go
export interface TemplateVersionExternalAuth {
	readonly id: string;
	readonly type: string;
	readonly display_name: string;
	readonly display_icon: string;
	readonly authenticate_url: string;
	readonly authenticated: boolean;
	readonly optional?: boolean;
}

// From codersdk/templateversions.go
/**
 * TemplateVersionParameter represents a parameter for a template version.
 */
export interface TemplateVersionParameter {
	readonly name: string;
	readonly display_name?: string;
	readonly description: string;
	readonly description_plaintext: string;
	readonly type: string;
	/**
	 * FormType has an enum value of empty string, `""`.
	 * Keep the leading comma in the enums struct tag.
	 */
	readonly form_type: string;
	readonly mutable: boolean;
	readonly default_value: string;
	readonly icon: string;
	readonly options: readonly TemplateVersionParameterOption[];
	readonly validation_error?: string;
	readonly validation_regex?: string;
	readonly validation_min?: number;
	readonly validation_max?: number;
	readonly validation_monotonic?: ValidationMonotonicOrder;
	readonly required: boolean;
	readonly ephemeral: boolean;
}

// From codersdk/templateversions.go
/**
 * TemplateVersionParameterOption represents a selectable option for a template parameter.
 */
export interface TemplateVersionParameterOption {
	readonly name: string;
	readonly description: string;
	readonly value: string;
	readonly icon: string;
}

// From codersdk/templateversions.go
/**
 * TemplateVersionVariable represents a managed template variable.
 */
export interface TemplateVersionVariable {
	readonly name: string;
	readonly description: string;
	readonly type: string;
	readonly value: string;
	readonly default_value: string;
	readonly required: boolean;
	readonly sensitive: boolean;
}

// From codersdk/templateversions.go
export type TemplateVersionWarning = "UNSUPPORTED_WORKSPACES";

export const TemplateVersionWarnings: TemplateVersionWarning[] = [
	"UNSUPPORTED_WORKSPACES",
];

// From codersdk/templates.go
/**
 * TemplateVersionsByTemplateRequest defines the request parameters for
 * TemplateVersionsByTemplate.
 */
export interface TemplateVersionsByTemplateRequest extends Pagination {
	readonly template_id: string;
	readonly include_archived: boolean;
}

// From codersdk/users.go
export type TerminalFontName =
	| "fira-code"
	| "geist-mono"
	| "ibm-plex-mono"
	| "jetbrains-mono"
	| "source-code-pro"
	| "";

export const TerminalFontNames: TerminalFontName[] = [
	"fira-code",
	"geist-mono",
	"ibm-plex-mono",
	"jetbrains-mono",
	"source-code-pro",
	"",
];

// From codersdk/users.go
export type ThemeMode = "single" | "sync" | "";

export const ThemeModes: ThemeMode[] = ["single", "sync", ""];

// From codersdk/users.go
export type ThinkingDisplayMode =
	| "always_collapsed"
	| "always_expanded"
	| "auto"
	| "preview";

export const ThinkingDisplayModes: ThinkingDisplayMode[] = [
	"always_collapsed",
	"always_expanded",
	"auto",
	"preview",
];

// From codersdk/workspacebuilds.go
export type TimingStage =
	| "apply"
	| "connect"
	| "cron"
	| "graph"
	| "init"
	| "plan"
	| "start"
	| "stop";

export const TimingStages: TimingStage[] = [
	"apply",
	"connect",
	"cron",
	"graph",
	"init",
	"plan",
	"start",
	"stop",
];

// From codersdk/apikey.go
export interface TokenConfig {
	readonly max_token_lifetime: number;
}

// From codersdk/apikey.go
export interface TokensFilter {
	readonly include_all: boolean;
	readonly include_expired: boolean;
}

// From codersdk/chats.go
/**
 * ToolResult is the client's response to a dynamic tool call.
 */
export interface ToolResult {
	readonly tool_call_id: string;
	readonly output: Record<string, string>;
	readonly is_error: boolean;
}

// From codersdk/deployment.go
export interface TraceConfig {
	readonly enable: boolean;
	readonly honeycomb_api_key: string;
	readonly capture_logs: boolean;
	readonly data_dog: boolean;
}

// From codersdk/templates.go
export interface TransitionStats {
	readonly P50: number | null;
	readonly P95: number | null;
}

// From codersdk/aiproviders.go
/**
 * UpdateAIProviderRequest is the payload for partially updating an
 * AI provider. At least one field must be non-nil. Pointer fields
 * distinguish "not sent" (nil) from "set to empty/zero" (a pointer
 * to the zero value). When APIKeys is non-nil, the supplied list
 * describes the post-patch state of the key set; see
 * AIProviderKeyMutation for the per-entry semantics. An empty slice
 * clears all keys.
 */
export interface UpdateAIProviderRequest {
	readonly display_name?: string;
	readonly icon?: string;
	readonly enabled?: boolean;
	readonly base_url?: string;
	readonly api_keys?: AIProviderKeyMutation[];
	readonly settings?: AIProviderSettings;
}

// From codersdk/templates.go
export interface UpdateActiveTemplateVersion {
	readonly id: string;
}

// From codersdk/chats.go
/**
 * UpdateAdvisorConfigRequest is the request body for updating advisor runtime configuration.
 */
export interface UpdateAdvisorConfigRequest {
	readonly max_uses_per_run: number;
	readonly max_output_tokens: number;
}

// From codersdk/deployment.go
export interface UpdateAppearanceConfig {
	readonly application_name: string;
	readonly logo_url: string;
	/**
	 * @deprecated ServiceBanner has been replaced by AnnouncementBanners.
	 */
	readonly service_banner: BannerConfig;
	readonly announcement_banners: readonly BannerConfig[];
	readonly codernauts_enabled: boolean;
}

// From codersdk/chats.go
export interface UpdateChatACL {
	readonly user_roles?: Record<string, ChatRole>;
	readonly group_roles?: Record<string, ChatRole>;
}

// From codersdk/chats.go
/**
 * UpdateChatAutoArchiveDaysRequest is a request to update the chat
 * auto-archive period.
 */
export interface UpdateChatAutoArchiveDaysRequest {
	readonly auto_archive_days: number;
}

// From codersdk/chats.go
/**
 * UpdateChatDebugLoggingAllowUsersRequest is the admin request to
 * toggle whether users may opt into chat debug logging.
 */
export interface UpdateChatDebugLoggingAllowUsersRequest {
	readonly allow_users: boolean;
}

// From codersdk/chats.go
/**
 * UpdateChatDebugRetentionDaysRequest is a request to update the chat
 * debug run retention period.
 */
export interface UpdateChatDebugRetentionDaysRequest {
	readonly debug_retention_days: number;
}

// From codersdk/chats.go
/**
 * UpdateChatModelACLRequest is a sparse update of a chat model ACL. Only the
 * listed principals change. ChatRoleDeleted removes an entry, while an omitted
 * map or principal is unchanged.
 */
export interface UpdateChatModelACLRequest {
	readonly user_roles?: Record<string, ChatRole>;
	readonly group_roles?: Record<string, ChatRole>;
}

// From codersdk/chats.go
/**
 * UpdateChatModelOverrideRequest is the request body for updating the chat
 * model override configuration endpoint.
 */
export interface UpdateChatModelOverrideRequest {
	readonly model_config_id: string;
	readonly reasoning_effort?: string;
}

// From codersdk/chats.go
/**
 * UpdateChatModelRequest updates a ChatModel. Empty Model and DisplayName
 * values preserve the stored values. Nil pointer fields preserve their stored
 * values. This request cannot clear DisplayName.
 */
export interface UpdateChatModelRequest {
	readonly ai_provider_id?: string;
	readonly model?: string;
	readonly display_name?: string;
	readonly enabled?: boolean;
	readonly is_default?: boolean;
	readonly context_limit?: number;
	readonly compression_threshold?: number;
	readonly model_config?: ChatModelCallConfig;
}

// From codersdk/chats.go
/**
 * UpdateChatPersonalModelOverridesAdminSettingsRequest is the request body for
 * updating personal model override admin settings.
 */
export interface UpdateChatPersonalModelOverridesAdminSettingsRequest {
	readonly allow_users: boolean;
}

// From codersdk/chats.go
/**
 * UpdateChatPlanModeInstructionsRequest is the request body for
 * updating the plan mode instructions configuration.
 */
export interface UpdateChatPlanModeInstructionsRequest {
	readonly plan_mode_instructions: string;
}

// From codersdk/chats.go
/**
 * UpdateChatProjectACL changes only the listed principals.
 * ChatProjectRoleDeleted removes an entry.
 */
export interface UpdateChatProjectACL {
	readonly user_roles?: Record<string, ChatProjectRole>;
	readonly group_roles?: Record<string, ChatProjectRole>;
}

// From codersdk/chats.go
/**
 * UpdateChatProjectInstructionsRequest sets a project's instructions. Use
 * DeleteChatProjectInstructions to clear them.
 */
export interface UpdateChatProjectInstructionsRequest {
	readonly instructions: string;
}

// From codersdk/chats.go
/**
 * UpdateChatProjectRequest updates a chat project.
 */
export interface UpdateChatProjectRequest {
	readonly name?: string;
	readonly description?: string;
	readonly icon?: string;
}

// From codersdk/chats.go
/**
 * UpdateChatRequest is the request to update a chat.
 */
export interface UpdateChatRequest {
	/**
	 * Title, when set, is stored as the user title even when its text is
	 * unchanged, so a generated title never replaces it afterwards. It is
	 * validated like CreateChatRequest.Title.
	 */
	readonly title?: string;
	readonly archived?: boolean;
	readonly workspace_id?: string;
	/**
	 * ProjectID moves a root chat, and with it its subagent chats.
	 * - nil: no change.
	 * - uuid.Nil: remove the chat from its project.
	 * - other: move the chat into that project. The chat's owner must own
	 *   the project or have it shared with them, and the project must be
	 *   in the chat's organization.
	 * The server returns 409 while the chat or a subagent chat is not idle.
	 */
	readonly project_id?: string;
	/**
	 * PinOrder controls the chat's pinned state and position.
	 * - nil: no change to pin state.
	 * - 0: unpin the chat.
	 * - >0 (chat is unpinned): pin the chat, appending it to
	 *   the end of the pinned list. The specific value is
	 *   ignored; the server assigns the next available position.
	 * - >0 (chat is already pinned): move the chat to the
	 *   requested position, shifting neighbors as needed. The
	 *   value is clamped to [1, pinned_count].
	 */
	readonly pin_order?: number;
	readonly labels?: Record<string, string>;
	/**
	 * Read moves the owner's read cursor, which drives HasUnread.
	 * - nil: no change.
	 * - true: mark every existing message as read.
	 * - false: clear the cursor so the chat reads as unread again.
	 *
	 * The cursor is owner-scoped, so only the chat owner may set this.
	 * Opening a chat's stream marks it read, so marking the chat the
	 * owner is currently viewing as unread does not persist.
	 */
	readonly read?: boolean;
	/**
	 * PlanMode switches the chat's persistent plan mode.
	 * nil: no change, ptr to "plan": enable, ptr to "": clear.
	 */
	readonly plan_mode?: ChatPlanMode;
}

// From codersdk/chats.go
/**
 * UpdateChatRetentionDaysRequest is a request to update the chat
 * retention period.
 */
export interface UpdateChatRetentionDaysRequest {
	readonly retention_days: number;
}

// From codersdk/chats.go
/**
 * UpdateChatSystemPromptRequest is the request body for updating the chat
 * system prompt configuration.
 */
export interface UpdateChatSystemPromptRequest {
	readonly system_prompt: string;
	readonly include_default_system_prompt?: boolean;
}

// From codersdk/chats.go
/**
 * UpdateChatWorkspaceTTLRequest is the request to update the chat
 * workspace TTL setting.
 */
export interface UpdateChatWorkspaceTTLRequest {
	/**
	 * WorkspaceTTLMillis is the workspace TTL in milliseconds.
	 * Zero means disabled; the template's own autostop setting applies.
	 */
	readonly workspace_ttl_ms: number;
}

// From codersdk/updatecheck.go
/**
 * UpdateCheckResponse contains information on the latest release of Coder.
 */
export interface UpdateCheckResponse {
	/**
	 * Current indicates whether the server version is the same as the latest.
	 */
	readonly current: boolean;
	/**
	 * Version is the semantic version for the latest release of Coder.
	 */
	readonly version: string;
	/**
	 * URL to download the latest release of Coder.
	 */
	readonly url: string;
}

// From healthsdk/healthsdk.go
export interface UpdateHealthSettings {
	readonly dismissed_healthchecks: readonly HealthSection[];
}

// From codersdk/inboxnotification.go
export interface UpdateInboxNotificationReadStatusRequest {
	readonly is_read: boolean;
}

// From codersdk/inboxnotification.go
export interface UpdateInboxNotificationReadStatusResponse {
	readonly notification: InboxNotification;
	readonly unread_count: number;
}

// From codersdk/mcp.go
/**
 * UpdateMCPServerConfigACLRequest is a sparse update of an MCP server
 * config ACL: only the listed principals change, and
 * MCPServerConfigRoleDeleted removes an entry.
 */
export interface UpdateMCPServerConfigACLRequest {
	readonly user_roles?: Record<string, MCPServerConfigRole>;
	readonly group_roles?: Record<string, MCPServerConfigRole>;
}

// From codersdk/mcp.go
/**
 * UpdateMCPServerConfigRequest is the request to update an MCP server config.
 */
export interface UpdateMCPServerConfigRequest {
	readonly display_name?: string;
	readonly slug?: string;
	readonly description?: string;
	readonly icon_url?: string;
	readonly transport?: string;
	readonly url?: string;
	readonly auth_type?: string;
	readonly oauth2_client_id?: string;
	readonly oauth2_client_secret?: string;
	readonly oauth2_auth_url?: string;
	readonly oauth2_token_url?: string;
	/**
	 * OAuth2RevocationURL is validated in the handler because a
	 * validate tag would reject the pointer to "" that clears it.
	 */
	readonly oauth2_revocation_url?: string;
	/**
	 * OAuth2Resource is the RFC 8707 resource indicator. Omit it to keep
	 * the stored value; send "" to clear it.
	 */
	readonly oauth2_resource?: string;
	readonly oauth2_scopes?: string;
	readonly api_key_header?: string;
	readonly api_key_value?: string;
	readonly custom_headers?: Record<string, string>;
	readonly tool_allow_list?: string[];
	readonly tool_deny_list?: string[];
	readonly availability?: string;
	readonly enabled?: boolean;
	readonly model_intent?: boolean;
	readonly allow_in_plan_mode?: boolean;
	/**
	 * ForwardCoderHeaders, when set, updates whether Coder identity
	 * headers are forwarded on every outgoing MCP request.
	 */
	readonly forward_coder_headers?: boolean;
	/**
	 * SigningSecret replaces the shared signing key. Omit to preserve it;
	 * an empty string clears it. It is never returned.
	 */
	readonly signing_secret?: string;
}

// From codersdk/notifications.go
export interface UpdateNotificationTemplateMethod {
	readonly method?: string;
}

// From codersdk/chats.go
/**
 * UpdateOrganizationChatSystemPromptRequest is the request body for updating
 * an organization's chat system prompt.
 */
export interface UpdateOrganizationChatSystemPromptRequest {
	readonly system_prompt: string;
}

// From codersdk/organizations.go
export interface UpdateOrganizationRequest {
	readonly name?: string;
	readonly display_name?: string;
	readonly description?: string;
	readonly icon?: string;
	/**
	 * DefaultOrgMemberRoles, when non-nil, replaces the org's default
	 * member roles.
	 */
	readonly default_org_member_roles?: string[];
}

// From codersdk/skills.go
/**
 * UpdateOrganizationSkillACLRequest is a sparse update of an organization
 * skill ACL: only the listed principals change, and
 * OrganizationSkillRoleDeleted removes an entry.
 */
export interface UpdateOrganizationSkillACLRequest {
	readonly user_roles?: Record<string, OrganizationSkillRole>;
	readonly group_roles?: Record<string, OrganizationSkillRole>;
}

// From codersdk/skills.go
/**
 * UpdateOrganizationSkillPreferencesRequest is a sparse update of an
 * organization member's organization skill choices. At least one field must
 * be set, and only the listed skills change.
 */
export interface UpdateOrganizationSkillPreferencesRequest {
	readonly all_skills_disabled?: boolean;
	/**
	 * Skills maps an organization skill ID to whether the skill is enabled
	 * for the member's chats.
	 */
	readonly skills?: Record<string, boolean>;
}

// From codersdk/skills.go
/**
 * UpdateOrganizationSkillSettingsRequest is the payload for updating an
 * organization's member opt-out setting for organization skills.
 */
export interface UpdateOrganizationSkillSettingsRequest {
	readonly members_can_disable_skills: boolean;
}

// From codersdk/users.go
export interface UpdateRoles {
	readonly roles: readonly string[];
}

// From codersdk/skills.go
/**
 * UpdateSkillRequest is the payload for updating a skill. At least one field
 * must be set.
 */
export interface UpdateSkillRequest {
	/**
	 * Content must be SKILL.md-format Markdown with YAML frontmatter. The
	 * frontmatter must include name, may include description, and must be
	 * followed by a non-empty body.
	 */
	readonly content?: string;
	readonly enabled?: boolean;
	/**
	 * Action is only accepted for project skills.
	 */
	readonly action?: boolean;
	readonly model_invocable?: boolean;
}

// From codersdk/templates.go
export interface UpdateTemplateACL {
	/**
	 * UserPerms is a mapping from valid user UUIDs to the template role they
	 * should be granted. To remove a user from the template, use "" as the role
	 * (available as a constant named codersdk.TemplateRoleDeleted)
	 */
	readonly user_perms?: Record<string, TemplateRole>;
	/**
	 * GroupPerms is a mapping from valid group UUIDs to the template role they
	 * should be granted. To remove a group from the template, use "" as the role
	 * (available as a constant named codersdk.TemplateRoleDeleted)
	 */
	readonly group_perms?: Record<string, TemplateRole>;
}

// From codersdk/templates.go
/**
 * UpdateTemplateMeta is the request body for the PATCH /templates/{template}
 * endpoint. All fields are optional. Fields that are nil are not modified.
 */
export interface UpdateTemplateMeta {
	readonly name?: string;
	readonly display_name?: string;
	readonly description?: string;
	readonly icon?: string;
	readonly default_ttl_ms?: number;
	/**
	 * ActivityBumpMillis allows optionally specifying the activity bump
	 * duration for all workspaces created from this template. Defaults to 1h
	 * but can be set to 0 to disable activity bumping.
	 */
	readonly activity_bump_ms?: number;
	/**
	 * TimeTilAutostopNotifyMillis allows optionally specifying the duration
	 * before the autostop deadline at which a reminder notification is sent for
	 * workspaces created from this template. Defaults to 0 (disabled). Omitting
	 * the field keeps the existing value.
	 */
	readonly time_til_autostop_notify_ms?: number;
	/**
	 * AutostopRequirement and AutostartRequirement can only be set if your license
	 * includes the advanced template scheduling feature. If you attempt to set this
	 * value while unlicensed, it will be ignored.
	 */
	readonly autostop_requirement?: TemplateAutostopRequirement;
	readonly autostart_requirement?: TemplateAutostartRequirement;
	readonly allow_user_autostart?: boolean;
	readonly allow_user_autostop?: boolean;
	readonly allow_user_cancel_workspace_jobs?: boolean;
	readonly failure_ttl_ms?: number;
	readonly time_til_dormant_ms?: number;
	readonly time_til_dormant_autodelete_ms?: number;
	/**
	 * UpdateWorkspaceLastUsedAt updates the last_used_at field of workspaces
	 * spawned from the template. This is useful for preventing workspaces being
	 * immediately locked when updating the inactivity_ttl field to a new, shorter
	 * value.
	 */
	readonly update_workspace_last_used_at?: boolean;
	/**
	 * UpdateWorkspaceDormantAt updates the dormant_at field of workspaces spawned
	 * from the template. This is useful for preventing dormant workspaces being
	 * immediately deleted when updating the dormant_ttl field to a new, shorter
	 * value.
	 */
	readonly update_workspace_dormant_at?: boolean;
	/**
	 * RequireActiveVersion mandates workspaces built using this template
	 * use the active version of the template. This option has no
	 * effect on template admins.
	 */
	readonly require_active_version?: boolean;
	/**
	 * DeprecationMessage if set, will mark the template as deprecated and block
	 * any new workspaces from using this template.
	 * If passed an empty string, will remove the deprecated message, making
	 * the template usable for new workspaces again.
	 */
	readonly deprecation_message?: string;
	/**
	 * DisableEveryoneGroupAccess allows optionally disabling the default
	 * behavior of granting the 'everyone' group access to use the template.
	 * If this is set to true, the template will not be available to all users,
	 * and must be explicitly granted to users or groups in the permissions settings
	 * of the template.
	 */
	readonly disable_everyone_group_access?: boolean;
	readonly max_port_share_level?: WorkspaceAgentPortShareLevel;
	readonly cors_behavior?: CORSBehavior;
	/**
	 * UseClassicParameterFlow is a flag that switches the default behavior to use the classic
	 * parameter flow when creating a workspace. This only affects deployments with the experiment
	 * "dynamic-parameters" enabled. This setting will live for a period after the experiment is
	 * made the default.
	 * An "opt-out" is present in case the new feature breaks some existing templates.
	 */
	readonly use_classic_parameter_flow?: boolean;
	/**
	 * DisableModuleCache disables the using of cached Terraform modules during
	 * provisioning. It is ignored while the deployment disables the module
	 * cache for all templates. It is recommended not to disable this.
	 */
	readonly disable_module_cache?: boolean;
	/**
	 * AgentsAllowed controls whether Coder Agents can create workspaces using
	 * this template. If omitted, the current value is preserved.
	 */
	readonly agents_allowed?: boolean;
	/**
	 * AllowWorkspaceRenames permits users to rename workspaces built from this
	 * template. Renaming can be destructive for templates whose Terraform
	 * references the workspace name.
	 */
	readonly allow_workspace_renames?: boolean;
}

// From codersdk/users.go
export interface UpdateUserAppearanceSettingsRequest {
	readonly theme_preference: string;
	/**
	 * ThemeMode is optional for backward compatibility. When empty,
	 * the server leaves theme_mode, theme_light, and theme_dark
	 * unchanged so older CLI clients do not erase sync-mode settings.
	 * Legacy auto preferences are the exception: they clear theme_mode
	 * so clients can migrate the old sync-with-system setting.
	 */
	readonly theme_mode: ThemeMode;
	/**
	 * ThemeLight is required when ThemeMode is "sync". In "single"
	 * mode an empty value means "preserve the previously persisted
	 * slot" rather than "clear the slot", so partial updates that send
	 * only one slot keep the other intact.
	 */
	readonly theme_light: string;
	/**
	 * ThemeDark is required when ThemeMode is "sync". In "single" mode
	 * an empty value means "preserve the previously persisted slot"
	 * rather than "clear the slot", so partial updates that send only
	 * one slot keep the other intact.
	 */
	readonly theme_dark: string;
	readonly terminal_font: TerminalFontName;
}

// From codersdk/chats.go
/**
 * UpdateUserChatCompactionThresholdRequest sets a user's per-model
 * chat compaction threshold override.
 */
export interface UpdateUserChatCompactionThresholdRequest {
	readonly threshold_percent: number;
}

// From codersdk/chats.go
/**
 * UpdateUserChatDebugLoggingRequest is the per-user request to
 * opt into or out of chat debug logging.
 */
export interface UpdateUserChatDebugLoggingRequest {
	readonly debug_logging_enabled: boolean;
}

// From codersdk/chats.go
/**
 * UpdateUserChatPersonalModelOverrideRequest is the request body for updating
 * a user personal model override.
 */
export interface UpdateUserChatPersonalModelOverrideRequest {
	readonly mode: ChatPersonalModelOverrideMode;
	readonly model_config_id: string;
	readonly reasoning_effort?: string;
}

// From codersdk/users.go
/**
 * UpdateUserEmailRequest changes a user's email by matching their current
 * email address. This API is experimental and may change without notice.
 */
export interface UpdateUserEmailRequest {
	readonly old_email: string;
	readonly new_email: string;
}

// From codersdk/notifications.go
export interface UpdateUserNotificationPreferences {
	readonly template_disabled_map: Record<string, boolean>;
}

// From codersdk/users.go
export interface UpdateUserPasswordRequest {
	readonly old_password: string;
	readonly password: string;
}

// From codersdk/users.go
export interface UpdateUserPreferenceSettingsRequest {
	readonly thinking_display_mode?: ThinkingDisplayMode;
	readonly shell_tool_display_mode?: AgentDisplayMode;
	readonly code_diff_display_mode?: AgentDisplayMode;
	readonly collapse_assistant_steps?: boolean;
	readonly agent_chat_send_shortcut?: AgentChatSendShortcut;
}

// From codersdk/users.go
export interface UpdateUserProfileRequest {
	readonly username: string;
	readonly name: string;
	/**
	 * AvatarURL is only applied for users whose login type is password or
	 * none. For other login types the avatar is synced from the identity
	 * provider on login, so a submitted value is ignored.
	 */
	readonly avatar_url: string;
}

// From codersdk/users.go
export interface UpdateUserQuietHoursScheduleRequest {
	/**
	 * Schedule is a cron expression that defines when the user's quiet hours
	 * window is. Schedule must not be empty. For new users, the schedule is set
	 * to 2am in their browser or computer's timezone. The schedule denotes the
	 * beginning of a 4 hour window where the workspace is allowed to
	 * automatically stop or restart due to maintenance or template schedule.
	 *
	 * The schedule must be daily with a single time, and should have a timezone
	 * specified via a CRON_TZ prefix (otherwise UTC will be used).
	 *
	 * If the schedule is empty, the user will be updated to use the default
	 * schedule.
	 */
	readonly schedule: string;
}

// From codersdk/usersecrets.go
/**
 * UpdateUserSecretRequest is the payload for partially updating a
 * user secret. At least one field must be non-nil. Pointer fields
 * distinguish "not sent" (nil) from "set to empty string" (pointer
 * to empty string). If the post-update row is enabled it must still
 * have at least one of EnvName or FilePath non-empty; clearing both
 * targets is only allowed when the secret is (or becomes) disabled.
 * When a deployment disables file path delivery, an enabled row also
 * requires EnvName.
 */
export interface UpdateUserSecretRequest {
	readonly value?: string;
	readonly description?: string;
	readonly env_name?: string;
	readonly file_path?: string;
	readonly enabled?: boolean;
}

// From codersdk/workspaces.go
export interface UpdateWorkspaceACL {
	/**
	 * UserRoles is a mapping from valid user UUIDs to the workspace role they
	 * should be granted. To remove a user from the workspace, use "" as the role
	 * (available as a constant named codersdk.WorkspaceRoleDeleted)
	 */
	readonly user_roles?: Record<string, WorkspaceRole>;
	/**
	 * GroupRoles is a mapping from valid group UUIDs to the workspace role they
	 * should be granted. To remove a group from the workspace, use "" as the role
	 * (available as a constant named codersdk.WorkspaceRoleDeleted)
	 */
	readonly group_roles?: Record<string, WorkspaceRole>;
}

// From codersdk/workspaces.go
/**
 * UpdateWorkspaceAutomaticUpdatesRequest is a request to updates a workspace's automatic updates setting.
 */
export interface UpdateWorkspaceAutomaticUpdatesRequest {
	readonly automatic_updates: AutomaticUpdates;
}

// From codersdk/workspaces.go
/**
 * UpdateWorkspaceAutostartRequest is a request to update a workspace's autostart schedule.
 */
export interface UpdateWorkspaceAutostartRequest {
	/**
	 * Schedule is expected to be of the form `CRON_TZ=<IANA Timezone> <min> <hour> * * <dow>`
	 * Example: `CRON_TZ=US/Central 30 9 * * 1-5` represents 0930 in the timezone US/Central
	 * on weekdays (Mon-Fri). `CRON_TZ` defaults to UTC if not present.
	 */
	readonly schedule?: string;
}

// From codersdk/workspacebuilds.go
/**
 * UpdateWorkspaceBuildStateRequest is the request body for updating the
 * provisioner state of a workspace build.
 */
export interface UpdateWorkspaceBuildStateRequest {
	readonly state: string;
}

// From codersdk/workspaces.go
/**
 * UpdateWorkspaceDormancy is a request to activate or make a workspace dormant.
 * A value of false will activate a dormant workspace.
 */
export interface UpdateWorkspaceDormancy {
	readonly dormant: boolean;
}

// From codersdk/workspaceproxy.go
export interface UpdateWorkspaceProxyResponse {
	readonly proxy: WorkspaceProxy;
	readonly proxy_token: string;
}

// From codersdk/workspaces.go
export interface UpdateWorkspaceRequest {
	readonly name?: string;
}

// From codersdk/workspacesharing.go
/**
 * UpdateWorkspaceSharingSettingsRequest represents workspace sharing settings
 * that can be updated for an organization.
 */
export interface UpdateWorkspaceSharingSettingsRequest {
	/**
	 * SharingDisabled is deprecated and left for backward compatibility
	 * purposes.
	 * @deprecated use `ShareableWorkspaceOwners` instead
	 */
	readonly sharing_disabled?: boolean;
	/**
	 * ShareableWorkspaceOwners controls whose workspaces can be shared
	 * within the organization.
	 */
	readonly shareable_workspace_owners?: ShareableWorkspaceOwners;
}

// From codersdk/workspaces.go
/**
 * UpdateWorkspaceTTLRequest is a request to update a workspace's TTL.
 */
export interface UpdateWorkspaceTTLRequest {
	readonly ttl_ms: number | null;
}

// From codersdk/chats.go
/**
 * UploadChatFileResponse is the response from uploading a chat file.
 */
export interface UploadChatFileResponse {
	readonly id: string;
}

// From codersdk/chats.go
/**
 * UploadChatWorkspaceFileResponse describes a file uploaded to a
 * chat's workspace filesystem.
 */
export interface UploadChatWorkspaceFileResponse {
	/**
	 * Path is the absolute path of the file on the workspace.
	 */
	readonly path: string;
	/**
	 * Name is the final basename of the uploaded file.
	 */
	readonly name: string;
	/**
	 * Size is the number of bytes written to the workspace.
	 */
	readonly size: number;
	/**
	 * MediaType is the client-declared content type for display.
	 */
	readonly media_type: string;
	/**
	 * WorkspaceID is the workspace whose filesystem received the
	 * bytes. Message parts referencing this upload must carry it.
	 */
	readonly workspace_id: string;
}

// From codersdk/files.go
/**
 * UploadResponse contains the hash to reference the uploaded file.
 */
export interface UploadResponse {
	readonly hash: string;
}

// From codersdk/aimodelprices.go
/**
 * UpsertAIModelPricesRequest sets prices for the listed models. Models absent
 * from the request are left untouched.
 */
export interface UpsertAIModelPricesRequest {
	readonly prices: readonly AIModelPriceUpsert[];
}

// From codersdk/aibridge.go
export interface UpsertGroupAIBudgetRequest {
	/**
	 * SpendLimitMicros must not exceed MaxAISpendLimitMicros.
	 */
	readonly spend_limit_micros: number;
}

// From codersdk/aibridge.go
export interface UpsertUserAIBudgetOverrideRequest {
	/**
	 * GroupID is the group the user's spend is attributed to. The user must
	 * be a member of this group.
	 */
	readonly group_id: string;
	/**
	 * SpendLimitMicros must not exceed MaxAISpendLimitMicros.
	 */
	readonly spend_limit_micros: number;
}

// From codersdk/workspaceagentportshare.go
export interface UpsertWorkspaceAgentPortShareRequest {
	readonly agent_name: string;
	readonly port: number;
	readonly share_level: WorkspaceAgentPortShareLevel;
	readonly protocol: WorkspaceAgentPortShareProtocol;
}

// From codersdk/workspaces.go
export type UsageAppName =
	| "jetbrains"
	| "port_forwarding"
	| "reconnecting-pty"
	| "ssh"
	| "vscode";

export const UsageAppNames: UsageAppName[] = [
	"jetbrains",
	"port_forwarding",
	"reconnecting-pty",
	"ssh",
	"vscode",
];

// From codersdk/deployment.go
export interface UsagePeriod {
	readonly issued_at: string;
	readonly start: string;
	readonly end: string;
}

// From codersdk/deployment.go
export interface UsageStatsConfig {
	readonly enable: boolean;
}

// From codersdk/users.go
/**
 * User represents a user in Coder.
 */
export interface User extends ReducedUser {
	readonly organization_ids: readonly string[];
	readonly roles: readonly SlimRole[];
	/**
	 * HasAISeat intentionally omits omitempty so the API always includes the
	 * field, even when false.
	 */
	readonly has_ai_seat: boolean;
}

// From codersdk/aibridge.go
export interface UserAIBudgetOverride {
	readonly user_id: string;
	readonly group_id: string;
	readonly spend_limit_micros: number;
	readonly created_at: string;
	readonly updated_at: string;
}

// From codersdk/aibridge.go
/**
 * UserAIBudgetSummary is the effective AI budget for a user. When no budget
 * applies, the effective group falls back to the Everyone group with a null
 * budget.
 */
export interface UserAIBudgetSummary {
	readonly user_id: string;
	/**
	 * EffectiveGroupID is the group the spend is attributed to, falling back to
	 * the Everyone group when no budget applies. Null only when the user has no
	 * organization membership.
	 */
	readonly effective_group_id: string | null;
	/**
	 * EffectiveBudget is the spend limit that applies to the user, whether it
	 * came from a group budget or a user override. Null when no budget
	 * applies, leaving the user's spend unlimited.
	 */
	readonly effective_budget: AIBudgetLimit | null;
}

// From codersdk/chats.go
/**
 * UserAIProviderKeyConfig is a provider summary from the current user's
 * perspective. It reports key presence but never returns key material.
 */
export interface UserAIProviderKeyConfig {
	readonly provider: AIProviderSummary;
	readonly has_user_api_key: boolean;
	readonly has_provider_api_key: boolean;
	readonly byok_enabled: boolean;
}

// From codersdk/aibridge.go
/**
 * UserAISpendStatus is the current AI spend snapshot for a user within
 * the active budget period.
 */
export interface UserAISpendStatus
	extends UserAIBudgetSummary,
		AISpendPeriodWindow {
	/**
	 * CurrentSpendMicros is the user's spend on their effective group over
	 * the current budget period.
	 */
	readonly current_spend_micros: number;
}

// From codersdk/insights.go
/**
 * UserActivity shows the session time for a user.
 */
export interface UserActivity {
	readonly template_ids: readonly string[];
	readonly user_id: string;
	readonly username: string;
	readonly avatar_url: string;
	readonly seconds: number;
}

// From codersdk/insights.go
/**
 * UserActivityInsightsReport is the report from the user activity insights
 * endpoint.
 */
export interface UserActivityInsightsReport {
	readonly start_time: string;
	readonly end_time: string;
	readonly template_ids: readonly string[];
	readonly users: readonly UserActivity[];
}

// From codersdk/insights.go
export interface UserActivityInsightsRequest {
	readonly start_time: string;
	readonly end_time: string;
	readonly template_ids: readonly string[];
}

// From codersdk/insights.go
/**
 * UserActivityInsightsResponse is the response from the user activity insights
 * endpoint.
 */
export interface UserActivityInsightsResponse {
	readonly report: UserActivityInsightsReport;
}

// From codersdk/users.go
export interface UserAppearanceSettings {
	/**
	 * ThemePreference is the legacy single-field appearance setting. In
	 * "single" mode it mirrors the active theme. In "sync" mode modern
	 * clients normally mirror the active OS slot, but older clients can
	 * update only this field, so it may diverge from ThemeLight or
	 * ThemeDark until a modern client saves the full appearance state
	 * again.
	 */
	readonly theme_preference: string;
	readonly theme_mode: ThemeMode;
	/**
	 * Ignored when ThemeMode is "single"
	 */
	readonly theme_light: string;
	/**
	 * Ignored when ThemeMode is "single"
	 */
	readonly theme_dark: string;
	readonly terminal_font: TerminalFontName;
}

// From codersdk/chats.go
/**
 * UserChatCompactionThreshold is a user's per-model chat compaction
 * threshold override.
 */
export interface UserChatCompactionThreshold {
	readonly model_config_id: string;
	readonly threshold_percent: number;
}

// From codersdk/chats.go
/**
 * UserChatCompactionThresholds wraps the user's per-model chat
 * compaction threshold overrides.
 */
export interface UserChatCompactionThresholds {
	readonly thresholds: readonly UserChatCompactionThreshold[];
}

// From codersdk/chats.go
/**
 * UserChatCustomPrompt is the request and response body for the
 * user chat custom prompt configuration endpoint.
 */
export interface UserChatCustomPrompt {
	readonly custom_prompt: string;
}

// From codersdk/chats.go
/**
 * UserChatDebugLoggingSettings describes whether debug logging is
 * active for the current user and whether the user may control it.
 */
export interface UserChatDebugLoggingSettings {
	readonly debug_logging_enabled: boolean;
	readonly user_toggle_allowed: boolean;
	readonly forced_by_deployment: boolean;
}

// From codersdk/chats.go
/**
 * UserChatPersonalModelOverridesResponse is the response body for user
 * personal model override settings.
 */
export interface UserChatPersonalModelOverridesResponse {
	readonly enabled: boolean;
	readonly root: ChatPersonalModelOverride;
	readonly general: ChatPersonalModelOverride;
	readonly explore: ChatPersonalModelOverride;
	readonly deployment_defaults: ChatPersonalModelOverrideDeploymentDefaults;
}

// From codersdk/chats.go
/**
 * UserChatProviderConfig is a summary of a provider that allows
 * user-supplied keys, as seen from the current user's perspective.
 */
export interface UserChatProviderConfig {
	readonly provider_id: string;
	readonly provider: string;
	readonly display_name: string;
	readonly icon: string;
	readonly enabled: boolean;
	readonly has_user_api_key: boolean;
	readonly has_central_api_key_fallback: boolean;
	readonly byok_enabled: boolean;
}

// From codersdk/insights.go
/**
 * UserLatency shows the connection latency for a user.
 */
export interface UserLatency {
	readonly template_ids: readonly string[];
	readonly user_id: string;
	readonly username: string;
	readonly avatar_url: string;
	readonly latency_ms: ConnectionLatency;
}

// From codersdk/insights.go
/**
 * UserLatencyInsightsReport is the report from the user latency insights
 * endpoint.
 */
export interface UserLatencyInsightsReport {
	readonly start_time: string;
	readonly end_time: string;
	readonly template_ids: readonly string[];
	readonly users: readonly UserLatency[];
}

// From codersdk/insights.go
export interface UserLatencyInsightsRequest {
	readonly start_time: string;
	readonly end_time: string;
	readonly template_ids: readonly string[];
}

// From codersdk/insights.go
/**
 * UserLatencyInsightsResponse is the response from the user latency insights
 * endpoint.
 */
export interface UserLatencyInsightsResponse {
	readonly report: UserLatencyInsightsReport;
}

// From codersdk/users.go
export interface UserLoginType {
	readonly login_type: LoginType;
}

// From codersdk/users.go
export interface UserPreferenceSettings {
	readonly thinking_display_mode: ThinkingDisplayMode;
	readonly shell_tool_display_mode: AgentDisplayMode;
	readonly code_diff_display_mode: AgentDisplayMode;
	readonly collapse_assistant_steps: boolean;
	readonly agent_chat_send_shortcut: AgentChatSendShortcut;
}

// From codersdk/deployment.go
export interface UserQuietHoursScheduleConfig {
	readonly default_schedule: string;
	readonly allow_user_custom: boolean;
}

// From codersdk/users.go
export interface UserQuietHoursScheduleResponse {
	readonly raw_schedule: string;
	/**
	 * UserSet is true if the user has set their own quiet hours schedule. If
	 * false, the user is using the default schedule.
	 */
	readonly user_set: boolean;
	/**
	 * UserCanSet is true if the user is allowed to set their own quiet hours
	 * schedule. If false, the user cannot set a custom schedule and the default
	 * schedule will always be used.
	 */
	readonly user_can_set: boolean;
	/**
	 * Time is the time of day that the quiet hours window starts in the given
	 * Timezone each day.
	 */
	readonly time: string; // HH:mm (24-hour)
	readonly timezone: string; // raw format from the cron expression, UTC if unspecified
	/**
	 * Next is the next time that the quiet hours window will start.
	 */
	readonly next: string;
}

// From codersdk/users.go
export interface UserRoles {
	readonly roles: readonly string[];
	readonly organization_roles: Record<string, string[]>;
}

// From codersdk/usersecrets.go
/**
 * UserSecret represents a user secret's metadata. The secret value
 * is never included in API responses.
 */
export interface UserSecret {
	readonly id: string;
	readonly name: string;
	readonly description: string;
	readonly env_name: string;
	readonly file_path: string;
	/**
	 * Enabled controls whether the secret is injected into workspaces.
	 * Disabled secrets remain visible and editable, but are not added
	 * to the agent manifest, so they are not exposed as environment
	 * variables or written to secret files.
	 */
	readonly enabled: boolean;
	readonly created_at: string;
	readonly updated_at: string;
}

// From codersdk/usersecretvalidation.go
/**
 * UserSecret*Field constants are the canonical ValidationError.Field values
 * for user secret fields. UserSecretNameField is also the chi URL parameter
 * name used in coderd route segments.
 */
export const UserSecretEnvNameField = "env_name";

// From codersdk/usersecretvalidation.go
/**
 * UserSecret*Field constants are the canonical ValidationError.Field values
 * for user secret fields. UserSecretNameField is also the chi URL parameter
 * name used in coderd route segments.
 */
export const UserSecretFilePathField = "file_path";

// From codersdk/usersecretvalidation.go
/**
 * UserSecretInjectionTargetRequiredDetail explains the injection-target
 * invariant. It is shared by the create validator above and the PATCH
 * handler's post-state check in coderd. The value is a user-facing
 * validation message, not a credential.
 */
export const UserSecretInjectionTargetRequiredDetail =
	"An enabled secret must have at least one of env_name or file_path set. To keep a secret without injecting it, set enabled to false instead of clearing both targets."; //nolint:gosec // G101: message text, not a hardcoded credential.

// From codersdk/usersecretvalidation.go
/**
 * UserSecret*Field constants are the canonical ValidationError.Field values
 * for user secret fields. UserSecretNameField is also the chi URL parameter
 * name used in coderd route segments.
 */
export const UserSecretNameField = "name";

// From codersdk/usersecretvalidation.go
/**
 * UserSecret*Field constants are the canonical ValidationError.Field values
 * for user secret fields. UserSecretNameField is also the chi URL parameter
 * name used in coderd route segments.
 */
export const UserSecretValueField = "value";

// From codersdk/usersecrets.go
/**
 * UserSecretsCapabilities reports which user secret delivery targets the
 * deployment allows. Any authenticated user can read it, unlike the full
 * deployment configuration.
 */
export interface UserSecretsCapabilities {
	/**
	 * FilePathDeliveryEnabled reports whether Coder writes stored file paths
	 * into workspaces. Stored paths are preserved either way.
	 */
	readonly file_path_delivery_enabled: boolean;
}

// From codersdk/users.go
export type UserStatus = "active" | "dormant" | "suspended";

// From codersdk/insights.go
export interface UserStatusChangeCount {
	readonly date: string;
	readonly count: number;
}

export const UserStatuses: UserStatus[] = ["active", "dormant", "suspended"];

// From codersdk/users.go
export interface UsersRequest extends Pagination {
	readonly q?: string;
}

// From codersdk/users.go
export interface ValidateUserPasswordRequest {
	readonly password: string;
}

// From codersdk/users.go
export interface ValidateUserPasswordResponse {
	readonly valid: boolean;
	readonly details: string;
}

// From codersdk/client.go
/**
 * ValidationError represents a scoped error to a user input.
 */
export interface ValidationError {
	readonly field: string;
	readonly detail: string;
}

// From codersdk/templateversions.go
export type ValidationMonotonicOrder = "decreasing" | "increasing";

export const ValidationMonotonicOrders: ValidationMonotonicOrder[] = [
	"decreasing",
	"increasing",
];

// From codersdk/organizations.go
export interface VariableValue {
	readonly name: string;
	readonly value: string;
}

// From codersdk/notifications.go
export interface WebpushMessage {
	readonly icon: string;
	readonly title: string;
	readonly body: string;
	readonly tag?: string;
	readonly actions: readonly WebpushMessageAction[];
	readonly data?: Record<string, string>;
}

// From codersdk/notifications.go
export interface WebpushMessageAction {
	readonly label: string;
	readonly url: string;
}

// From codersdk/notifications.go
export interface WebpushSubscription {
	readonly endpoint: string;
	readonly auth_key: string;
	readonly p256dh_key: string;
}

// From healthsdk/healthsdk.go
/**
 * WebsocketReport shows if the configured access URL allows establishing WebSocket connections.
 */
export interface WebsocketReport extends BaseReport {
	/**
	 * Healthy is deprecated and left for backward compatibility purposes, use `Severity` instead.
	 */
	readonly healthy: boolean;
	readonly body: string;
	readonly code: number;
}

// From codersdk/workspaces.go
/**
 * Workspace is a deployment of a template. It references a specific
 * version and can be updated.
 */
export interface Workspace {
	readonly id: string;
	readonly created_at: string;
	readonly updated_at: string;
	readonly owner_id: string;
	/**
	 * OwnerName is the username of the owner of the workspace.
	 */
	readonly owner_name: string;
	readonly owner_avatar_url: string;
	readonly organization_id: string;
	readonly organization_name: string;
	readonly template_id: string;
	readonly template_name: string;
	readonly template_display_name: string;
	readonly template_icon: string;
	readonly template_allow_user_cancel_workspace_jobs: boolean;
	readonly template_active_version_id: string;
	readonly template_require_active_version: boolean;
	readonly template_use_classic_parameter_flow: boolean;
	readonly latest_build: WorkspaceBuild;
	readonly latest_app_status: WorkspaceAppStatus | null;
	readonly outdated: boolean;
	readonly name: string;
	readonly autostart_schedule?: string;
	readonly ttl_ms?: number;
	readonly last_used_at: string;
	/**
	 * DeletingAt indicates the time at which the workspace will be permanently deleted.
	 * A workspace is eligible for deletion if it is dormant (a non-nil dormant_at value)
	 * and a value has been specified for time_til_dormant_autodelete on its template.
	 */
	readonly deleting_at: string | null;
	/**
	 * DormantAt being non-nil indicates a workspace that is dormant.
	 * A dormant workspace is no longer accessible must be activated.
	 * It is subject to deletion if it breaches
	 * the duration of the time_til_ field on its template.
	 */
	readonly dormant_at: string | null;
	/**
	 * Health shows the health of the workspace and information about
	 * what is causing an unhealthy status.
	 */
	readonly health: WorkspaceHealth;
	readonly automatic_updates: AutomaticUpdates;
	/**
	 * AllowRenames is the effective rename permission for this workspace,
	 * derived from the template's allow_workspace_renames setting and the
	 * deprecated deployment-wide flag.
	 */
	readonly allow_renames: boolean;
	readonly favorite: boolean;
	readonly next_start_at: string | null;
	/**
	 * IsPrebuild indicates whether the workspace is a prebuilt workspace.
	 * Prebuilt workspaces are owned by the prebuilds system user and have specific behavior,
	 * such as being managed differently from regular workspaces.
	 * Once a prebuilt workspace is claimed by a user, it transitions to a regular workspace,
	 * and IsPrebuild returns false.
	 */
	readonly is_prebuild: boolean;
	readonly shared_with?: readonly SharedWorkspaceActor[];
}

// From codersdk/workspaces.go
export interface WorkspaceACL {
	readonly users: readonly WorkspaceUser[];
	readonly group: readonly WorkspaceGroup[];
}

// From codersdk/workspaceagents.go
export interface WorkspaceAgent {
	readonly id: string;
	readonly parent_id: string | null;
	readonly created_at: string;
	readonly updated_at: string;
	readonly first_connected_at?: string;
	readonly last_connected_at?: string;
	readonly disconnected_at?: string;
	readonly started_at?: string;
	readonly ready_at?: string;
	readonly status: WorkspaceAgentStatus;
	readonly lifecycle_state: WorkspaceAgentLifecycle;
	readonly name: string;
	readonly resource_id: string;
	readonly instance_id?: string;
	readonly architecture: string;
	readonly environment_variables: Record<string, string>;
	readonly operating_system: string;
	readonly logs_length: number;
	readonly logs_overflowed: boolean;
	readonly directory?: string;
	readonly expanded_directory?: string;
	readonly version: string;
	readonly api_version: string;
	readonly apps: readonly WorkspaceApp[];
	/**
	 * DERPLatency is mapped by region name (e.g. "New York City", "Seattle").
	 */
	readonly latency?: Record<string, DERPRegion>;
	readonly connection_timeout_seconds: number;
	readonly troubleshooting_url: string;
	readonly subsystems: readonly AgentSubsystem[];
	readonly health: WorkspaceAgentHealth; // Health reports the health of the agent.
	readonly display_apps: readonly DisplayApp[];
	readonly log_sources: readonly WorkspaceAgentLogSource[];
	readonly scripts: readonly WorkspaceAgentScript[];
	/**
	 * Metadata is only populated on the workspaces list endpoint when the
	 * request opts in with the include_agent_metadata search key, and it
	 * only carries the requested keys. The description's script is always
	 * empty here: it can be long, and list consumers want values.
	 */
	readonly metadata?: readonly WorkspaceAgentMetadata[];
	/**
	 * StartupScriptBehavior is a legacy field that is deprecated in favor
	 * of the `coder_script` resource. It's only referenced by old clients.
	 * @deprecated Remove in the future!
	 */
	readonly startup_script_behavior: WorkspaceAgentStartupScriptBehavior;
}

// From codersdk/workspaceagents.go
/**
 * WorkspaceAgentContainer describes a devcontainer of some sort
 * that is visible to the workspace agent. This struct is an abstraction
 * of potentially multiple implementations, and the fields will be
 * somewhat implementation-dependent.
 */
export interface WorkspaceAgentContainer {
	/**
	 * CreatedAt is the time the container was created.
	 */
	readonly created_at: string;
	/**
	 * ID is the unique identifier of the container.
	 */
	readonly id: string;
	/**
	 * FriendlyName is the human-readable name of the container.
	 */
	readonly name: string;
	/**
	 * Image is the name of the container image.
	 */
	readonly image: string;
	/**
	 * Labels is a map of key-value pairs of container labels.
	 */
	readonly labels: Record<string, string>;
	/**
	 * Running is true if the container is currently running.
	 */
	readonly running: boolean;
	/**
	 * Ports includes ports exposed by the container.
	 */
	readonly ports: readonly WorkspaceAgentContainerPort[];
	/**
	 * Status is the current status of the container. This is somewhat
	 * implementation-dependent, but should generally be a human-readable
	 * string.
	 */
	readonly status: string;
	/**
	 * Volumes is a map of "things" mounted into the container. Again, this
	 * is somewhat implementation-dependent.
	 */
	readonly volumes: Record<string, string>;
}

// From codersdk/workspaceagents.go
/**
 * WorkspaceAgentContainerPort describes a port as exposed by a container.
 */
export interface WorkspaceAgentContainerPort {
	/**
	 * Port is the port number *inside* the container.
	 */
	readonly port: number;
	/**
	 * Network is the network protocol used by the port (tcp, udp, etc).
	 */
	readonly network: string;
	/**
	 * HostIP is the IP address of the host interface to which the port is
	 * bound. Note that this can be an IPv4 or IPv6 address.
	 */
	readonly host_ip?: string;
	/**
	 * HostPort is the port number *outside* the container.
	 */
	readonly host_port?: number;
}

// From codersdk/workspaceagents.go
/**
 * WorkspaceAgentDevcontainer defines the location of a devcontainer
 * configuration in a workspace that is visible to the workspace agent.
 */
export interface WorkspaceAgentDevcontainer {
	readonly id: string;
	readonly name: string;
	readonly workspace_folder: string;
	readonly config_path?: string;
	readonly subagent_id?: string;
	/**
	 * Additional runtime fields.
	 */
	readonly status: WorkspaceAgentDevcontainerStatus;
	readonly dirty: boolean;
	readonly container?: WorkspaceAgentContainer;
	readonly agent?: WorkspaceAgentDevcontainerAgent;
	readonly error?: string;
}

// From codersdk/workspaceagents.go
/**
 * WorkspaceAgentDevcontainerAgent represents the sub agent for a
 * devcontainer.
 */
export interface WorkspaceAgentDevcontainerAgent {
	readonly id: string;
	readonly name: string;
	readonly directory: string;
}

// From codersdk/workspaceagents.go
export type WorkspaceAgentDevcontainerStatus =
	| "deleting"
	| "error"
	| "running"
	| "starting"
	| "stopped"
	| "stopping";

export const WorkspaceAgentDevcontainerStatuses: WorkspaceAgentDevcontainerStatus[] =
	["deleting", "error", "running", "starting", "stopped", "stopping"];

// From codersdk/workspaceagents.go
/**
 * WorkspaceAgentGitClientMessage is a message sent from the client to
 * the agent over the git watch WebSocket.
 */
export interface WorkspaceAgentGitClientMessage {
	readonly type: WorkspaceAgentGitClientMessageType;
}

// From codersdk/workspaceagents.go
export type WorkspaceAgentGitClientMessageType = "refresh";

export const WorkspaceAgentGitClientMessageTypes: WorkspaceAgentGitClientMessageType[] =
	["refresh"];

// From codersdk/workspaceagents.go
/**
 * WorkspaceAgentGitServerMessage is a message sent from the agent to
 * the client over the git watch WebSocket.
 */
export interface WorkspaceAgentGitServerMessage {
	readonly type: WorkspaceAgentGitServerMessageType;
	readonly scanned_at?: string;
	readonly repositories?: readonly WorkspaceAgentRepoChanges[];
	readonly message?: string;
}

// From codersdk/workspaceagents.go
export type WorkspaceAgentGitServerMessageType = "changes" | "error";

export const WorkspaceAgentGitServerMessageTypes: WorkspaceAgentGitServerMessageType[] =
	["changes", "error"];

// From codersdk/workspaceagents.go
export interface WorkspaceAgentHealth {
	readonly healthy: boolean; // Healthy is true if the agent is healthy.
	readonly reason?: string; // Reason is a human-readable explanation of the agent's health. It is empty if Healthy is true.
}

// From codersdk/workspaceagents.go
export type WorkspaceAgentLifecycle =
	| "created"
	| "off"
	| "ready"
	| "shutdown_error"
	| "shutdown_timeout"
	| "shutting_down"
	| "start_error"
	| "start_timeout"
	| "starting";

export const WorkspaceAgentLifecycles: WorkspaceAgentLifecycle[] = [
	"created",
	"off",
	"ready",
	"shutdown_error",
	"shutdown_timeout",
	"shutting_down",
	"start_error",
	"start_timeout",
	"starting",
];

// From codersdk/workspaceagents.go
/**
 * WorkspaceAgentListContainersResponse is the response to the list containers
 * request.
 */
export interface WorkspaceAgentListContainersResponse {
	/**
	 * Devcontainers is a list of devcontainers visible to the workspace agent.
	 */
	readonly devcontainers: readonly WorkspaceAgentDevcontainer[];
	/**
	 * Containers is a list of containers visible to the workspace agent.
	 */
	readonly containers: readonly WorkspaceAgentContainer[];
	/**
	 * Warnings is a list of warnings that may have occurred during the
	 * process of listing containers. This should not include fatal errors.
	 */
	readonly warnings?: readonly string[];
}

// From codersdk/workspaceagents.go
export interface WorkspaceAgentListeningPort {
	readonly process_name: string; // may be empty
	readonly network: string; // only "tcp" at the moment
	readonly port: number;
}

// From codersdk/workspaceagents.go
export interface WorkspaceAgentListeningPortsResponse {
	/**
	 * If there are no ports in the list, nothing should be displayed in the UI.
	 * There must not be a "no ports available" message or anything similar, as
	 * there will always be no ports displayed on platforms where our port
	 * detection logic is unsupported.
	 */
	readonly ports: readonly WorkspaceAgentListeningPort[];
}

// From codersdk/workspaceagents.go
export interface WorkspaceAgentLog {
	readonly id: number;
	readonly created_at: string;
	readonly output: string;
	readonly level: LogLevel;
	readonly source_id: string;
}

// From codersdk/workspaceagents.go
export interface WorkspaceAgentLogSource {
	readonly workspace_agent_id: string;
	readonly id: string;
	readonly created_at: string;
	readonly display_name: string;
	readonly icon: string;
}

// From codersdk/workspaceagents.go
export interface WorkspaceAgentMetadata {
	readonly result: WorkspaceAgentMetadataResult;
	readonly description: WorkspaceAgentMetadataDescription;
}

// From codersdk/workspaceagents.go
/**
 * WorkspaceAgentMetadataDescription is a description of dynamic metadata the agent should report
 * back to coderd. It is provided via the `metadata` list in the `coder_agent`
 * block.
 */
export interface WorkspaceAgentMetadataDescription {
	readonly display_name: string;
	readonly key: string;
	readonly script: string;
	readonly interval: number;
	readonly timeout: number;
}

// From codersdk/workspaceagents.go
export interface WorkspaceAgentMetadataResult {
	readonly collected_at: string;
	/**
	 * Age is the number of seconds since the metadata was collected.
	 * It is provided in addition to CollectedAt to protect against clock skew.
	 */
	readonly age: number;
	readonly value: string;
	readonly error: string;
}

// From codersdk/workspaceagentportshare.go
export interface WorkspaceAgentPortShare {
	readonly workspace_id: string;
	readonly agent_name: string;
	readonly port: number;
	readonly share_level: WorkspaceAgentPortShareLevel;
	readonly protocol: WorkspaceAgentPortShareProtocol;
}

// From codersdk/workspaceagentportshare.go
export type WorkspaceAgentPortShareLevel =
	| "authenticated"
	| "organization"
	| "owner"
	| "public";

export const WorkspaceAgentPortShareLevels: WorkspaceAgentPortShareLevel[] = [
	"authenticated",
	"organization",
	"owner",
	"public",
];

// From codersdk/workspaceagentportshare.go
export type WorkspaceAgentPortShareProtocol = "http" | "https";

export const WorkspaceAgentPortShareProtocols: WorkspaceAgentPortShareProtocol[] =
	["http", "https"];

// From codersdk/workspaceagentportshare.go
export interface WorkspaceAgentPortShares {
	readonly shares: readonly WorkspaceAgentPortShare[];
}

// From codersdk/workspaceagents.go
/**
 * WorkspaceAgentRepoChanges describes the current state of a single
 * git repository's working tree. When Removed is true the repo root
 * directory or its .git subdirectory no longer exists; all other
 * fields (Branch, RemoteOrigin, UnifiedDiff) are empty/zero.
 */
export interface WorkspaceAgentRepoChanges {
	readonly repo_root: string;
	readonly branch: string;
	readonly remote_origin?: string;
	readonly unified_diff?: string;
	readonly removed?: boolean;
}

// From codersdk/workspaceagents.go
export interface WorkspaceAgentScript {
	readonly id: string;
	readonly log_source_id: string;
	readonly log_path: string;
	readonly script: string;
	readonly cron: string;
	readonly run_on_start: boolean;
	readonly run_on_stop: boolean;
	readonly start_blocks_login: boolean;
	readonly timeout: number;
	readonly display_name: string;
	/**
	 * ResourceAddress is the full Terraform address of the coder_script
	 * resource, for example "module.git_clone.coder_script.clone". It is
	 * empty for scripts that are not coder_script resources.
	 */
	readonly resource_address: string;
	/**
	 * Dependencies lists the scripts this one waits for, as the
	 * provisioner resolved them. It is omitted when empty.
	 */
	readonly dependencies?: readonly WorkspaceAgentScriptDependency[];
	readonly exit_code?: number;
	readonly status?: WorkspaceAgentScriptStatus;
}

// From codersdk/workspaceagents.go
/**
 * WorkspaceAgentScriptDependency names a prerequisite script and the
 * outcome it must reach.
 */
export interface WorkspaceAgentScriptDependency {
	readonly prerequisite_resource_address: string;
	readonly requirement: WorkspaceAgentScriptDependencyRequirement;
}

// From codersdk/workspaceagents.go
export type WorkspaceAgentScriptDependencyRequirement =
	| "completion"
	| "success";

export const WorkspaceAgentScriptDependencyRequirements: WorkspaceAgentScriptDependencyRequirement[] =
	["completion", "success"];

// From codersdk/workspaceagents.go
export type WorkspaceAgentScriptStatus =
	| "canceled"
	| "exit_failure"
	| "ok"
	| "pipes_left_open"
	| "skipped"
	| "timed_out";

export const WorkspaceAgentScriptStatuses: WorkspaceAgentScriptStatus[] = [
	"canceled",
	"exit_failure",
	"ok",
	"pipes_left_open",
	"skipped",
	"timed_out",
];

// From codersdk/workspaceagents.go
export type WorkspaceAgentStartupScriptBehavior = "blocking" | "non-blocking";

export const WorkspaceAgentStartupScriptBehaviors: WorkspaceAgentStartupScriptBehavior[] =
	["blocking", "non-blocking"];

// From codersdk/workspaceagents.go
export type WorkspaceAgentStatus =
	| "connected"
	| "connecting"
	| "disconnected"
	| "timeout";

export const WorkspaceAgentStatuses: WorkspaceAgentStatus[] = [
	"connected",
	"connecting",
	"disconnected",
	"timeout",
];

// From codersdk/workspaceapps.go
export interface WorkspaceApp {
	readonly id: string;
	/**
	 * URL is the address being proxied to inside the workspace.
	 * If external is specified, this will be opened on the client.
	 */
	readonly url?: string;
	/**
	 * External specifies whether the URL should be opened externally on
	 * the client or not.
	 */
	readonly external: boolean;
	/**
	 * Slug is a unique identifier within the agent.
	 */
	readonly slug: string;
	/**
	 * DisplayName is a friendly name for the app.
	 */
	readonly display_name?: string;
	readonly command?: string;
	/**
	 * Icon is a relative path or external URL that specifies
	 * an icon to be displayed in the dashboard.
	 */
	readonly icon?: string;
	/**
	 * Subdomain denotes whether the app should be accessed via a path on the
	 * `coder server` or via a hostname-based dev URL. If this is set to true
	 * and there is no app wildcard configured on the server, the app will not
	 * be accessible in the UI.
	 */
	readonly subdomain: boolean;
	/**
	 * SubdomainName is the application domain exposed on the `coder server`.
	 */
	readonly subdomain_name?: string;
	readonly sharing_level: WorkspaceAppSharingLevel;
	/**
	 * Healthcheck specifies the configuration for checking app health.
	 */
	readonly healthcheck?: Healthcheck;
	readonly health: WorkspaceAppHealth;
	readonly group?: string;
	readonly hidden: boolean;
	readonly open_in: WorkspaceAppOpenIn;
	/**
	 * Tooltip is an optional markdown supported field that is displayed
	 * when hovering over workspace apps in the UI.
	 */
	readonly tooltip?: string;
	/**
	 * Statuses is a list of statuses for the app.
	 */
	readonly statuses: readonly WorkspaceAppStatus[];
}

// From codersdk/workspaceapps.go
export type WorkspaceAppHealth =
	| "disabled"
	| "healthy"
	| "initializing"
	| "unhealthy";

export const WorkspaceAppHealths: WorkspaceAppHealth[] = [
	"disabled",
	"healthy",
	"initializing",
	"unhealthy",
];

// From codersdk/workspaceapps.go
export type WorkspaceAppOpenIn = "slim-window" | "tab";

export const WorkspaceAppOpenIns: WorkspaceAppOpenIn[] = ["slim-window", "tab"];

// From codersdk/workspaceapps.go
export type WorkspaceAppSharingLevel =
	| "authenticated"
	| "organization"
	| "owner"
	| "public";

export const WorkspaceAppSharingLevels: WorkspaceAppSharingLevel[] = [
	"authenticated",
	"organization",
	"owner",
	"public",
];

// From codersdk/workspaceapps.go
export interface WorkspaceAppStatus {
	readonly id: string;
	readonly created_at: string;
	readonly workspace_id: string;
	readonly agent_id: string;
	readonly app_id: string;
	readonly state: WorkspaceAppStatusState;
	readonly message: string;
	/**
	 * URI is the URI of the resource that the status is for.
	 * e.g. https://github.com/org/repo/pull/123
	 * e.g. file:///path/to/file
	 */
	readonly uri: string;
	/**
	 * @deprecated This field is unused and will be removed in a future version.
	 * Icon is an external URL to an icon that will be rendered in the UI.
	 */
	readonly icon: string;
	/**
	 * @deprecated This field is unused and will be removed in a future version.
	 * NeedsUserAttention specifies whether the status needs user attention.
	 */
	readonly needs_user_attention: boolean;
}

// From codersdk/workspaceapps.go
export type WorkspaceAppStatusState =
	| "complete"
	| "failure"
	| "idle"
	| "working";

export const WorkspaceAppStatusStates: WorkspaceAppStatusState[] = [
	"complete",
	"failure",
	"idle",
	"working",
];

// From codersdk/workspacebuilds.go
/**
 * WorkspaceBuild is an at-point representation of a workspace state.
 * BuildNumbers start at 1 and increase by 1 for each subsequent build
 */
export interface WorkspaceBuild {
	readonly id: string;
	readonly created_at: string;
	readonly updated_at: string;
	readonly workspace_id: string;
	readonly workspace_name: string;
	readonly workspace_owner_id: string;
	/**
	 * WorkspaceOwnerName is the username of the owner of the workspace.
	 */
	readonly workspace_owner_name: string;
	readonly workspace_owner_avatar_url?: string;
	readonly template_version_id: string;
	readonly template_version_name: string;
	readonly build_number: number;
	readonly transition: WorkspaceTransition;
	readonly initiator_id: string;
	readonly initiator_name: string;
	readonly job: ProvisionerJob;
	readonly reason: BuildReason;
	readonly resources: readonly WorkspaceResource[];
	readonly deadline?: string;
	readonly max_deadline?: string;
	readonly status: WorkspaceStatus;
	readonly daily_cost: number;
	readonly matched_provisioners?: MatchedProvisioners;
	readonly template_version_preset_id: string | null;
	readonly has_external_agent?: boolean;
}

// From codersdk/workspacebuilds.go
/**
 * WorkspaceBuildDebugEventRequest is the request body for
 * POST /api/v2/workspacebuilds/{workspacebuild}/debug-events.
 */
export interface WorkspaceBuildDebugEventRequest {
	/**
	 * ID identifies this click so a later step of the funnel can be
	 * attributed to it.
	 */
	readonly id: string;
}

// From codersdk/workspacebuilds.go
/**
 * WorkspaceBuildParameter represents a parameter specific for a workspace build.
 */
export interface WorkspaceBuildParameter {
	readonly name: string;
	readonly value: string;
}

// From codersdk/workspacebuilds.go
export interface WorkspaceBuildTimings {
	readonly provisioner_timings: readonly ProvisionerTiming[];
	/**
	 * TODO: Consolidate agent-related timing metrics into a single struct when
	 * updating the API version
	 */
	readonly agent_script_timings: readonly AgentScriptTiming[];
	readonly agent_connection_timings: readonly AgentConnectionTiming[];
}

// From codersdk/workspaces.go
/**
 * WorkspaceBuildUpdate contains information about a workspace build state change.
 * This is published via the /watch-all-workspacebuilds SSE endpoint when the
 * workspace-build-updates experiment is enabled.
 */
export interface WorkspaceBuildUpdate {
	readonly workspace_id: string;
	readonly workspace_name: string;
	readonly build_id: string;
	/**
	 * Transition is the workspace transition type: "start", "stop", or "delete".
	 */
	readonly transition: string;
	/**
	 * JobStatus is the provisioner job status: "pending", "running",
	 * "succeeded", "canceling", "canceled", or "failed".
	 */
	readonly job_status: string;
	readonly build_number: number;
}

// From codersdk/workspaces.go
export interface WorkspaceBuildsRequest extends Pagination {
	readonly since?: string;
}

// From codersdk/deployment.go
export interface WorkspaceConnectionLatencyMS {
	readonly P50: number;
	readonly P95: number;
}

// From codersdk/deployment.go
export interface WorkspaceDeploymentStats {
	readonly pending: number;
	readonly building: number;
	readonly running: number;
	readonly failed: number;
	readonly stopped: number;
	readonly connection_latency_ms: WorkspaceConnectionLatencyMS;
	readonly rx_bytes: number;
	readonly tx_bytes: number;
}

// From codersdk/workspaces.go
export interface WorkspaceFilter {
	/**
	 * FilterQuery supports a raw filter query string
	 */
	readonly q?: string;
}

// From codersdk/workspaces.go
export interface WorkspaceGroup extends Group {
	readonly role: WorkspaceRole;
}

// From codersdk/workspaces.go
export interface WorkspaceHealth {
	readonly healthy: boolean; // Healthy is true if the workspace is healthy.
	readonly failing_agents: readonly string[]; // FailingAgents lists the IDs of the agents that are failing, if any.
}

// From codersdk/workspaces.go
export interface WorkspaceOptions {
	readonly include_deleted?: boolean;
	/**
	 * IncludeRelated selects which related data to load alongside the workspace.
	 * A nil value loads everything; a non-nil value is encoded into the
	 * include_related query parameter and loads only the selected data.
	 */
	readonly include_related?: string;
}

// From codersdk/workspaceproxy.go
export interface WorkspaceProxy extends Region {
	readonly derp_enabled: boolean;
	readonly derp_only: boolean;
	/**
	 * Status is the latest status check of the proxy. This will be empty for deleted
	 * proxies. This value can be used to determine if a workspace proxy is healthy
	 * and ready to use.
	 */
	readonly status?: WorkspaceProxyStatus;
	readonly created_at: string;
	readonly updated_at: string;
	readonly deleted: boolean;
	readonly version: string;
}

// From codersdk/deployment.go
export interface WorkspaceProxyBuildInfo {
	/**
	 * TODO: @emyrk what should we include here?
	 */
	readonly workspace_proxy: boolean;
	/**
	 * DashboardURL is the URL of the coderd this proxy is connected to.
	 */
	readonly dashboard_url: string;
}

// From healthsdk/healthsdk.go
/**
 * WorkspaceProxyReport includes health details of each connected workspace proxy.
 */
export interface WorkspaceProxyReport extends BaseReport {
	/**
	 * Healthy is deprecated and left for backward compatibility purposes, use `Severity` instead.
	 */
	readonly healthy: boolean;
	readonly workspace_proxies: RegionsResponse<WorkspaceProxy>;
}

// From codersdk/workspaceproxy.go
export interface WorkspaceProxyStatus {
	readonly status: ProxyHealthStatus;
	/**
	 * Report provides more information about the health of the workspace proxy.
	 */
	readonly report?: ProxyHealthReport;
	readonly checked_at: string;
}

// From codersdk/workspaces.go
export interface WorkspaceQuota {
	readonly credits_consumed: number;
	readonly budget: number;
}

// From codersdk/workspacebuilds.go
/**
 * WorkspaceResource describes resources used to create a workspace, for instance:
 * containers, images, volumes.
 */
export interface WorkspaceResource {
	readonly id: string;
	readonly created_at: string;
	readonly job_id: string;
	readonly workspace_transition: WorkspaceTransition;
	readonly type: string;
	readonly name: string;
	readonly hide: boolean;
	readonly icon: string;
	readonly agents?: readonly WorkspaceAgent[];
	readonly metadata?: readonly WorkspaceResourceMetadata[];
	readonly daily_cost: number;
}

// From codersdk/workspacebuilds.go
/**
 * WorkspaceResourceMetadata annotates the workspace resource with custom key-value pairs.
 */
export interface WorkspaceResourceMetadata {
	readonly key: string;
	readonly value: string;
	readonly sensitive: boolean;
}

// From codersdk/workspaces.go
export type WorkspaceRole = "admin" | "" | "use";

export const WorkspaceRoles: WorkspaceRole[] = ["admin", "", "use"];

// From codersdk/workspacesecrets.go
/**
 * WorkspaceSecret is the metadata of a secret linked to a workspace build.
 * Workspace secrets have no description, cannot be disabled, and are never
 * updated, so Description is empty, Enabled is true, and UpdatedAt equals
 * CreatedAt. The secret value is never included in API responses.
 */
export interface WorkspaceSecret extends UserSecret {
	readonly workspace_build_id: string;
	/**
	 * Ephemeral secrets are delivered to their build only and not copied to
	 * the next one.
	 */
	readonly ephemeral: boolean;
	/**
	 * ClearedAt is set once a later build superseded the secret and its
	 * value was dropped. Cleared secrets are not delivered and replace
	 * nothing.
	 */
	readonly cleared_at?: string;
	/**
	 * EnvReplaces is the ID of the workspace owner's user secret that this
	 * secret displaces on env_name.
	 */
	readonly env_replaces?: string;
	/**
	 * FileReplaces is the ID of the workspace owner's user secret that this
	 * secret displaces on file_path.
	 */
	readonly file_replaces?: string;
}

// From codersdk/workspacesecrets.go
/**
 * WorkspaceSecretInjectionTargetRequiredDetail explains that a workspace
 * secret has no disabled state, so every set request needs a delivery
 * target.
 */
export const WorkspaceSecretInjectionTargetRequiredDetail =
	"A workspace secret must have at least one of env_name or file_path set."; //nolint:gosec // G101: message text, not a hardcoded credential.

// From codersdk/workspacesecrets.go
/**
 * WorkspaceSecretInput sets or removes a workspace secret as part of a
 * workspace or workspace build request.
 *
 * Workspace secrets are delivered to the workspace only through the agent
 * manifest (as an environment variable, a file, or both). They are never
 * passed to the provisioner, so they do not appear in workspace build
 * parameters or Terraform state, and they cannot be read back through the
 * API. Every build holds its own copy of the secrets it receives.
 * Non-ephemeral secrets carry forward to later builds until a request
 * replaces them by name, removes them with a null value, or sets another
 * secret on their env_name or file_path. A secret applies as a whole, so
 * taking over either of its targets drops it entirely. Ephemeral secrets
 * are delivered to that build only.
 */
export interface WorkspaceSecretInput {
	readonly name: string;
	/**
	 * Value is the plaintext secret. A null value removes the secret. An
	 * empty string sets an empty secret.
	 */
	readonly value: string | null;
	/**
	 * EnvName is the environment variable to inject the secret as. Empty
	 * means no env injection. Required when file_path is empty and value is
	 * set.
	 */
	readonly env_name?: string;
	/**
	 * FilePath is the path to write the secret to inside the workspace.
	 * Empty means no file is written. Deployments may disable file path
	 * delivery.
	 */
	readonly file_path?: string;
	/**
	 * Ephemeral secrets are delivered to this build only and are not
	 * carried forward to the next build.
	 */
	readonly ephemeral?: boolean;
}

// From codersdk/workspacesecrets.go
/**
 * WorkspaceSecretRemovalWithFieldsDetail explains that a removal carries
 * only a name.
 */
export const WorkspaceSecretRemovalWithFieldsDetail =
	"A null value removes the secret, so env_name, file_path, and ephemeral must not be set. Set value to set the secret."; //nolint:gosec // G101: message text, not a hardcoded credential.

// From codersdk/workspacesharing.go
/**
 * WorkspaceSharingSettings represents workspace sharing settings affecting an
 * organization.
 */
export interface WorkspaceSharingSettings {
	/**
	 * SharingGloballyDisabled is true if sharing has been disabled for this
	 * organization because of a deployment-wide setting.
	 */
	readonly sharing_globally_disabled: boolean;
	/**
	 * SharingDisabled is deprecated and left for backward compatibility
	 * purposes.
	 * @deprecated use `ShareableWorkspaceOwners` instead
	 */
	readonly sharing_disabled: boolean;
	/**
	 * ShareableWorkspaceOwners controls whose workspaces can be shared
	 * within the organization.
	 */
	readonly shareable_workspace_owners: ShareableWorkspaceOwners;
}

// From codersdk/workspacebuilds.go
export type WorkspaceStatus =
	| "canceled"
	| "canceling"
	| "deleted"
	| "deleting"
	| "failed"
	| "pending"
	| "running"
	| "starting"
	| "stopped"
	| "stopping";

export const WorkspaceStatuses: WorkspaceStatus[] = [
	"canceled",
	"canceling",
	"deleted",
	"deleting",
	"failed",
	"pending",
	"running",
	"starting",
	"stopped",
	"stopping",
];

// From codersdk/workspacebuilds.go
export type WorkspaceTransition = "delete" | "start" | "stop";

export const WorkspaceTransitions: WorkspaceTransition[] = [
	"delete",
	"start",
	"stop",
];

// From codersdk/workspaces.go
export interface WorkspaceUser extends MinimalUser {
	readonly role: WorkspaceRole;
}

// From codersdk/workspaces.go
export interface WorkspacesRequest extends Pagination {
	readonly q?: string;
}

// From codersdk/workspaces.go
export interface WorkspacesResponse {
	readonly workspaces: readonly Workspace[];
	readonly count: number;
}
